import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ButtonControl,
  CheckboxControl,
  DialogControl,
  FieldControl,
  FilterGroupsControl,
  IconControl,
  Loading,
  MultiSelectListControl,
  SearchInput,
  SelectListControl,
  TabPaneControl,
  TableControl,
  ToastNotifications,
} from "spark-core-dx/components";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { useCoreRoute, useGenericManager } from "spark-core-dx/hooks";
import { SystemTypes } from "spark-core-dx/services";
import ConvokeSourceObjectManager from "../../../managers/convoke/ConvokeSourceObjectManager";
import ConvokeEntityObjectManager from "../../../managers/convoke/ConvokeEntityObjectManager";
import { Operations } from "../../../services/ItemServices";
import AIPanel from "../../../components/AIChat/AIPanel";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import { DATA_EXECUTION_MODE } from "../dataExecutionModes";
import {
  formatVirtualDbVersionLabel,
  getUserControlledWorkspaceVersion,
  parseWorkspaceVersion,
  normalizeUserControlledWorkspaceVersion,
  normalizeWorkspaceVersion,
} from "../workspaceVersioning";
import DataHeader from "../components/DataHeader/DataHeader";
import DashboardHeader from "../../dashboard/components/DashboardHeader/DashboardHeader";
import NavigationRibbon from "../../dashboard/components/NavigationRibbon/NavigationRibbon";
import { useDashboardDomainContext } from "../../dashboard/DashboardDomainProvider";
import "./DataModelDesigner.scss";

const toAbsoluteRoutePath = (value, fallback = "") => {
  const normalizedValue = String(value ?? fallback ?? "").trim();
  if (!normalizedValue) {
    return "";
  }

  return normalizedValue.startsWith("/") ? normalizedValue : `/${normalizedValue}`;
};

const TABLE_TYPE = "table";
const VIEW_TYPE = "view";
const MODEL_KIND_TABLE = "table";
const MODEL_KIND_VIEW = "view";

const MODEL_KIND_OPTIONS = [
  { Text: "Table", Value: MODEL_KIND_TABLE },
  { Text: "View", Value: MODEL_KIND_VIEW },
];

const TABLE_SOURCE_TYPE_OPTIONS = [
  { Text: "Source", Value: 1 },
  { Text: "Entity", Value: 2 },
];

const VIEW_AUTHORING_MODE_BUILDER = 1;
const VIEW_AUTHORING_MODE_SQL = 2;
const VIEW_AUTHORING_MODE_OPTIONS = [
  { Text: "Builder", Value: VIEW_AUTHORING_MODE_BUILDER },
  { Text: "SQL", Value: VIEW_AUTHORING_MODE_SQL },
];

const DEFAULT_BUILD_SEQUENCE = 100;
const DETAILS_TAB_MODEL = "model";
const DETAILS_TAB_COLUMNS = "columns";
const RUNTIME_DROP_TARGET_VIRTUAL = "virtual";
const RUNTIME_DROP_TARGET_LOCAL = "local";
const DETAILS_TAB_FILTER = "filter";
const DETAILS_TAB_VIEW = "view";
const DETAILS_TAB_SQL = "sql";
const PANEL_WORKFLOW_RELATION = "relation";
const PANEL_WORKFLOW_SEMANTIC = "semantic";
const SEMANTIC_EDITOR_TAB_DETAILS = "details";
const SEMANTIC_EDITOR_TAB_EXPRESSION = "expression";
const DEFAULT_SORT_DIRECTION_NONE = 0;
const DEFAULT_SORT_DIRECTION_ASC = 1;
const DEFAULT_SORT_DIRECTION_DESC = 2;
const DEFAULT_SORT_DIRECTION_OPTIONS = [
  { Text: "Do Not Sort", Value: DEFAULT_SORT_DIRECTION_NONE },
  { Text: "Ascending", Value: DEFAULT_SORT_DIRECTION_ASC },
  { Text: "Descending", Value: DEFAULT_SORT_DIRECTION_DESC },
];
const SEMANTIC_COLUMN_KIND_BASE = 1;
const SEMANTIC_COLUMN_KIND_CALCULATED = 2;
const SEMANTIC_COLUMN_KIND_STATIC = 3;
const SEMANTIC_ADD_COLUMN_KIND_OPTIONS = [
  { Text: "Calculated", Value: SEMANTIC_COLUMN_KIND_CALCULATED },
  { Text: "Static", Value: SEMANTIC_COLUMN_KIND_STATIC },
];
const MEASURE_KIND_AGGREGATE = 1;
const MEASURE_KIND_CALCULATED = 2;
const MEASURE_DEFINITION_KIND_COLUMN = 1;
const MEASURE_DEFINITION_KIND_EXPRESSION = 2;
const MEASURE_KIND_OPTIONS = [
  { Text: "Aggregate", Value: MEASURE_KIND_AGGREGATE },
  { Text: "Calculated", Value: MEASURE_KIND_CALCULATED },
];
const AGGREGATION_KIND_SUM = 1;
const AGGREGATION_KIND_OPTIONS = [
  { Text: "Sum", Value: 1 },
  { Text: "Count", Value: 2 },
  { Text: "Count Distinct", Value: 3 },
  { Text: "Average", Value: 4 },
  { Text: "Min", Value: 5 },
  { Text: "Max", Value: 6 },
  { Text: "Median", Value: 7 },
  { Text: "Std Dev", Value: 8 },
  { Text: "Variance", Value: 9 },
  { Text: "First", Value: 10 },
  { Text: "Last", Value: 11 },
];
const NUMERIC_AGGREGATION_KINDS = new Set([1, 4, 7, 8, 9]);
const ANY_TYPE_AGGREGATION_KINDS = new Set([2, 3, 5, 6, 10, 11]);
const SEMANTIC_DELETE_KIND_COLUMN = "column";
const SEMANTIC_DELETE_KIND_MEASURE = "measure";
const SEMANTIC_DATA_TYPE_OPTIONS = [
  { Text: "String", Value: "System.String" },
  { Text: "Boolean", Value: "System.Boolean" },
  { Text: "Short", Value: "System.Int16" },
  { Text: "Integer", Value: "System.Int32" },
  { Text: "Long", Value: "System.Int64" },
  { Text: "Decimal", Value: "System.Decimal" },
  { Text: "Double", Value: "System.Double" },
  { Text: "Float", Value: "System.Single" },
  { Text: "Date", Value: "System.DateOnly" },
  { Text: "Date Time", Value: "System.DateTime" },
  { Text: "Guid", Value: "System.Guid" },
];
const SELECTDATA_UIFORMAT_OPERATION = "SelectDataUIFormat";
const EXPRESSION_OPERATOR_OPTIONS = [
  { Text: "+", Value: " + " },
  { Text: "-", Value: " - " },
  { Text: "*", Value: " * " },
  { Text: "/", Value: " / " },
  { Text: "=", Value: " = " },
  { Text: "!=", Value: " != " },
  { Text: "<", Value: " < " },
  { Text: "<=", Value: " <= " },
  { Text: ">", Value: " > " },
  { Text: ">=", Value: " >= " },
  { Text: "AND", Value: " AND " },
  { Text: "OR", Value: " OR " },
  { Text: "NOT", Value: "NOT " },
];
const EXPRESSION_FUNCTION_OPTIONS = [
  { Category: "Logical", Text: "IF", Value: "IF(condition, true_value, false_value)" },
  { Category: "Logical", Text: "COALESCE", Value: "COALESCE(value1, value2)" },
  { Category: "Text", Text: "CONCAT", Value: "CONCAT(value1, value2)" },
  { Category: "Text", Text: "LOWER", Value: "LOWER(value1)" },
  { Category: "Text", Text: "UPPER", Value: "UPPER(value1)" },
  { Category: "Text", Text: "TRIM", Value: "TRIM(value1)" },
  { Category: "Convert", Text: "TOSTRING", Value: "TOSTRING(value1)" },
  { Category: "Convert", Text: "TOBOOLEAN", Value: "TOBOOLEAN(value1)" },
  { Category: "Convert", Text: "TOSHORT", Value: "TOSHORT(value1)" },
  { Category: "Convert", Text: "TOINTEGER", Value: "TOINTEGER(value1)" },
  { Category: "Convert", Text: "TOLONG", Value: "TOLONG(value1)" },
  { Category: "Convert", Text: "TODECIMAL", Value: "TODECIMAL(value1)" },
  { Category: "Convert", Text: "TODOUBLE", Value: "TODOUBLE(value1)" },
  { Category: "Convert", Text: "TOFLOAT", Value: "TOFLOAT(value1)" },
  { Category: "Convert", Text: "TODATE", Value: "TODATE(value1)" },
  { Category: "Convert", Text: "TODATETIME", Value: "TODATETIME(value1)" },
  { Category: "Convert", Text: "TOGUID", Value: "TOGUID(value1)" },
  { Category: "Math", Text: "ABS", Value: "ABS(value1)" },
  { Category: "Math", Text: "ROUND", Value: "ROUND(value1, 2)" },
  { Category: "Date", Text: "YEAR", Value: "YEAR(value1)" },
  { Category: "Date", Text: "MONTH", Value: "MONTH(value1)" },
  { Category: "Date", Text: "DAY", Value: "DAY(value1)" },
];
const AGGREGATE_EXPRESSION_FUNCTION_OPTIONS = [
  { Text: "SUM", Value: "SUM(value1)" },
  { Text: "AVG", Value: "AVG(value1)" },
  { Text: "MIN", Value: "MIN(value1)" },
  { Text: "MAX", Value: "MAX(value1)" },
  { Text: "COUNT", Value: "COUNT(value1)" },
  { Text: "COUNTDISTINCT", Value: "COUNTDISTINCT(value1)" },
  { Text: "MEDIAN", Value: "MEDIAN(value1)" },
  { Text: "STDDEV", Value: "STDDEV(value1)" },
  { Text: "VARIANCE", Value: "VARIANCE(value1)" },
  { Text: "FIRST", Value: "FIRST(value1)" },
  { Text: "LAST", Value: "LAST(value1)" },
];
const VALIDATE_DATA_MODEL_EXPRESSION_OPERATION = "ValidateDataModelExpression";
const COMPILE_DATA_MODEL_SQL_OPERATION = "CompileDataModelSql";
const LOAD_DATA_MODELS_AND_DATA_OPERATION = "LoadDataModelsAndData";
const GET_DATA_MODELS_AND_DATA_STATUS_OPERATION = "GetDataModelsAndDataStatus";
const DROP_DATA_MODELS_AND_DATA_OPERATION = "DropDataModelsAndData";
const EXECUTE_DATA_QUERY_OPERATION = "ExecuteDataQuery";
const BUILD_DATA_MODEL_WORKSPACE_ARTIFACT_ON_DEMAND_OPERATION = "BuildDataModelWorkspaceArtifactOnDemand";
const PUBLISH_DATA_MODEL_WORKSPACE_ARTIFACT_OPERATION = "PublishDataModelWorkspaceArtifact";
const EXECUTE_DATA_MODEL_WORKSPACE_ARTIFACT_QUERY_OPERATION = "ExecuteDataModelWorkspaceArtifactQuery";
const DATA_RUNTIME_NOT_LOADED_MESSAGE_CODE = "E3565";
const DATA_MODEL_WORKSPACE_ARTIFACT_NOT_PUBLISHED_MESSAGE_CODE = "E3566";
const DATA_MODEL_WORKSPACE_ARTIFACT_MISSING_MESSAGE_CODE = "E3567";
const WORKSPACE_SURFACE_MODE_DESIGN = "design";
const WORKSPACE_SURFACE_MODE_PUBLISHED = "published";
const DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL = 1;
const DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH = 2;
const PROCESSOR_TYPE_REMOTE = 8;
const DESIGN_VIRTUAL_DB_TARGET_DESIGN = "design";
const DESIGN_VIRTUAL_DB_TARGET_BUILT = "built";
const VIRTUAL_DB_STATUS_LABEL = "Virtual DB Status";
const ARTIFACT_STATUS_TAB_OVERVIEW = "overview";
const ARTIFACT_STATUS_TAB_BUILT = "built";
const ARTIFACT_STATUS_TAB_PUBLISHED = "published";
const ARTIFACT_STATUS_TAB_DIAGNOSTICS = "diagnostics";
const RUNTIME_STATUS_TAB_OVERVIEW = "overview";
const RUNTIME_STATUS_TAB_SOURCE = "source";
const RUNTIME_STATUS_TAB_MODEL = "model";
const RUNTIME_STATUS_TAB_DIAGNOSTICS = "diagnostics";
const WORKSPACE_READ_ONLY_MESSAGE = "Switch the model workspace to Design mode to make changes.";
const getWorkspaceSurfaceModeLabel = (mode) =>
  String(mode ?? "").trim().toLowerCase() === WORKSPACE_SURFACE_MODE_PUBLISHED
    ? "Published Mode"
    : "Design Mode";
const DASHBOARD_ARTIFACT_STATUS_LABELS = {
  1: "Draft",
  2: "Published",
  3: "Expired",
  4: "Archived",
  5: "Failed",
};
const DASHBOARD_BUILD_RUN_STATUS_LABELS = {
  1: "Queued",
  2: "Running",
  3: "Succeeded",
  4: "Failed",
  5: "Cancelled",
  6: "Reused Existing",
};
const normalizeExpressionBracketPairs = (value) =>
  String(value ?? "")
    .replace(/\[{2,}/g, "[")
    .replace(/\]{2,}/g, "]");

const formatDashboardRuntimeLoadedAt = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString();
};

const formatRuntimeRowCount = (value) => {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) {
    return "0";
  }

  return numericValue.toLocaleString();
};

const getRuntimeActionProgressLabel = (actionLabel) => {
  switch (String(actionLabel ?? "").trim().toLowerCase()) {
    case "build":
    case "rebuild":
      return "Building";
    case "drop":
      return "Dropping";
    case "publish":
      return "Publishing";
    case "reload":
      return "Reloading";
    case "status":
      return "Checking";
    default:
      return "Loading";
  }
};

const isRuntimeLoadedStatus = (status) =>
  String(status ?? "").trim().toLowerCase() === "loaded";

const isDataRuntimeUnloadedError = (errorValue) =>
  String(errorValue?.code ?? "").trim().toUpperCase() === DATA_RUNTIME_NOT_LOADED_MESSAGE_CODE;

const buildServiceError = (response, fallbackMessage) => {
  const validationMessages = getValidationDisplayMessages(response);
  const error = new Error(
    validationMessages[0]
    || response?.DisplayMessage
    || response?.Message?.DisplayMessage
    || fallbackMessage
  );
  error.code = String(response?.Message?.Code ?? "").trim().toUpperCase();
  error.messageName = String(response?.Message?.Name ?? "").trim();
  return error;
};

const buildModelPreviewExecutionModeOptions = (modelType) => [
  {
    Text: "Virtual DB (Columns)",
    Value: DATA_EXECUTION_MODE.VIRTUAL_DB_COLUMNS,
  },
  {
    Text: "Virtual DB (Measures)",
    Value: DATA_EXECUTION_MODE.VIRTUAL_DB_MEASURES,
  },
  {
    Text: "Client (Columns)",
    Value: DATA_EXECUTION_MODE.CLIENT_COLUMNS,
  },
  {
    Text: "Client (Measures)",
    Value: DATA_EXECUTION_MODE.CLIENT_MEASURES,
  },
  {
    Text: "RealTime (Raw Data)",
    Value: DATA_EXECUTION_MODE.REALTIME_RAW,
    Disabled: modelType === VIEW_TYPE,
  },
];

const isRealtimePreviewExecutionMode = (previewMode) =>
  Number(previewMode) === DATA_EXECUTION_MODE.REALTIME_RAW;

const isServerPreviewExecutionMode = (previewMode) =>
  [
    DATA_EXECUTION_MODE.VIRTUAL_DB_COLUMNS,
    DATA_EXECUTION_MODE.VIRTUAL_DB_MEASURES,
  ].includes(Number(previewMode));

const isClientPreviewExecutionMode = (previewMode) =>
  [
    DATA_EXECUTION_MODE.CLIENT_COLUMNS,
    DATA_EXECUTION_MODE.CLIENT_MEASURES,
  ].includes(Number(previewMode));

const isColumnsOnlyPreviewExecutionMode = (previewMode) =>
  [
    DATA_EXECUTION_MODE.VIRTUAL_DB_COLUMNS,
    DATA_EXECUTION_MODE.CLIENT_COLUMNS,
  ].includes(Number(previewMode));

const isMeasuresOnlyPreviewExecutionMode = (previewMode) =>
  [
    DATA_EXECUTION_MODE.VIRTUAL_DB_MEASURES,
    DATA_EXECUTION_MODE.CLIENT_MEASURES,
  ].includes(Number(previewMode));

const getPreviewExecutionBaseMode = (previewMode) => {
  if (isServerPreviewExecutionMode(previewMode)) {
    return 3;
  }

  if (isClientPreviewExecutionMode(previewMode)) {
    return 2;
  }

  return 1;
};

const getItemId = (item) => item?._id ?? item?.UniqueId ?? item?.Id ?? item?.id ?? null;
const createClientId = (prefix = "id") => {
  if (typeof globalThis?.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};
const getFirstItem = (result) => result?.Items?.[0] ?? null;
const deepCopy = (value) => (typeof structuredClone === "function" ? structuredClone(value) : JSON.parse(JSON.stringify(value)));
const DEFAULT_FILTER_DRAFT = [[{ id: "0", fn: "", ft: "", dt: "", dj: "", c: "", v: "" }]];

const createDefaultFilterDraft = () => deepCopy(DEFAULT_FILTER_DRAFT);

const createWorkspaceEditDraft = (workspace) => ({
  Name: String(workspace?.Name ?? "").trim(),
  Description: String(workspace?.Description ?? "").trim(),
  Version: getUserControlledWorkspaceVersion(workspace?.Version),
  ServerExecutionHost: Number(workspace?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH),
});

const isFilterRowActive = (row) => {
  if (!row || typeof row !== "object") {
    return false;
  }

  const fieldName = String(row?.fn ?? row?.FieldName ?? row?.field ?? "").trim();
  const comparator = String(row?.c ?? row?.op ?? row?.operator ?? "").trim();
  if (!fieldName || !comparator) {
    return false;
  }

  if (comparator === "isEmpty") {
    return true;
  }

  const value = row?.v;
  return !(value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0));
};

const sanitizeFilterDraft = (filters) => {
  if (!Array.isArray(filters)) {
    return [];
  }

  return filters
    .map((group) => (Array.isArray(group) ? group.filter(isFilterRowActive) : []))
    .filter((group) => group.length > 0);
};

const uiToServerMethod = (comparator) => {
  switch (comparator) {
    case "contains":
      return "Contains";
    case "startsWith":
      return "StartsWith";
    case "endsWith":
      return "EndsWith";
    case "strictMatch":
      return "==";
    default:
      return comparator;
  }
};

const makeFilterLeaf = (row) => ({
  Name: row.fn,
  Method: uiToServerMethod(row.c),
  Value: row.v,
  CheckCase: false,
  Left: null,
  Right: null,
});

const makeFilterConjunction = (left, op, right) => ({
  Name: null,
  Method: op,
  Value: null,
  CheckCase: false,
  Left: left,
  Right: right,
});

const expandIsEmptyFilter = (row) => {
  const nullCheck = { Name: row.fn, Method: "==", Value: null, CheckCase: false, Left: null, Right: null };
  const emptyCheck = { Name: row.fn, Method: "==", Value: "", CheckCase: false, Left: null, Right: null };
  return makeFilterConjunction(nullCheck, "||", emptyCheck);
};

const foldFilterNodes = (nodes, op) => {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return null;
  }

  let node = nodes[0];
  for (let i = 1; i < nodes.length; i += 1) {
    node = makeFilterConjunction(node, op, nodes[i]);
  }

  return node;
};

const buildItemFilterTree = (filters) => {
  const groups = Array.isArray(filters) ? filters : [];
  const groupTrees = groups
    .map((group) => {
      const usableRows = (Array.isArray(group) ? group : []).filter(isFilterRowActive);
      if (usableRows.length === 0) {
        return null;
      }

      const nodes = usableRows.map((row) => (row.c === "isEmpty" ? expandIsEmptyFilter(row) : makeFilterLeaf(row)));
      return foldFilterNodes(nodes, "&&");
    })
    .filter(Boolean);

  return foldFilterNodes(groupTrees, "||");
};

const getModelKindValue = (model) => {
  if (Number.isFinite(Number(model?.ModelKind)) && Number(model?.ModelKind) > 0) {
    return Number(model.ModelKind);
  }

  if (model?.UiModelKind === MODEL_KIND_VIEW) {
    return 2;
  }

  if (model?.UiModelKind === MODEL_KIND_TABLE) {
    return 1;
  }

  return 0;
};

const getModelType = (model) => (getModelKindValue(model) === 2 ? VIEW_TYPE : TABLE_TYPE);
const getTableDefinition = (model) => model?.TableDefinition ?? null;
const getViewDefinition = (model) => model?.ViewDefinition ?? null;
const getSemanticDefinition = (model) => model?.SemanticDefinition ?? {};
const getModelSourceType = (model) => Number(getTableDefinition(model)?.SourceType ?? model?.SourceType ?? 0);
const getModelReferenceId = (model) => String(getTableDefinition(model)?.ReferenceId ?? model?.ReferenceId ?? "");
const getModelWarehouseId = (model) => getTableDefinition(model)?.WarehouseId ?? model?.SelectedWarehouseId ?? "";
const getModelWarehouseDeploymentId = (model) =>
  getTableDefinition(model)?.WarehouseDeploymentId ?? model?.SelectedWarehouseDeploymentId ?? "";
const getModelPrimaryDateColumnName = (model) =>
  getSemanticDefinition(model)?.PrimaryDateColumnName ?? model?.PrimaryDateColumnName ?? "";
const getModelDefaultSorts = (model) => {
  const defaultSorts = getSemanticDefinition(model)?.DefaultSorts;
  return Array.isArray(defaultSorts) ? defaultSorts : [];
};
const getSemanticColumnValueKind = (columnKind) => {
  switch (Number(columnKind)) {
    case SEMANTIC_COLUMN_KIND_CALCULATED:
      return 2;
    case SEMANTIC_COLUMN_KIND_STATIC:
      return 3;
    default:
      return 1;
  }
};
const getSelectControlValue = (value, currentValueObject = null) => {
  if (currentValueObject && typeof currentValueObject === "object") {
    if (currentValueObject.Value !== undefined && currentValueObject.Value !== null) {
      return currentValueObject.Value;
    }

    if (currentValueObject.value !== undefined && currentValueObject.value !== null) {
      return currentValueObject.value;
    }
  }

  if (value && typeof value === "object") {
    if (value.Value !== undefined && value.Value !== null) {
      return value.Value;
    }

    if (value.value !== undefined && value.value !== null) {
      return value.value;
    }
  }

  return value;
};
const formatColumnDataTypeName = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "";
  }

  const normalized = raw.split(",")[0].trim();
  const shortName = normalized.includes(".") ? normalized.split(".").pop() : normalized;

  switch (String(shortName).toLowerCase()) {
    case "string":
      return "string";
    case "boolean":
      return "bool";
    case "int16":
      return "short";
    case "int32":
      return "int";
    case "int64":
      return "long";
    case "decimal":
      return "decimal";
    case "double":
      return "double";
    case "single":
      return "float";
    case "datetime":
      return "datetime";
    case "dateonly":
      return "date";
    case "guid":
      return "guid";
    default:
      return shortName;
  }
};
const getModelSemanticColumns = (model) => {
  const columns = getSemanticDefinition(model)?.Columns;
  return Array.isArray(columns) ? columns : [];
};
const getModelSemanticMeasures = (model) => {
  const measures = getSemanticDefinition(model)?.Measures;
  return Array.isArray(measures) ? measures : [];
};
const getSemanticColumnKey = (column, fallback = "") =>
  String(column?._semanticKey ?? column?._id ?? column?.UniqueId ?? column?.FieldName ?? column?.ColumnName ?? fallback ?? "");
const getSemanticMeasureKey = (measure, fallback = "") =>
  String(measure?._semanticKey ?? measure?._id ?? measure?.UniqueId ?? measure?.MeasureName ?? fallback ?? "");
const buildSemanticSortableId = (itemType, key) => `${itemType}:${String(key ?? "")}`;
const getSemanticItemSortOrder = (item, fallback = 0) => {
  const sortOrder = Number(item?.SortOrder);
  if (Number.isFinite(sortOrder)) {
    return sortOrder;
  }

  const displaySequence = Number(item?.DisplaySequence);
  if (Number.isFinite(displaySequence)) {
    return displaySequence;
  }

  return fallback;
};
const buildSemanticListItemsFromCollections = (columns = [], measures = []) => {
  const normalizedColumns = Array.isArray(columns) ? columns : [];
  const normalizedMeasures = Array.isArray(measures) ? measures : [];
  const columnItems = normalizedColumns.map((column, index) => {
    const key = getSemanticColumnKey(column, `column-${index}`);

    return {
      itemType: "column",
      key,
      sortableId: buildSemanticSortableId("column", key),
      item: column,
      index,
      sortOrder: getSemanticItemSortOrder(column, index),
    };
  });
  const measureItems = normalizedMeasures.map((measure, index) => {
    const key = getSemanticMeasureKey(measure, `measure-${index}`);

    return {
      itemType: "measure",
      key,
      sortableId: buildSemanticSortableId("measure", key),
      item: measure,
      index,
      sortOrder: getSemanticItemSortOrder(measure, normalizedColumns.length + index),
    };
  });

  return [...columnItems, ...measureItems].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }

    if (a.itemType !== b.itemType) {
      return a.itemType.localeCompare(b.itemType);
    }

    return a.index - b.index;
  });
};
const resequenceSemanticCollectionsFromItems = (orderedItems = []) => {
  const nextColumns = [];
  const nextMeasures = [];

  (Array.isArray(orderedItems) ? orderedItems : []).forEach((entry, index) => {
    if (entry?.itemType === "measure") {
      nextMeasures.push({
        ...entry.item,
        SortOrder: index,
      });
      return;
    }

    nextColumns.push({
      ...entry.item,
      GridSequence: nextColumns.length,
      DisplaySequence: index + 1,
      SortOrder: index,
    });
  });

  return {
    columns: nextColumns,
    measures: nextMeasures,
  };
};
const getSemanticColumnTypeLabel = (columnKind) => {
  switch (Number(columnKind)) {
    case SEMANTIC_COLUMN_KIND_CALCULATED:
      return "Calculated";
    case SEMANTIC_COLUMN_KIND_STATIC:
      return "Static";
    default:
      return "Column";
  }
};
const getSemanticMeasureTypeLabel = (measureKind) =>
  Number(measureKind) === MEASURE_KIND_CALCULATED ? "Formula" : "Aggregate";
const getSemanticColumnTypeTitle = (column) =>
  Number(column?.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) === SEMANTIC_COLUMN_KIND_CALCULATED
    ? String(column?.ValueDefinitionValue ?? "").trim()
    : "";
const getSemanticMeasureTypeTitle = (measure) =>
  Number(measure?.MeasureKind ?? MEASURE_KIND_AGGREGATE) === MEASURE_KIND_CALCULATED
    ? String(measure?.ExpressionValue ?? "").trim()
    : "";
const getModelViewAuthoringMode = (model) =>
  Number(getViewDefinition(model)?.AuthoringMode ?? model?.ViewAuthoringMode ?? VIEW_AUTHORING_MODE_BUILDER);
const getModelViewPrimaryModelId = (model) =>
  String(getViewDefinition(model)?.PrimaryModelId ?? model?.ViewPrimaryModelId ?? "");
const getModelViewTemplate = (model) => getViewDefinition(model)?.ViewTemplate ?? model?.ViewTemplate ?? "";
const getModelFilterDefinition = (model) => {
  if (getModelType(model) === VIEW_TYPE) {
    return getViewDefinition(model)?.PreAggregateFilterDefinition ?? null;
  }

  return getTableDefinition(model)?.ConvokeQueryFilterDefinition ?? null;
};
const getModelFilterDraft = (model) => {
  const filterDraft = getModelFilterDefinition(model)?.FilterDraft ?? model?.FilterDraft;
  if (Array.isArray(filterDraft) && filterDraft.length > 0) {
    return filterDraft;
  }

  return null;
};

const getInitialFilterDraft = (model) => {
  const filterDraft = getModelFilterDraft(model);
  if (Array.isArray(filterDraft) && filterDraft.length > 0) {
    return deepCopy(filterDraft);
  }

  return createDefaultFilterDraft();
};

const getEffectiveColumnName = (column) => String(column?.ColumnName || column?.FieldName || "").trim();
const getSemanticValidationIdentity = (column) =>
  String(getEffectiveColumnName(column) || column?.UniqueId || column?._semanticKey || column?._id || "").trim();
const buildSemanticColumnValidationPath = (column, leafPath) => {
  const identity = getSemanticValidationIdentity(column);
  return identity ? `SemanticDefinition.Columns.${identity}.${leafPath}` : `SemanticDefinition.Columns.${leafPath}`;
};
const getSemanticColumnValidationPaths = (column, leafPath) => [
  buildSemanticColumnValidationPath(column, leafPath),
  `SemanticDefinition.Columns.${leafPath}`,
];
const getEffectiveMeasureName = (measure) => String(measure?.MeasureName || "").trim();
const getSemanticMeasureValidationIdentity = (measure) =>
  String(getEffectiveMeasureName(measure) || measure?.UniqueId || measure?._semanticKey || measure?._id || "").trim();
const buildSemanticMeasureValidationPath = (measure, leafPath) => {
  const identity = getSemanticMeasureValidationIdentity(measure);
  return identity ? `SemanticDefinition.Measures.${identity}.${leafPath}` : `SemanticDefinition.Measures.${leafPath}`;
};
const getSemanticMeasureValidationPaths = (measure, leafPath) => [
  buildSemanticMeasureValidationPath(measure, leafPath),
  `SemanticDefinition.Measures.${leafPath}`,
];
const buildExpressionFieldReference = (column) => `[${getEffectiveColumnName(column)}]`;
const buildExpressionMeasureReference = (measure) => `[${getEffectiveMeasureName(measure)}]`;
const EXPRESSION_REFERENCE_REGEX = /\[([^\]]+)\]/g;

const extractExpressionReferences = (expression = "") => {
  const references = new Set();
  const normalizedExpression = String(expression ?? "");

  normalizedExpression.replace(EXPRESSION_REFERENCE_REGEX, (_, referenceName) => {
    const normalizedReference = String(referenceName ?? "").trim().toLowerCase();
    if (normalizedReference) {
      references.add(normalizedReference);
    }

    return _;
  });

  return references;
};

const getSemanticColumnUsageLabel = (column) => getEffectiveColumnName(column) || column?.DisplayName || column?.FieldName || "column";
const getSemanticMeasureUsageLabel = (measure) => getEffectiveMeasureName(measure) || measure?.DisplayName || "measure";

const getRelationColumnUsageReasons = (model, relationColumn) => {
  const normalizedPublishedName = String(getEffectiveColumnName(relationColumn) ?? "").trim().toLowerCase();
  if (!normalizedPublishedName) {
    return [];
  }

  const reasons = [];
  const semanticColumns = Array.isArray(model?.Columns) ? model.Columns : [];
  const semanticMeasures = Array.isArray(model?.Measures) ? model.Measures : [];
  const defaultSorts = normalizeDefaultSortsForSave(model?.DefaultSorts ?? []);
  const primaryDateColumnName = String(model?.PrimaryDateColumnName ?? "").trim().toLowerCase();

  if (primaryDateColumnName === normalizedPublishedName) {
    reasons.push("Primary date");
  }

  if (
    defaultSorts.some(
      (sort) =>
        Number(sort?.TargetKind ?? 1) === 1 &&
        String(sort?.TargetName ?? "").trim().toLowerCase() === normalizedPublishedName
    )
  ) {
    reasons.push("Default sort");
  }

  semanticColumns.forEach((column) => {
    const columnKind = Number(column?.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE);
    if (columnKind === SEMANTIC_COLUMN_KIND_BASE) {
      return;
    }

    if (extractExpressionReferences(column?.ValueDefinitionValue).has(normalizedPublishedName)) {
      reasons.push(`${getSemanticColumnTypeLabel(columnKind)} column ${getSemanticColumnUsageLabel(column)}`);
    }
  });

  semanticMeasures.forEach((measure) => {
    const measureKind = Number(measure?.MeasureKind ?? MEASURE_KIND_AGGREGATE);
    const measureLabel = getSemanticMeasureUsageLabel(measure);

    if (
      measureKind === MEASURE_KIND_AGGREGATE &&
      String(measure?.TargetName ?? "").trim().toLowerCase() === normalizedPublishedName
    ) {
      reasons.push(`Aggregate measure ${measureLabel}`);
      return;
    }

    if (
      measureKind === MEASURE_KIND_CALCULATED &&
      extractExpressionReferences(measure?.ExpressionValue).has(normalizedPublishedName)
    ) {
      reasons.push(`Formula ${measureLabel}`);
    }
  });

  return [...new Set(reasons)];
};

const normalizeDefaultSortDirection = (value) => {
  const numericValue = Number(value);
  if (numericValue === DEFAULT_SORT_DIRECTION_DESC) {
    return DEFAULT_SORT_DIRECTION_DESC;
  }

  if (numericValue === DEFAULT_SORT_DIRECTION_ASC) {
    return DEFAULT_SORT_DIRECTION_ASC;
  }

  return DEFAULT_SORT_DIRECTION_NONE;
};

const normalizeDefaultSortsForSave = (defaultSorts = []) =>
  (Array.isArray(defaultSorts) ? defaultSorts : [])
    .filter((sort) => String(sort?.TargetName ?? "").trim())
    .sort((a, b) => Number(a?.SortOrder ?? 0) - Number(b?.SortOrder ?? 0))
    .map((sort, index) => {
      const sortId = sort?._id || sort?.UniqueId || createClientId("default-sort");

      return {
        _id: sortId,
        UniqueId: sort?.UniqueId || sortId,
        TargetKind: Number(sort?.TargetKind) === 2 ? 2 : 1,
        TargetName: String(sort?.TargetName ?? "").trim(),
        Direction: normalizeDefaultSortDirection(sort?.Direction) === DEFAULT_SORT_DIRECTION_DESC ? 2 : 1,
        SortOrder: index,
        IsActive: sort?.IsActive !== false,
        IsDeleted: sort?.IsDeleted === true,
      };
    });

const filterDefaultSortsToColumns = (defaultSorts = [], columns = []) => {
  const allowedColumnNames = new Set(
    (Array.isArray(columns) ? columns : [])
      .map(getEffectiveColumnName)
      .filter(Boolean)
      .map((value) => value.toLowerCase())
  );

  return normalizeDefaultSortsForSave(defaultSorts).filter((sort) => {
    if (Number(sort?.TargetKind ?? 1) !== 1) {
      return true;
    }

    return allowedColumnNames.has(String(sort?.TargetName ?? "").trim().toLowerCase());
  });
};

const getColumnDefaultSortDirection = (defaultSorts = [], columnName) => {
  const normalizedColumnName = String(columnName ?? "").trim().toLowerCase();
  if (!normalizedColumnName) {
    return DEFAULT_SORT_DIRECTION_NONE;
  }

  const matchingSort = normalizeDefaultSortsForSave(defaultSorts).find(
    (sort) =>
      Number(sort?.TargetKind ?? 1) === 1 &&
      String(sort?.TargetName ?? "").trim().toLowerCase() === normalizedColumnName
  );

  return normalizeDefaultSortDirection(matchingSort?.Direction);
};

const getMeasureDefaultSortDirection = (defaultSorts = [], measureName) => {
  const normalizedMeasureName = String(measureName ?? "").trim().toLowerCase();
  if (!normalizedMeasureName) {
    return DEFAULT_SORT_DIRECTION_NONE;
  }

  const matchingSort = normalizeDefaultSortsForSave(defaultSorts).find(
    (sort) =>
      Number(sort?.TargetKind ?? 1) === 2 &&
      String(sort?.TargetName ?? "").trim().toLowerCase() === normalizedMeasureName
  );

  return normalizeDefaultSortDirection(matchingSort?.Direction);
};

const setDefaultSortDirection = (defaultSorts = [], targetKind, targetName, direction) => {
  const normalizedTargetKind = Number(targetKind) === 2 ? 2 : 1;
  const normalizedTargetName = String(targetName ?? "").trim();
  const normalizedDirection = normalizeDefaultSortDirection(direction);
  const normalizedSorts = normalizeDefaultSortsForSave(defaultSorts);

  let updatedExisting = false;
  const nextSorts = normalizedSorts.flatMap((sort) => {
    const isMatchingTarget =
      Number(sort?.TargetKind ?? 1) === normalizedTargetKind &&
      String(sort?.TargetName ?? "").trim().toLowerCase() === normalizedTargetName.toLowerCase();

    if (!isMatchingTarget) {
      return [sort];
    }

    updatedExisting = true;
    if (!normalizedTargetName || normalizedDirection === DEFAULT_SORT_DIRECTION_NONE) {
      return [];
    }

    return [
      {
        ...sort,
        TargetKind: normalizedTargetKind,
        TargetName: normalizedTargetName,
        Direction: normalizedDirection,
      },
    ];
  });

  if (!updatedExisting && normalizedTargetName && normalizedDirection !== DEFAULT_SORT_DIRECTION_NONE) {
    const sortId = createClientId("default-sort");
    nextSorts.push({
      _id: sortId,
      UniqueId: sortId,
      TargetKind: normalizedTargetKind,
      TargetName: normalizedTargetName,
      Direction: normalizedDirection,
      SortOrder: nextSorts.length,
      IsActive: true,
      IsDeleted: false,
    });
  }

  return nextSorts.map((sort, index) => ({
    ...sort,
    SortOrder: index,
  }));
};

const renameColumnDefaultSortTarget = (defaultSorts = [], previousColumnName, nextColumnName) => {
  const previousName = String(previousColumnName ?? "").trim();
  const nextName = String(nextColumnName ?? "").trim();
  if (!previousName || !nextName || previousName.toLowerCase() === nextName.toLowerCase()) {
    return normalizeDefaultSortsForSave(defaultSorts);
  }

  return normalizeDefaultSortsForSave(defaultSorts).map((sort) => {
    if (
      Number(sort?.TargetKind ?? 1) === 1 &&
      String(sort?.TargetName ?? "").trim().toLowerCase() === previousName.toLowerCase()
    ) {
      return {
        ...sort,
        TargetName: nextName,
      };
    }

    return sort;
  });
};

const renameMeasureDefaultSortTarget = (defaultSorts = [], previousMeasureName, nextMeasureName) => {
  const previousName = String(previousMeasureName ?? "").trim();
  const nextName = String(nextMeasureName ?? "").trim();
  if (!previousName || !nextName || previousName.toLowerCase() === nextName.toLowerCase()) {
    return normalizeDefaultSortsForSave(defaultSorts);
  }

  return normalizeDefaultSortsForSave(defaultSorts).map((sort) => {
    if (
      Number(sort?.TargetKind ?? 1) === 2 &&
      String(sort?.TargetName ?? "").trim().toLowerCase() === previousName.toLowerCase()
    ) {
      return {
        ...sort,
        TargetName: nextName,
      };
    }

    return sort;
  });
};

const buildModelFilterDefinition = (filters) => {
  const sanitizedFilters = sanitizeFilterDraft(filters);
  return {
    FilterDraft: sanitizedFilters,
    RootFilterNode: buildItemFilterTree(sanitizedFilters) ?? {},
  };
};

const toMachineKey = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const toColumnNameInput = (value) =>
  String(value ?? "")
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .toLowerCase();

const EXPRESSION_FIELD_PLACEHOLDER_REGEX = /\[(ColumnName|DateColumn)\]|\b(condition|true_value|false_value|value\d+)\b/i;

const middleEllipsis = (value, maxLength = 40) => {
  const text = String(value ?? "");
  if (text.length <= maxLength) {
    return text;
  }

  const visible = Math.max(6, maxLength - 3);
  const front = Math.ceil(visible / 2);
  const back = Math.floor(visible / 2);
  return `${text.slice(0, front)}...${text.slice(text.length - back)}`;
};

const formatArtifactVersionChipText = (artifact, prefix = "Artifact") => {
  const versionId = String(artifact?.VersionId ?? artifact?._id ?? "").trim();
  return versionId ? `${prefix} ${middleEllipsis(versionId, 18)}` : `${prefix} Unknown`;
};

const formatArtifactStatusLabel = (value) => {
  const numericValue = Number(value ?? 0);
  return DASHBOARD_ARTIFACT_STATUS_LABELS[numericValue] || String(value ?? "Unknown");
};

const formatBuildRunStatusLabel = (value) => {
  const numericValue = Number(value ?? 0);
  return DASHBOARD_BUILD_RUN_STATUS_LABELS[numericValue] || String(value ?? "Unknown");
};

const formatArtifactDiagnosticValue = (value, fallback = "Not available") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const formatArtifactSizeLabel = (value) => {
  const size = Number(value ?? 0);
  if (!Number.isFinite(size) || size <= 0) {
    return "Unknown";
  }

  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  }

  if (size >= 1024) {
    return `${(size / 1024).toFixed(size >= 10 * 1024 ? 0 : 1)} KB`;
  }

  return `${size} B`;
};

const getBuildRunSortDateValue = (buildRun) => {
  const candidateValue = buildRun?.CompletedAtUtc
    || buildRun?.StartedAtUtc
    || buildRun?.RequestedAtUtc
    || buildRun?.UpdatedDate
    || buildRun?.CreatedDate
    || null;
  if (!candidateValue) {
    return 0;
  }

  const nextDate = new Date(candidateValue);
  return Number.isNaN(nextDate.getTime()) ? 0 : nextDate.getTime();
};

const formatFilterComparator = (value) => {
  switch (value) {
    case "strictMatch":
      return "=";
    case "contains":
      return "contains";
    case "startsWith":
      return "starts with";
    case "endsWith":
      return "ends with";
    case "isEmpty":
      return "is empty";
    default:
      return String(value ?? "");
  }
};

const formatFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (value === true) {
    return "Yes";
  }

  if (value === false) {
    return "No";
  }

  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

const summarizeFilters = (filters) => {
  const sanitized = sanitizeFilterDraft(filters);
  if (sanitized.length === 0) {
    return "All Data";
  }

  const parts = sanitized.flatMap((group) =>
    group.map((row) => {
      const fieldName = String(row?.fn ?? "").trim();
      const comparator = formatFilterComparator(row?.c);
      const value = formatFilterValue(row?.v);
      return comparator === "is empty"
        ? `${fieldName} is empty`
        : `${fieldName} ${comparator} ${value}`.trim();
    })
  );

  if (parts.length <= 2) {
    return parts.join("; ");
  }

  return `${parts.slice(0, 2).join("; ")} +${parts.length - 2} more`;
};

const normalizeSourceSystemOption = (item) => ({
  Text: item?.Name || item?.DisplayName || item?.SystemName || "Unnamed Source System",
  Value: String(getItemId(item) ?? ""),
});

const normalizeSchemaOption = (item) => ({
  Text: item?.Name || item?.DisplayName || "Unnamed Schema",
  Value: String(getItemId(item) ?? ""),
});

const normalizeSourceObjectOption = (item) => ({
  Text: item?.DisplayName || item?.SystemName || item?.Name || "Unnamed Source Object",
  Value: String(getItemId(item) ?? ""),
});

const normalizeEntityObjectOption = (item) => ({
  Text: item?.DisplayName || item?.Name || item?.ObjectName || "Unnamed Entity",
  Value: String(getItemId(item) ?? ""),
});

const normalizeWarehouseOption = (item) => ({
  Text: item?.Name || item?.DisplayName || item?.DefaultDBName || item?.SystemName || "Unnamed Warehouse",
  Value: String(getItemId(item) ?? ""),
});

const normalizeWarehouseDeploymentOption = (item) => ({
  Text: item?.DBName || item?.Name || item?.DisplayName || item?.SystemName || "Unnamed Deployment",
  Value: String(getItemId(item) ?? ""),
});

const getWarehouseDeployments = (warehouse) => {
  if (Array.isArray(warehouse?.WarehouseDeployments)) {
    return warehouse.WarehouseDeployments;
  }

  if (Array.isArray(warehouse?.Deployments)) {
    return warehouse.Deployments;
  }

  return [];
};

const getSourceObjectLabel = (item) => item?.DisplayName || item?.SystemName || item?.Name || "";
const getSourceObjectKeyBase = (item) => item?.SystemName || item?.ObjectName || item?.Name || item?.DisplayName || "";
const getEntityObjectLabel = (item) => item?.DisplayName || item?.Name || item?.ObjectName || "";
const getEntityObjectKeyBase = (item) => item?.ObjectName || item?.Name || item?.DisplayName || "";

const isDateLikeField = (field) => {
  const typeName = String(field?.DataTypeName ?? field?.dataTypeName ?? "").toLowerCase();
  const format = String(field?.Format ?? field?.format ?? "").toLowerCase();
  return (
    typeName.includes("datetime") ||
    typeName.includes("dateonly") ||
    typeName === "date" ||
    typeName.endsWith(".date") ||
    format.includes("date")
  );
};

const getFieldName = (field) => field?.FieldName || field?.ObjectFieldName || field?.fieldName || field?.Name || "";
const getFieldDisplayName = (field) => field?.DisplayName || field?.Label || getFieldName(field);
const getFieldSequence = (field, fallback = 0) => {
  const gridSequence = Number(field?.GridSequence);
  if (Number.isFinite(gridSequence)) {
    return gridSequence;
  }

  const displaySequence = Number(field?.DisplaySequence);
  if (Number.isFinite(displaySequence)) {
    return displaySequence;
  }

  return fallback;
};
const isNumericField = (field) => {
  const typeName = String(field?.DataTypeName ?? field?.dataTypeName ?? "").toLowerCase();
  return (
    typeName.includes("int") ||
    typeName.includes("decimal") ||
    typeName.includes("double") ||
    typeName.includes("float") ||
    typeName.includes("number")
  );
};
const isMeasureTargetColumnCompatible = (column, aggregationKind) => {
  const normalizedAggregationKind = Number(aggregationKind ?? AGGREGATION_KIND_SUM);
  if (ANY_TYPE_AGGREGATION_KINDS.has(normalizedAggregationKind)) {
    return true;
  }

  if (NUMERIC_AGGREGATION_KINDS.has(normalizedAggregationKind)) {
    return isNumericField(column);
  }

  return true;
};
const getCompatibleMeasureTargetColumns = (columns = [], aggregationKind) =>
  (Array.isArray(columns) ? columns : []).filter((column) => {
    const columnName = getEffectiveColumnName(column);
    return (
      !!columnName &&
      column?.IsDeleted !== true &&
      column?.IsActive !== false &&
      isMeasureTargetColumnCompatible(column, aggregationKind)
    );
  });
const getAggregationDisplayName = (aggregationKind) =>
  AGGREGATION_KIND_OPTIONS.find((item) => Number(item.Value) === Number(aggregationKind))?.Text || "Aggregate";
const buildAutoGeneratedMeasureNames = (measure, columns = []) => {
  const normalizedMeasure = measure ?? {};
  const targetColumn = (Array.isArray(columns) ? columns : []).find(
    (column) =>
      String(getEffectiveColumnName(column)).trim().toLowerCase()
      === String(normalizedMeasure?.TargetName ?? "").trim().toLowerCase()
  );
  const targetColumnDisplayName = targetColumn?.DisplayName || getEffectiveColumnName(targetColumn);
  if (!targetColumnDisplayName) {
    return {
      measureName: "",
      displayName: "",
    };
  }

  const aggregationDisplayName = getAggregationDisplayName(
    Number(normalizedMeasure?.AggregationKind ?? AGGREGATION_KIND_SUM)
  );

  return {
    measureName: toColumnNameInput(`${targetColumnDisplayName}_${aggregationDisplayName}`),
    displayName: `${aggregationDisplayName} of ${targetColumnDisplayName}`,
  };
};
const isUsableBackingField = (field) =>
  !!getFieldName(field) &&
  field?.IsDeleted !== true &&
  field?.IsActive !== false &&
  !field?.SubUIItemId;

const buildColumnSemanticType = (field) => {
  if (isDateLikeField(field)) {
    return String(field?.DataTypeName ?? "").toLowerCase().includes("time") ? "datetime" : "date";
  }

  if (isNumericField(field)) {
    return "measure";
  }

  if (field?.PrimaryField) {
    return "identifier";
  }

  return "dimension";
};

const buildModelColumnFromField = (field, index = 0, existingColumn = null) => {
  const fieldName = getFieldName(field);
  const displayName = getFieldDisplayName(field);
  const fallbackSequence = index + 1;
  const columnId = existingColumn?._id || existingColumn?.UniqueId || createClientId("column");

  return {
    ...(existingColumn ?? {}),
    _semanticKey: existingColumn?._semanticKey || columnId,
    _id: columnId,
    UniqueId: existingColumn?.UniqueId || columnId,
    FieldName: fieldName,
    ValueDefinitionValue: existingColumn?.ValueDefinitionValue || fieldName,
    ValueKind: existingColumn?.ValueKind || 1,
    ColumnKind: existingColumn?.ColumnKind || SEMANTIC_COLUMN_KIND_BASE,
    ColumnName: existingColumn?.ColumnName || fieldName,
    DisplayName: existingColumn?.DisplayName || displayName || fieldName,
    Description: existingColumn?.Description || "",
    DataTypeName: existingColumn?.DataTypeName || field?.DataTypeName || field?.dataTypeName || "",
    Format: existingColumn?.Format || field?.Format || field?.format || "",
    SemanticType: existingColumn?.SemanticType || buildColumnSemanticType(field) || "dimension",
    ShowOnGrid: existingColumn?.ShowOnGrid ?? field?.ShowOnGrid ?? true,
    GridSequence: index,
    DisplaySequence: existingColumn?.DisplaySequence ?? getFieldSequence(field, fallbackSequence),
    CanSort: existingColumn?.CanSort ?? field?.CanSort ?? true,
    CanFilter: existingColumn?.CanFilter ?? field?.CanFilter ?? true,
    CanGroup: existingColumn?.CanGroup ?? field?.CanGroup ?? true,
    PrimaryField: existingColumn?.PrimaryField ?? field?.PrimaryField ?? false,
    IsActive: existingColumn?.IsActive !== false,
    IsDeleted: existingColumn?.IsDeleted === true,
  };
};

const getDefaultColumnFieldNames = (fields = []) => {
  const usableFields = fields.filter(isUsableBackingField);
  const orderedFields = [...usableFields].sort((a, b) => getFieldSequence(a) - getFieldSequence(b));
  const visibleFields = orderedFields.filter((field) => field?.ShowOnGrid);

  if (visibleFields.length > 0) {
    return visibleFields.map((field) => getFieldName(field));
  }

  const preferredNames = new Set([
    "name",
    "displayname",
    "display_name",
    "partner_name",
    "contact_name",
    "email",
    "email_from",
    "phone",
    "website",
  ]);

  const importantFields = orderedFields.filter((field) =>
    field?.PrimaryField || preferredNames.has(getFieldName(field).toLowerCase()) || isDateLikeField(field)
  );

  if (importantFields.length > 0) {
    return importantFields.slice(0, 8).map((field) => getFieldName(field));
  }

  return orderedFields.slice(0, 8).map((field) => getFieldName(field));
};

const buildColumnsFromFieldNames = (fieldNames = [], sourceFields = [], existingColumns = []) => {
  const sourceByName = new Map(
    sourceFields
      .filter(isUsableBackingField)
      .map((field) => [String(getFieldName(field)), field])
  );
  const existingByName = new Map(
    (Array.isArray(existingColumns) ? existingColumns : [])
      .filter((column) => column?.FieldName)
      .map((column) => [String(column.FieldName), column])
  );

  return fieldNames
    .map((fieldName, index) => {
      const sourceField = sourceByName.get(String(fieldName));
      if (!sourceField) {
        return null;
      }

      return buildModelColumnFromField(sourceField, index, existingByName.get(String(fieldName)) ?? null);
    })
    .filter(Boolean)
    .map((column, index) => ({
      ...column,
      GridSequence: index,
    }));
};

const isRelationBaseColumn = (column) =>
  Number(column?.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) === SEMANTIC_COLUMN_KIND_BASE && !!column?.FieldName;

const splitRelationAndDerivedColumns = (columns = []) => {
  const normalizedColumns = Array.isArray(columns) ? columns : [];

  return {
    relationColumns: normalizedColumns.filter(isRelationBaseColumn),
    derivedColumns: normalizedColumns.filter((column) => !isRelationBaseColumn(column)),
  };
};

const normalizeSemanticColumnsForSave = (columns = []) =>
  (Array.isArray(columns) ? columns : [])
    .filter(Boolean)
    .map((column, index) => {
      const columnId = column?._id || column?.UniqueId || createClientId("column");
      const columnKind = Number(column?.ColumnKind ?? ((column?.FieldName ? SEMANTIC_COLUMN_KIND_BASE : SEMANTIC_COLUMN_KIND_CALCULATED)));
      const valueKind = getSemanticColumnValueKind(columnKind);
      const value =
        columnKind === SEMANTIC_COLUMN_KIND_BASE
          ? column.FieldName || column.ValueDefinitionValue || column.ColumnName
          : String(column?.ValueDefinitionValue ?? "");

      return {
        _id: columnId,
        UniqueId: column?.UniqueId || columnId,
        ColumnName: column.ColumnName || column.FieldName,
        DisplayName: column.DisplayName || column.Label || column.ColumnName || column.FieldName,
        Description: column.Description || "",
        ColumnKind: columnKind,
        ValueDefinition: {
          ValueKind: valueKind,
          Value: value,
        },
        DataTypeName: column.DataTypeName || "",
        Format: column.Format || "",
        SemanticType: column.SemanticType || "none",
        CanGroup: column.CanGroup ?? true,
        CanSort: column.CanSort ?? true,
        CanFilter: column.CanFilter ?? true,
        IsVisible: column.ShowOnGrid ?? true,
        SortOrder: Number.isFinite(Number(column?.DisplaySequence)) ? Number(column.DisplaySequence) : index,
        IsActive: column.IsActive !== false,
        IsDeleted: column.IsDeleted === true,
      };
    });

const normalizeSemanticMeasuresForSave = (measures = []) =>
  (Array.isArray(measures) ? measures : [])
    .filter(Boolean)
    .map((measure, index) => {
      const measureId = measure?._id || measure?.UniqueId || createClientId("measure");
      const measureKind = Number(measure?.MeasureKind ?? MEASURE_KIND_AGGREGATE);
      const definitionKind =
        measureKind === MEASURE_KIND_CALCULATED ? MEASURE_DEFINITION_KIND_EXPRESSION : MEASURE_DEFINITION_KIND_COLUMN;

      return {
        _id: measureId,
        UniqueId: measure?.UniqueId || measureId,
        MeasureName: getEffectiveMeasureName(measure),
        DisplayName: measure?.DisplayName || getEffectiveMeasureName(measure),
        Description: measure?.Description || "",
        MeasureKind: measureKind,
        MeasureDefinition: {
          DefinitionKind: definitionKind,
          TargetName: definitionKind === MEASURE_DEFINITION_KIND_COLUMN ? String(measure?.TargetName ?? "").trim() : null,
          Value: definitionKind === MEASURE_DEFINITION_KIND_EXPRESSION ? String(measure?.ExpressionValue ?? "").trim() : null,
        },
        AggregationKind: Number(measure?.AggregationKind ?? AGGREGATION_KIND_SUM),
        DataTypeName: measure?.DataTypeName || "",
        Format: measure?.Format || "",
        SemanticType: measure?.SemanticType || "measure",
        IsVisible: measure?.IsVisible ?? true,
        SortOrder: Number.isFinite(Number(measure?.SortOrder)) ? Number(measure.SortOrder) : index,
        IsActive: measure?.IsActive !== false,
        IsDeleted: measure?.IsDeleted === true,
      };
    });

const buildViewSelectedColumnsForSave = (model, columns = []) =>
  (Array.isArray(columns) ? columns : [])
    .filter((column) => column?.FieldName)
    .map((column, index) => {
      const columnId = column?._id || column?.UniqueId || createClientId("view-column");

      return {
        _id: columnId,
        UniqueId: column?.UniqueId || columnId,
        SourceModelId: model?.ViewPrimaryModelId || null,
        ColumnName: column.FieldName,
        Alias:
          String(column?.ColumnName || column?.FieldName).trim() &&
          String(column?.ColumnName || "").trim() !== String(column?.FieldName || "").trim()
            ? column.ColumnName
            : null,
        SortOrder: index,
        IsActive: column?.IsActive !== false,
        IsDeleted: column?.IsDeleted === true,
      };
    });

const normalizeColumnSelectionOptions = (fields = []) =>
  fields
    .filter(isUsableBackingField)
    .sort((a, b) => getFieldSequence(a) - getFieldSequence(b))
    .map((field) => ({
      Text: getFieldDisplayName(field),
      Value: getFieldName(field),
    }));

const buildPreviewFieldOverrides = (fields = [], modelColumns = []) => {
  const usableFields = (Array.isArray(fields) ? fields : []).filter(isUsableBackingField);
  const normalizedColumns = Array.isArray(modelColumns) ? modelColumns.filter((column) => column?.FieldName) : [];

  if (normalizedColumns.length === 0) {
    return usableFields;
  }

  const configuredByName = new Map(
    normalizedColumns.map((column, index) => [
      String(column.FieldName),
      {
        ...column,
        GridSequence: Number.isFinite(Number(column?.GridSequence)) ? Number(column.GridSequence) : index,
      },
    ])
  );

  return usableFields.map((field, index) => {
    const fieldName = String(getFieldName(field));
    const configured = configuredByName.get(fieldName);
    if (!configured) {
      return {
        ...field,
        ShowOnGrid: false,
        IsActive: false,
        IsDeleted: true,
      };
    }

    return {
      ...field,
      DisplayName: configured.DisplayName || configured.Label || getFieldDisplayName(field),
      Label: configured.DisplayName || configured.Label || getFieldDisplayName(field),
      Format: configured.Format || field?.Format || "",
      DataTypeName: configured.DataTypeName || field?.DataTypeName || field?.dataTypeName || "",
      SemanticType: configured.SemanticType || field?.SemanticType || "",
      ShowOnGrid: configured.ShowOnGrid ?? true,
      GridSequence: Number.isFinite(Number(configured?.GridSequence)) ? Number(configured.GridSequence) : index,
      DisplaySequence: Number.isFinite(Number(configured?.DisplaySequence))
        ? Number(configured.DisplaySequence)
        : getFieldSequence(field, index + 1),
      CanSort: configured.CanSort ?? field?.CanSort ?? true,
      CanFilter: configured.CanFilter ?? field?.CanFilter ?? true,
      CanGroup: configured.CanGroup ?? field?.CanGroup ?? true,
      PrimaryField: configured.PrimaryField ?? field?.PrimaryField ?? false,
      IsActive: configured.IsActive !== false,
      IsDeleted: configured.IsDeleted === true,
    };
  });
};

const normalizePreviewMetadata = (metadata, sourceType) => {
  if (!metadata) {
    return null;
  }

  const metadataList = Array.isArray(metadata) ? metadata : [metadata];
  if (Number(sourceType) !== 2) {
    return metadataList;
  }

  return metadataList.map((metadataItem) => {
    const nextMetadata = deepCopy(metadataItem);
    const fields = Array.isArray(nextMetadata?.Fields) ? nextMetadata.Fields : [];
    const hasPaging = Array.isArray(nextMetadata?.HasPaging) && nextMetadata.HasPaging.length > 0;

    if (nextMetadata?.ServerFilterSort && !hasPaging) {
      nextMetadata.ServerFilterSort = false;
    }

    const hasVisibleColumns = fields.some((field) => field?.ShowOnGrid && !field?.SubUIItemId);
    if (!hasVisibleColumns && fields.length > 0) {
      const preferredNames = new Set(["name", "displayname", "display_name", "partner_name", "contact_name", "email_from", "phone", "website"]);
      let selectedFields = fields.filter((field) => field?.PrimaryField || preferredNames.has((field?.FieldName ?? "").toLowerCase()));
      if (selectedFields.length === 0) {
        selectedFields = fields.filter((field) => !field?.SubUIItemId).slice(0, 8);
      }

      const selectedFieldNames = new Set(selectedFields.map((field) => field.FieldName));
      nextMetadata.Fields = fields.map((field) => {
        if (field?.SubUIItemId) {
          return field;
        }

        return {
          ...field,
          ShowOnGrid: selectedFieldNames.has(field.FieldName),
        };
      });
    }

    return nextMetadata;
  });
};

const getPreviewTakeFromMetadata = (metadata) => {
  const metadataItem = Array.isArray(metadata) ? metadata[0] : metadata;
  if (!metadataItem) {
    return 50000;
  }

  const profileHasPaging = Array.isArray(metadataItem?.ProfilePreferences?.HasPaging)
    ? metadataItem.ProfilePreferences.HasPaging[0]
    : metadataItem?.ProfilePreferences?.HasPaging?.first?.();
  const metadataHasPaging = Array.isArray(metadataItem?.HasPaging)
    ? metadataItem.HasPaging[0]
    : metadataItem?.HasPaging?.first?.();

  const take = Number(profileHasPaging ?? metadataHasPaging ?? 50000);
  return Number.isFinite(take) && take > 0 ? take : 50000;
};

const getPreviewFieldTypeFromDataType = (dataTypeName) => {
  return SystemTypes.UIFieldType.Label;
};

const DEFAULT_PREVIEW_COLUMN_WIDTH = 20;
const DEFAULT_PREVIEW_MIN_COLUMN_WIDTH = 10;

const getPreviewColumnWidth = (column) => {
  const columnWidth = Number(column?.ColumnWidth);
  return Number.isFinite(columnWidth) && columnWidth > 0 ? columnWidth : DEFAULT_PREVIEW_COLUMN_WIDTH;
};

const getPreviewMinColumnWidth = (column) => {
  const minColumnWidth = Number(column?.MinColumnWidth);
  return Number.isFinite(minColumnWidth) && minColumnWidth > 0 ? minColumnWidth : DEFAULT_PREVIEW_MIN_COLUMN_WIDTH;
};

const getPreviewColumnFieldName = (column) =>
  String(
    column?.FieldName
    ?? column?.Column
    ?? column?.Header
    ?? column?.Name
    ?? ""
  ).trim();

const inferPreviewColumnDataType = (rows = [], fieldName = "") => {
  const normalizedFieldName = String(fieldName ?? "").trim();
  if (!normalizedFieldName || !Array.isArray(rows)) {
    return "";
  }

  for (const row of rows) {
    if (!row || typeof row !== "object") {
      continue;
    }

    const value = row[normalizedFieldName];
    if (value === null || value === undefined) {
      continue;
    }

    if (value instanceof Date) {
      return "System.DateTime";
    }

    if (typeof value === "boolean") {
      return "System.Boolean";
    }

    if (typeof value === "number") {
      return Number.isInteger(value) ? "System.Int64" : "System.Decimal";
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return "System.String";
      }

      const normalizedDate = Date.parse(trimmedValue);
      if (!Number.isNaN(normalizedDate) && /[-/:T]/.test(trimmedValue)) {
        return "System.DateTime";
      }

      return "System.String";
    }
  }

  return "";
};

const normalizeServerPreviewColumns = (columns = [], rows = []) => {
  const normalizedColumns = Array.isArray(columns) ? columns : [];
  const rowKeys = Array.isArray(rows)
    ? Array.from(
      rows.reduce((keys, row) => {
        if (row && typeof row === "object") {
          Object.keys(row).forEach((key) => {
            if (String(key ?? "").trim()) {
              keys.add(String(key).trim());
            }
          });
        }

        return keys;
      }, new Set())
    )
    : [];
  const seen = new Set();
  const nextColumns = [];

  normalizedColumns.forEach((column, index) => {
    const fieldName = getPreviewColumnFieldName(column);
    if (!fieldName) {
      return;
    }

    const dedupeKey = fieldName.toLowerCase();
    if (seen.has(dedupeKey)) {
      return;
    }

    seen.add(dedupeKey);
    nextColumns.push({
      ...column,
      FieldName: fieldName,
      DisplayName: String(column?.DisplayName ?? column?.Label ?? fieldName).trim() || fieldName,
      DataTypeName: String(column?.DataTypeName ?? "").trim() || inferPreviewColumnDataType(rows, fieldName),
      GridSequence: Number.isFinite(Number(column?.GridSequence)) ? Number(column.GridSequence) : index,
      DisplaySequence: Number.isFinite(Number(column?.DisplaySequence)) ? Number(column.DisplaySequence) : index + 1,
    });
  });

  rowKeys.forEach((fieldName) => {
    const dedupeKey = fieldName.toLowerCase();
    if (seen.has(dedupeKey)) {
      return;
    }

    seen.add(dedupeKey);
    nextColumns.push({
      FieldName: fieldName,
      DisplayName: fieldName,
      DataTypeName: inferPreviewColumnDataType(rows, fieldName),
      GridSequence: nextColumns.length,
      DisplaySequence: nextColumns.length + 1,
    });
  });

  return nextColumns;
};

const buildServerPreviewMetadata = (columns = [], rows = [], enableServerFilterSort = false) => {
  const normalizedColumns = normalizeServerPreviewColumns(columns, rows);
  const preferredPrimaryFieldNames = new Set(["name", "displayname", "display_name", "title", "label"]);
  const pageSizes = [50, 100, 250, 500];

  return [
    {
      _id: "data-model-server-preview",
      Name: "DataModelServerPreview",
      DisplayName: "Preview",
      Permission: "DataModel",
      DisableCanCreate: true,
      DisableCanEdit: true,
      DisableCanDelete: true,
      DisableCanClone: true,
      DisableCanHardDelete: true,
      ServerFilterSort: enableServerFilterSort,
      HasPaging: pageSizes,
      ProfilePreferences: {
        HasPaging: pageSizes,
        UserFieldPreferences: {},
        SavedFilters: [],
      },
      Fields: normalizedColumns.map((column, index) => {
        const fieldName = String(column?.FieldName ?? "").trim();
        const displayName = String(column?.DisplayName ?? fieldName).trim() || fieldName;
        const normalizedFieldName = fieldName.toLowerCase();

        return {
          _id: `server-preview-field-${fieldName}`,
          FieldName: fieldName,
          Label: displayName,
          DisplayName: displayName,
          Description: column?.Description ?? "",
          DataTypeName: column?.DataTypeName ?? "",
          FieldType: getPreviewFieldTypeFromDataType(column?.DataTypeName),
          Format: column?.Format ?? "",
          SemanticType: column?.SemanticType ?? "",
          ColumnWidth: getPreviewColumnWidth(column),
          MinColumnWidth: getPreviewMinColumnWidth(column),
          ShowOnGrid: column?.ShowOnGrid !== false,
          GridSequence: Number.isFinite(Number(column?.GridSequence)) ? Number(column.GridSequence) : index,
          DisplaySequence: Number.isFinite(Number(column?.DisplaySequence)) ? Number(column.DisplaySequence) : index + 1,
          CanSort: column?.CanSort !== false,
          CanFilter: column?.CanFilter !== false,
          CanGroup: column?.CanGroup === true,
          PrimaryField: column?.PrimaryField === true || (index === 0 && preferredPrimaryFieldNames.has(normalizedFieldName)),
          IsActive: true,
          IsDeleted: false,
        };
      }),
    },
  ];
};

const buildServerPreviewManager = (totalCount = 0) => ({
  getTotalItemCount: () => totalCount,
});

const isDefaultServerPreviewSort = (sortBy) =>
  ["", "createddate"].includes(String(sortBy ?? "").trim().toLowerCase());

const getActiveSemanticColumns = (modelItem) =>
  getModelSemanticColumns(modelItem)
    .filter((column) => column?.IsDeleted !== true && column?.IsActive !== false && String(column?.ColumnName ?? "").trim());

const getActiveSemanticMeasures = (modelItem) =>
  getModelSemanticMeasures(modelItem)
    .filter((measure) => measure?.IsDeleted !== true && measure?.IsActive !== false && String(measure?.MeasureName ?? "").trim());

const getOrderedPreviewSemanticColumns = (modelItem) => {
  const activeColumns = getActiveSemanticColumns(modelItem)
    .sort((left, right) => {
      const leftSortOrder = Number.isFinite(Number(left?.SortOrder)) ? Number(left.SortOrder) : Number.MAX_SAFE_INTEGER;
      const rightSortOrder = Number.isFinite(Number(right?.SortOrder)) ? Number(right.SortOrder) : Number.MAX_SAFE_INTEGER;
      if (leftSortOrder !== rightSortOrder) {
        return leftSortOrder - rightSortOrder;
      }

      return String(left?.ColumnName ?? "").localeCompare(String(right?.ColumnName ?? ""), undefined, { sensitivity: "base" });
    });

  const visibleColumns = activeColumns.filter((column) => column?.IsVisible !== false);
  return visibleColumns.length > 0 ? visibleColumns : activeColumns;
};

const getOrderedPreviewSemanticMeasures = (modelItem) => {
  const activeMeasures = getActiveSemanticMeasures(modelItem)
    .sort((left, right) => {
      const leftSortOrder = Number.isFinite(Number(left?.SortOrder)) ? Number(left.SortOrder) : Number.MAX_SAFE_INTEGER;
      const rightSortOrder = Number.isFinite(Number(right?.SortOrder)) ? Number(right.SortOrder) : Number.MAX_SAFE_INTEGER;
      if (leftSortOrder !== rightSortOrder) {
        return leftSortOrder - rightSortOrder;
      }

      return String(left?.MeasureName ?? "").localeCompare(String(right?.MeasureName ?? ""), undefined, { sensitivity: "base" });
    });

  const visibleMeasures = activeMeasures.filter((measure) => measure?.IsVisible !== false);
  return visibleMeasures.length > 0 ? visibleMeasures : activeMeasures;
};

const getPreviewSemanticColumnNames = (modelItem) =>
  getOrderedPreviewSemanticColumns(modelItem)
    .map((column) => String(column?.ColumnName ?? "").trim())
    .filter(Boolean);

const getPreviewSemanticMeasureNames = (modelItem) =>
  getOrderedPreviewSemanticMeasures(modelItem)
    .map((measure) => String(measure?.MeasureName ?? "").trim())
    .filter(Boolean);

const getStructuredPreviewQueryError = (previewMode) => {
  if (isMeasuresOnlyPreviewExecutionMode(previewMode)) {
    return "No visible semantic measures are available for this preview mode.";
  }

  if (isColumnsOnlyPreviewExecutionMode(previewMode)) {
    return "No visible semantic columns are available for this preview mode.";
  }

  return "Unable to build the selected preview query.";
};

const buildStructuredPreviewQuery = (modelItem, previewMode, take = 50000) => {
  const modelId = getItemId(modelItem);
  if (!modelId) {
    return null;
  }

  const isMeasuresOnly = isMeasuresOnlyPreviewExecutionMode(previewMode);
  const isColumnsOnly = isColumnsOnlyPreviewExecutionMode(previewMode);
  if (!isMeasuresOnly && !isColumnsOnly) {
    return null;
  }

  const selectColumns = isColumnsOnly ? getPreviewSemanticColumnNames(modelItem) : [];
  const measureNames = isMeasuresOnly ? getPreviewSemanticMeasureNames(modelItem) : [];
  if ((isColumnsOnly && selectColumns.length === 0) || (isMeasuresOnly && measureNames.length === 0)) {
    return null;
  }

  return {
    Key: "",
    Label: "Preview Query",
    Description: "",
    QueryMode: 2,
    SemanticShape: isMeasuresOnly ? 2 : 1,
    SupportsRealTime: false,
    ModelKind: Number(modelItem?.ModelKind ?? 0),
    ModelId: modelId,
    SelectColumns: selectColumns,
    GroupByColumns: [],
    MeasureNames: measureNames,
    QueryAggregates: [],
    FilterDraft: [],
    RootFilterNode: null,
    Sorts: [],
    Groupings: [],
    VisibleColumns: isMeasuresOnly ? [...measureNames] : [...selectColumns],
    Limit: take,
    Offset: 0,
    Skip: 0,
    Take: take,
    SearchText: "",
  };
};

const buildStructuredPreviewTableQuery = (modelItem, previewMode, tableRequest = {}) => {
  const take = Math.max(1, Number(tableRequest?.Take ?? tableRequest?.take ?? 50) || 50);
  const nextQuery = buildStructuredPreviewQuery(modelItem, previewMode, take);
  if (!nextQuery) {
    return null;
  }

  const skip = Math.max(0, Number(tableRequest?.Skip ?? tableRequest?.skip ?? 0) || 0);
  const sortBy = String(tableRequest?.SortBy ?? tableRequest?.sortBy ?? "").trim();
  const sortDirection = String(tableRequest?.SortOrder ?? tableRequest?.sortOrder ?? "ASC").trim().toUpperCase() === "DESC"
    ? "DESC"
    : "ASC";

  nextQuery.Skip = skip;
  nextQuery.Take = take;
  nextQuery.Offset = skip;
  nextQuery.Limit = take;
  nextQuery.SortBy = sortBy;
  nextQuery.SortOrder = sortDirection === "DESC" ? 1 : 0;
  nextQuery.Sorts = sortBy
    ? [
      {
        Column: sortBy,
        Direction: sortDirection === "DESC" ? 2 : 1,
        Priority: 0,
        Stage: 1,
        Enabled: true,
      },
    ]
    : [];

  return nextQuery;
};

const buildPreviewSearchModel = (model) => {
  const referenceId = getModelReferenceId(model);
  if (!referenceId || getModelType(model) !== TABLE_TYPE) {
    return null;
  }

  const nextQuery = {
    Filters: sanitizeFilterDraft(getModelFilterDraft(model)),
    RootFilterNode: getModelFilterDefinition(model)?.RootFilterNode ?? buildItemFilterTree(getModelFilterDraft(model)),
  };
  const selectedColumns = getModelSemanticColumns(model)
    .map((column) => buildModelColumnFromSemanticColumn(column))
    .filter((column) => Number(column?.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) === SEMANTIC_COLUMN_KIND_BASE && column?.FieldName)
    .sort((a, b) => Number(a?.SortOrder ?? 0) - Number(b?.SortOrder ?? 0))
    .map((column) => column.FieldName);

  if (selectedColumns.length > 0) {
    nextQuery.SelectColumns = selectedColumns;
  }

  if (getModelSourceType(model) === 1) {
    nextQuery.SourceObjectId = referenceId;
  }

  if (getModelSourceType(model) === 2) {
    nextQuery.EntityObjectId = referenceId;
    nextQuery.WarehouseId = getModelWarehouseId(model) ?? "";
    nextQuery.WarehouseDeploymentId = getModelWarehouseDeploymentId(model) ?? "";
  }

  return Object.keys(nextQuery).length > 0 ? nextQuery : null;
};

const buildModelColumnFromSemanticColumn = (column, index = 0) => {
  const columnId = column?._id || column?.UniqueId || createClientId("column");
  const columnKind = Number(column?.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE);
  const valueDefinitionValue = String(column?.ValueDefinition?.Value ?? "");
  const fieldName = columnKind === SEMANTIC_COLUMN_KIND_BASE ? String(valueDefinitionValue || (column?.ColumnName ?? "")) : "";
  const valueKind = Number(column?.ValueDefinition?.ValueKind ?? getSemanticColumnValueKind(columnKind));

  return {
    _semanticKey: columnId,
    _id: columnId,
    UniqueId: column?.UniqueId || columnId,
    FieldName: fieldName,
    ValueDefinitionValue: valueDefinitionValue,
    ValueKind: valueKind,
    ColumnKind: columnKind,
    ColumnName: column?.ColumnName || fieldName,
    DisplayName: column?.DisplayName || column?.ColumnName || fieldName,
    Description: column?.Description || "",
    DataTypeName: column?.DataTypeName || "",
    Format: column?.Format || "",
    SemanticType: column?.SemanticType || "none",
    ShowOnGrid: column?.IsVisible ?? true,
    GridSequence: index,
    DisplaySequence: Number.isFinite(Number(column?.SortOrder)) ? Number(column.SortOrder) : index + 1,
    SortOrder: Number.isFinite(Number(column?.SortOrder)) ? Number(column.SortOrder) : index,
    CanSort: column?.CanSort ?? true,
    CanFilter: column?.CanFilter ?? true,
    CanGroup: column?.CanGroup ?? true,
    PrimaryField: false,
    IsActive: column?.IsActive !== false,
    IsDeleted: column?.IsDeleted === true,
  };
};

const buildModelMeasureFromSemanticMeasure = (measure, index = 0) => {
  const measureId = measure?._id || measure?.UniqueId || createClientId("measure");
  const measureKind = Number(measure?.MeasureKind ?? MEASURE_KIND_AGGREGATE);
  const definition = measure?.MeasureDefinition ?? {};

  return {
    _semanticKey: measureId,
    _id: measureId,
    UniqueId: measure?.UniqueId || measureId,
    MeasureName: measure?.MeasureName || "",
    DisplayName: measure?.DisplayName || measure?.MeasureName || "",
    Description: measure?.Description || "",
    MeasureKind: measureKind,
    AggregationKind: Number(measure?.AggregationKind ?? AGGREGATION_KIND_SUM),
    TargetName: String(definition?.TargetName ?? ""),
    ExpressionValue: String(definition?.Value ?? ""),
    DataTypeName: measure?.DataTypeName || "",
    Format: measure?.Format || "",
    SemanticType: measure?.SemanticType || "measure",
    IsVisible: measure?.IsVisible ?? true,
    SortOrder: Number.isFinite(Number(measure?.SortOrder)) ? Number(measure.SortOrder) : index,
    IsActive: measure?.IsActive !== false,
    IsDeleted: measure?.IsDeleted === true,
  };
};

const buildBackingFieldsFromModelColumns = (columns = []) =>
  (Array.isArray(columns) ? columns : [])
    .filter((column) => column?.ColumnName)
    .map((column, index) => ({
      FieldName: column.ColumnName,
      ObjectFieldName: column.ColumnName,
      DisplayName: column.DisplayName || column.ColumnName,
      Label: column.DisplayName || column.ColumnName,
      DataTypeName: column.DataTypeName || "",
      Format: column.Format || "",
      SemanticType: column.SemanticType || "none",
      CanSort: column.CanSort ?? true,
      CanFilter: column.CanFilter ?? true,
      CanGroup: column.CanGroup ?? true,
      ShowOnGrid: column.IsVisible ?? true,
      DisplaySequence: Number.isFinite(Number(column?.SortOrder)) ? Number(column.SortOrder) : index + 1,
      GridSequence: index,
      IsActive: column.IsActive !== false,
      IsDeleted: column.IsDeleted === true,
    }));

const createDraftSemanticColumn = (columnKind = SEMANTIC_COLUMN_KIND_CALCULATED) => {
  const columnId = createClientId("column");
  const normalizedKind = Number(columnKind) || SEMANTIC_COLUMN_KIND_CALCULATED;

  return {
    _semanticKey: columnId,
    _id: columnId,
    UniqueId: columnId,
    FieldName: "",
    ValueDefinitionValue: "",
    ValueKind: getSemanticColumnValueKind(normalizedKind),
    ColumnKind: normalizedKind,
    ColumnName: "",
    DisplayName: "",
    Description: "",
    DataTypeName: "System.String",
    Format: "",
    SemanticType: "none",
    ShowOnGrid: true,
    GridSequence: 0,
    DisplaySequence: 0,
    SortOrder: 0,
    CanSort: true,
    CanFilter: true,
    CanGroup: true,
    PrimaryField: false,
    IsActive: true,
    IsDeleted: false,
  };
};

const createSemanticColumnDraft = (columns = [], columnKind = SEMANTIC_COLUMN_KIND_CALCULATED) => {
  const normalizedKind = Number(columnKind) || SEMANTIC_COLUMN_KIND_CALCULATED;
  const baseLabel = normalizedKind === SEMANTIC_COLUMN_KIND_STATIC ? "Static Column" : "Calculated Column";
  const existingNames = new Set(
    (Array.isArray(columns) ? columns : [])
      .map((column) => String(getEffectiveColumnName(column) || "").trim().toLowerCase())
      .filter(Boolean)
  );

  let suffix = (Array.isArray(columns) ? columns.length : 0) + 1;
  let displayName = `${baseLabel} ${suffix}`;
  let columnName = toMachineKey(displayName);

  while (!columnName || existingNames.has(columnName.toLowerCase())) {
    suffix += 1;
    displayName = `${baseLabel} ${suffix}`;
    columnName = toMachineKey(displayName);
  }

  return {
    ...createDraftSemanticColumn(normalizedKind),
    ColumnName: columnName,
    DisplayName: displayName,
  };
};

const createDraftSemanticMeasure = () => {
  const measureId = createClientId("measure");

  return {
    _semanticKey: measureId,
    _id: measureId,
    UniqueId: measureId,
    MeasureName: "",
    DisplayName: "",
    Description: "",
    MeasureKind: MEASURE_KIND_AGGREGATE,
    AggregationKind: AGGREGATION_KIND_SUM,
    TargetName: "",
    ExpressionValue: "",
    DataTypeName: "System.Decimal",
    Format: "",
    SemanticType: "measure",
    IsVisible: true,
    SortOrder: 0,
    IsActive: true,
    IsDeleted: false,
  };
};

const createSemanticMeasureDraft = (measures = [], columns = []) => {
  const existingNames = new Set(
    (Array.isArray(measures) ? measures : [])
      .map((measure) => String(getEffectiveMeasureName(measure) || "").trim().toLowerCase())
      .filter(Boolean)
  );

  const compatibleColumns = getCompatibleMeasureTargetColumns(columns, AGGREGATION_KIND_SUM);
  const targetColumn =
    compatibleColumns[0]
    ?? (Array.isArray(columns) ? columns : [])[0]
    ?? null;

  const targetName = getEffectiveColumnName(targetColumn);
  const generatedNames = buildAutoGeneratedMeasureNames(
    {
      AggregationKind: AGGREGATION_KIND_SUM,
      TargetName: targetName,
    },
    columns
  );

  let suffix = (Array.isArray(measures) ? measures.length : 0) + 1;
  let displayName = generatedNames.displayName || `Measure ${suffix}`;
  let measureName = generatedNames.measureName || toMachineKey(displayName);

  while (!measureName || existingNames.has(measureName.toLowerCase())) {
    suffix += 1;
    if (generatedNames.measureName) {
      measureName = toColumnNameInput(`${generatedNames.measureName}_${suffix}`);
      displayName = generatedNames.displayName;
    } else {
      displayName = `Measure ${suffix}`;
      measureName = toMachineKey(displayName);
    }
  }

  return {
    ...createDraftSemanticMeasure(),
    MeasureName: measureName,
    DisplayName: displayName,
    TargetName: targetName,
  };
};

const SortableColumnRow = ({ column, index, onRemove, deleteDisabled = false, deleteTitle = "Remove column", isReadOnly = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(column?.FieldName ?? index),
  });
  const dragHandleProps = isReadOnly ? {} : { ...attributes, ...listeners };
  const effectiveDeleteDisabled = isReadOnly || deleteDisabled;
  const effectiveDeleteTitle = isReadOnly ? "Published Mode is view only." : deleteTitle;

  return (
    <div
      ref={setNodeRef}
      className={`model-column-row${isDragging ? " dragging" : ""}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <div className="model-column-row__left">
        {!isReadOnly ? (
          <button
            type="button"
            className="model-column-row__drag"
            title="Drag to reorder"
            {...dragHandleProps}
          >
            <IconControl iconKey="dragsixdots" />
          </button>
        ) : null}
        <div className="model-column-row__sequence">{index + 1}</div>
        <div className="model-column-row__details">
          <div className="model-column-row__title" title={column?.DisplayName || column?.ColumnName || column?.FieldName || "Column"}>
            {middleEllipsis(column?.DisplayName || column?.ColumnName || column?.FieldName || "Column", 36)}
          </div>
          <div className="model-column-row__subtitle" title={column?.ColumnName || column?.FieldName || ""}>
            {middleEllipsis(
              column?.ColumnName && column?.ColumnName !== column?.FieldName
                ? `${column.ColumnName} <= ${column.FieldName}`
                : column?.FieldName || "",
              36
            )}
          </div>
        </div>
      </div>
      <div className="model-column-row__actions">
        <div title={deleteTitle}>
          <ButtonControl
            type="icon-small"
            className="model-designer-delete-button"
            onClick={() => onRemove(column?.FieldName)}
            title={effectiveDeleteDisabled ? "" : effectiveDeleteTitle}
            disabled={effectiveDeleteDisabled}
          >
            <IconControl iconKey="delete" />
          </ButtonControl>
        </div>
      </div>
    </div>
  );
};

const SortableSemanticFieldRow = ({ column, index, sortableId, isSelected, hasError, errorMessage, defaultSortDirection, isPrimaryDate, onSelect, onRequestDelete, isReadOnly = false }) => {
  const columnKey = getSemanticColumnKey(column, `column-${index}`);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });
  const dragHandleProps = isReadOnly ? {} : { ...attributes, ...listeners };
  const publishedColumnName = getEffectiveColumnName(column) || column?.FieldName || "";
  const displayName = column?.DisplayName || column?.FieldName || "Column";
  const normalizedTypeName = formatColumnDataTypeName(column?.DataTypeName);
  const columnTitle = normalizedTypeName ? `${publishedColumnName} (${normalizedTypeName})` : publishedColumnName;
  const columnTypeLabel = getSemanticColumnTypeLabel(column?.ColumnKind);
  const columnTypeTitle = getSemanticColumnTypeTitle(column);
  const isDerivedColumn = Number(column?.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) !== SEMANTIC_COLUMN_KIND_BASE;
  const isHiddenColumn = column?.ShowOnGrid === false;

  return (
    <div
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      className={`semantic-editor__list-item${isSelected ? " selected" : ""}${isDragging ? " dragging" : ""}${isHiddenColumn ? " is-hidden" : ""}${hasError ? " has-error" : ""}`}
      title={errorMessage || undefined}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      onClick={() => onSelect(columnKey)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(columnKey);
        }
      }}
    >
      <div className="semantic-editor__list-item-header">
        <div className="semantic-editor__list-item-header-left">
          {!isReadOnly ? (
            <button
              type="button"
              className="semantic-editor__drag"
              title="Drag to reorder"
              onClick={(event) => event.stopPropagation()}
              {...dragHandleProps}
            >
              <IconControl iconKey="dragsixdots" />
            </button>
          ) : null}
          <div className="semantic-editor__list-item-text">
            <div className="semantic-editor__list-item-title" title={columnTitle}>
              {middleEllipsis(columnTitle, 36)}
            </div>
            <div className="semantic-editor__list-item-subtitle" title={displayName}>
              {middleEllipsis(displayName, 34)}
            </div>
          </div>
        </div>
        <div className="semantic-editor__list-item-header-right">
          <span className="semantic-editor__field-badge" title={columnTypeTitle || undefined}>{columnTypeLabel}</span>
          {isDerivedColumn ? (
            <ButtonControl
              type="icon-small"
              className="model-designer-delete-button"
              title={isReadOnly ? "Published Mode is view only." : "Delete column"}
              onClick={(event) => {
                event.stopPropagation();
                onRequestDelete?.(columnKey);
              }}
              disabled={isReadOnly}
            >
              <IconControl iconKey="delete" />
            </ButtonControl>
          ) : null}
        </div>
      </div>
      <div className="semantic-editor__list-item-meta">
        {isPrimaryDate ? <span className="semantic-editor__meta-chip">Primary Date</span> : null}
        {defaultSortDirection === DEFAULT_SORT_DIRECTION_ASC ? <span className="semantic-editor__meta-chip">Sort Asc</span> : null}
        {defaultSortDirection === DEFAULT_SORT_DIRECTION_DESC ? <span className="semantic-editor__meta-chip">Sort Desc</span> : null}
      </div>
    </div>
  );
};

const SemanticMeasureRow = ({
  measure,
  index,
  sortableId,
  isSelected,
  hasError,
  errorMessage,
  defaultSortDirection,
  onSelect,
  onRequestDelete,
  isReadOnly = false,
}) => {
  const measureKey = getSemanticMeasureKey(measure, `measure-${index}`);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });
  const dragHandleProps = isReadOnly ? {} : { ...attributes, ...listeners };
  const measureName = getEffectiveMeasureName(measure) || "measure";
  const displayName = measure?.DisplayName || measureName;
  const measureTypeLabel = getSemanticMeasureTypeLabel(measure?.MeasureKind);
  const measureTypeTitle = getSemanticMeasureTypeTitle(measure);
  const isCalculatedMeasure = Number(measure?.MeasureKind ?? MEASURE_KIND_AGGREGATE) === MEASURE_KIND_CALCULATED;
  const isHiddenMeasure = measure?.IsVisible === false;
  const targetSummary = isCalculatedMeasure
    ? "Expression"
    : measure?.TargetName
      ? `${AGGREGATION_KIND_OPTIONS.find((item) => Number(item.Value) === Number(measure?.AggregationKind))?.Text || "Aggregate"} of ${measure.TargetName}`
      : "Select a target column";

  return (
    <div
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      className={`semantic-editor__list-item${isSelected ? " selected" : ""}${isDragging ? " dragging" : ""}${isHiddenMeasure ? " is-hidden" : ""}${hasError ? " has-error" : ""}`}
      title={errorMessage || undefined}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      onClick={() => onSelect(measureKey)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(measureKey);
        }
      }}
    >
      <div className="semantic-editor__list-item-header">
        <div className="semantic-editor__list-item-header-left">
          {!isReadOnly ? (
            <button
              type="button"
              className="semantic-editor__drag"
              title="Drag to reorder"
              onClick={(event) => event.stopPropagation()}
              {...dragHandleProps}
            >
              <IconControl iconKey="dragsixdots" />
            </button>
          ) : null}
          <div className="semantic-editor__list-item-text">
            <div className="semantic-editor__list-item-title" title={measureName}>
              {middleEllipsis(measureName, 36)}
            </div>
            {targetSummary ? (
              <div className="semantic-editor__list-item-subtitle" title={`${displayName} | ${targetSummary}`}>
                {middleEllipsis(targetSummary, 42)}
              </div>
            ) : null}
          </div>
        </div>
        <div className="semantic-editor__list-item-header-right">
          <span className="semantic-editor__field-badge" title={measureTypeTitle || undefined}>{measureTypeLabel}</span>
          <ButtonControl
            type="icon-small"
            className="model-designer-delete-button"
            title={isReadOnly ? "Published Mode is view only." : "Delete measure"}
            onClick={(event) => {
              event.stopPropagation();
              onRequestDelete?.(measureKey);
            }}
            disabled={isReadOnly}
          >
            <IconControl iconKey="delete" />
          </ButtonControl>
        </div>
      </div>
      <div className="semantic-editor__list-item-meta">
        {defaultSortDirection === DEFAULT_SORT_DIRECTION_ASC ? <span className="semantic-editor__meta-chip">Sort Asc</span> : null}
        {defaultSortDirection === DEFAULT_SORT_DIRECTION_DESC ? <span className="semantic-editor__meta-chip">Sort Desc</span> : null}
      </div>
    </div>
  );
};

const getPrimaryDateOptions = (columns = []) =>
  (Array.isArray(columns) ? columns : [])
    .filter((column) => column?.IsActive !== false && column?.IsDeleted !== true && isDateLikeField(column))
    .map((column) => ({
      Text: column?.DisplayName || column?.ColumnName || column?.FieldName,
      Value: column?.ColumnName || column?.FieldName,
    }));

const unwrapServiceResult = (response) => {
  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  return response ?? null;
};

const sanitizeValidationMessage = (message) => {
  const normalizedMessage = String(message ?? "")
    .replace(/^Operation failed:\s*/i, "")
    .replace(/^Operation Field:\s*/i, "")
    .trim();

  const numericOperatorMatch = normalizedMessage.match(/^Operator '([+\-*/])' requires numeric operands\.?$/i);
  if (numericOperatorMatch) {
    const operatorName = {
      "+": "Addition",
      "-": "Subtraction",
      "*": "Multiplication",
      "/": "Division",
    }[numericOperatorMatch[1]] || "This operation";

    return `${operatorName} requires numeric values.`;
  }

  const booleanOperatorMatch = normalizedMessage.match(/^Operator '(AND|OR|NOT)' requires boolean operands\.?$/i);
  if (booleanOperatorMatch) {
    return `${booleanOperatorMatch[1].toUpperCase()} requires true/false values.`;
  }

  return normalizedMessage;
};

const getValidationDisplayMessages = (result) =>
  Object.values(unwrapServiceResult(result)?.ValidationErrors ?? {})
    .map((entry) => sanitizeValidationMessage(entry?.DisplayMessage ?? entry?.Content ?? ""))
    .filter(Boolean);

const getValidationEntries = (result) =>
  Object.entries(unwrapServiceResult(result)?.ValidationErrors ?? {}).map(([path, entry]) => ({
    path: String(path ?? ""),
    message: sanitizeValidationMessage(entry?.DisplayMessage ?? entry?.Content ?? ""),
  }));

const getValidationMessagesForPaths = (entries = [], candidatePaths = []) => {
  const normalizedCandidates = (Array.isArray(candidatePaths) ? candidatePaths : [candidatePaths])
    .map((path) => String(path ?? "").trim())
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return [];
  }

  return (Array.isArray(entries) ? entries : [])
    .filter((entry) =>
      normalizedCandidates.some((candidate) => entry?.path === candidate || String(entry?.path ?? "").endsWith(candidate))
    )
    .map((entry) => entry?.message)
    .filter(Boolean);
};

const shouldSuppressInlineValidationState = (candidatePaths = [], message = "") => {
  const normalizedMessage = String(message ?? "").trim().toLowerCase();
  if (!normalizedMessage.endsWith("is required.")) {
    return false;
  }

  const normalizedPaths = (Array.isArray(candidatePaths) ? candidatePaths : [candidatePaths])
    .map((path) => String(path ?? "").trim());

  return normalizedPaths.some((path) => path.endsWith(".ColumnName") || path.endsWith(".MeasureName"));
};

const removeValidationEntriesForPaths = (entries = [], candidatePaths = []) => {
  const normalizedCandidates = (Array.isArray(candidatePaths) ? candidatePaths : [candidatePaths])
    .map((path) => String(path ?? "").trim())
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return Array.isArray(entries) ? entries : [];
  }

  return (Array.isArray(entries) ? entries : []).filter(
    (entry) => !normalizedCandidates.some((candidate) => entry?.path === candidate || String(entry?.path ?? "").endsWith(candidate))
  );
};

const renderPanelError = (errorValue) => {
  const messages = Array.isArray(errorValue)
    ? errorValue.map((message) => sanitizeValidationMessage(message)).filter(Boolean)
    : sanitizeValidationMessage(errorValue)
      ? [sanitizeValidationMessage(errorValue)]
      : [];

  if (messages.length === 0) {
    return null;
  }

  const showList = messages.length > 1;

  return (
    <div className="model-panel-error">
      {showList ? (
        <ul className="model-panel-error__list">
          {messages.map((message, index) => (
            <li key={`${message}-${index}`}>{message}</li>
          ))}
        </ul>
      ) : (
        messages[0]
      )}
    </div>
  );
};

const escapeRegExp = (value) => String(value ?? "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildSemanticColumnValidationIdentifiers = (column, fallback = "") =>
  [...new Set([
    getSemanticValidationIdentity(column),
    getEffectiveColumnName(column),
    getSemanticColumnKey(column, fallback),
    column?.FieldName,
    column?._semanticKey,
    column?._id,
    column?.UniqueId,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean))];

const buildSemanticMeasureValidationIdentifiers = (measure, fallback = "") =>
  [...new Set([
    getSemanticMeasureValidationIdentity(measure),
    getEffectiveMeasureName(measure),
    getSemanticMeasureKey(measure, fallback),
    measure?._semanticKey,
    measure?._id,
    measure?.UniqueId,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean))];

const findSemanticValidationIssue = (entries = [], columns = [], measures = []) => {
  const normalizedEntries = Array.isArray(entries) ? entries : [];

  for (const entry of normalizedEntries) {
    const entryPath = String(entry?.path ?? "").trim();
    if (!entryPath) {
      continue;
    }

    for (let index = 0; index < columns.length; index += 1) {
      const column = columns[index];
      const identifier = buildSemanticColumnValidationIdentifiers(column, `column-${index}`)
        .find((candidate) => new RegExp(`^SemanticDefinition\\.Columns\\.${escapeRegExp(candidate)}(?:\\.|$)`, "i").test(entryPath));

      if (identifier) {
        return {
          itemType: "column",
          item: column,
          itemKey: getSemanticColumnKey(column, `column-${index}`),
          identity: identifier,
          leafPath: entryPath.replace(new RegExp(`^SemanticDefinition\\.Columns\\.${escapeRegExp(identifier)}\\.?`, "i"), ""),
          message: entry?.message || "",
        };
      }
    }

    for (let index = 0; index < measures.length; index += 1) {
      const measure = measures[index];
      const identifier = buildSemanticMeasureValidationIdentifiers(measure, `measure-${index}`)
        .find((candidate) => new RegExp(`^SemanticDefinition\\.Measures\\.${escapeRegExp(candidate)}(?:\\.|$)`, "i").test(entryPath));

      if (identifier) {
        return {
          itemType: "measure",
          item: measure,
          itemKey: getSemanticMeasureKey(measure, `measure-${index}`),
          identity: identifier,
          leafPath: entryPath.replace(new RegExp(`^SemanticDefinition\\.Measures\\.${escapeRegExp(identifier)}\\.?`, "i"), ""),
          message: entry?.message || "",
        };
      }
    }
  }

  return null;
};

const findSemanticValidationEntryForIdentifiers = (entries = [], collectionName, identifiers = []) => {
  const normalizedEntries = Array.isArray(entries) ? entries : [];
  const normalizedIdentifiers = [...new Set((Array.isArray(identifiers) ? identifiers : [])
    .map((value) => String(value ?? "").trim())
    .filter(Boolean))];

  if (!collectionName || normalizedIdentifiers.length === 0) {
    return null;
  }

  for (const entry of normalizedEntries) {
    const entryPath = String(entry?.path ?? "").trim();
    if (!entryPath) {
      continue;
    }

    const matchedIdentifier = normalizedIdentifiers.find((candidate) =>
      new RegExp(`^SemanticDefinition\\.${escapeRegExp(collectionName)}\\.${escapeRegExp(candidate)}(?:\\.|$)`, "i").test(entryPath)
    );

    if (!matchedIdentifier) {
      continue;
    }

    return {
      ...entry,
      identity: matchedIdentifier,
      leafPath: entryPath.replace(new RegExp(`^SemanticDefinition\\.${escapeRegExp(collectionName)}\\.${escapeRegExp(matchedIdentifier)}\\.?`, "i"), ""),
    };
  }

  return null;
};

const getSemanticColumnValidationEntry = (entries = [], column, fallback = "") =>
  findSemanticValidationEntryForIdentifiers(
    entries,
    "Columns",
    buildSemanticColumnValidationIdentifiers(column, fallback)
  );

const getSemanticMeasureValidationEntry = (entries = [], measure, fallback = "") =>
  findSemanticValidationEntryForIdentifiers(
    entries,
    "Measures",
    buildSemanticMeasureValidationIdentifiers(measure, fallback)
  );

const describeSemanticValidationField = (leafPath = "") => {
  const normalizedLeafPath = String(leafPath ?? "").trim();

  switch (normalizedLeafPath) {
  case "ValueDefinition.Value":
  case "MeasureDefinition.Value":
    return "expression";
  case "MeasureDefinition.TargetName":
    return "target column";
  case "AggregationKind":
    return "aggregation";
  case "ColumnName":
    return "column name";
  case "MeasureName":
    return "measure name";
  case "DisplayName":
    return "display name";
  case "Description":
    return "description";
  case "DataTypeName":
    return "data type";
  case "ColumnKind":
    return "column type";
  case "MeasureKind":
    return "measure type";
  default:
    return "field";
  }
};

const buildSemanticValidationSummary = (issue) => {
  if (!issue?.item) {
    return "";
  }

  if (issue.itemType === "measure") {
    const measure = issue.item;
    const measureName = getEffectiveMeasureName(measure) || measure?.DisplayName || "measure";
    const measureType = Number(measure?.MeasureKind ?? MEASURE_KIND_AGGREGATE) === MEASURE_KIND_CALCULATED
      ? "formula"
      : "aggregate measure";

    return `Save blocked by ${measureType} "${measureName}" ${describeSemanticValidationField(issue.leafPath)}: ${issue.message}`;
  }

  const column = issue.item;
  const columnName = getEffectiveColumnName(column) || column?.DisplayName || "column";
  const columnKind = Number(column?.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE);
  const columnType = columnKind === SEMANTIC_COLUMN_KIND_CALCULATED
    ? "calculated column"
    : columnKind === SEMANTIC_COLUMN_KIND_STATIC
      ? "static column"
      : "column";

  return `Save blocked by ${columnType} "${columnName}" ${describeSemanticValidationField(issue.leafPath)}: ${issue.message}`;
};

const buildPanelErrorFromValidationEntries = (entries = [], columns = [], measures = []) => {
  const normalizedEntries = Array.isArray(entries) ? entries : [];
  if (normalizedEntries.length === 0) {
    return "";
  }

  const focusedIssue = findSemanticValidationIssue(normalizedEntries, columns, measures);
  const focusedIssueMessage = buildSemanticValidationSummary(focusedIssue);
  if (focusedIssueMessage) {
    return focusedIssueMessage;
  }

  const messages = [...new Set(normalizedEntries.map((entry) => entry?.message).filter(Boolean))];
  if (messages.length === 0) {
    return "";
  }

  return messages.length === 1 ? messages[0] : messages;
};

const buildModelList = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      id: item?._id,
      type: getModelType(item),
      title: item?.Label || item?.Key || `Untitled Model ${getModelType(item) === VIEW_TYPE ? "View" : "Table"}`,
      subtitle: item?.Key || "",
      item,
    }))
    .sort((a, b) =>
    String(a?.title ?? "").localeCompare(String(b?.title ?? ""))
  );

const createBlankModel = () => ({
  UiModelKind: MODEL_KIND_TABLE,
  Label: "",
  Key: "",
  Description: "",
  SourceType: 0,
  ReferenceId: "",
  ViewAuthoringMode: VIEW_AUTHORING_MODE_BUILDER,
  ViewPrimaryModelId: "",
  ViewTemplate: "",
  BuildSequence: DEFAULT_BUILD_SEQUENCE,
  PrimaryDateColumnName: "",
  DefaultSorts: [],
  PermissionPolicy: "",
  FilterDraft: [],
  Columns: [],
  Measures: [],
  ColumnsInitializedForReferenceId: "",
  SelectedSourceSystemId: "",
  SelectedSourceObjectId: "",
  SelectedSchemaId: "",
  SelectedEntityObjectId: "",
  SelectedWarehouseId: "",
  SelectedWarehouseDeploymentId: "",
  IsActive: true,
  IsDeleted: false,
});

const createEditableModel = ({
  item,
  sourceSystemId = "",
  schemaId = "",
}) => ({
  ...createBlankModel(),
  UiModelKind: getModelType(item) === VIEW_TYPE ? MODEL_KIND_VIEW : MODEL_KIND_TABLE,
  SourceType: getModelSourceType(item),
  ReferenceId: getModelReferenceId(item),
  ViewAuthoringMode: getModelViewAuthoringMode(item),
  ViewPrimaryModelId: getModelViewPrimaryModelId(item),
  ViewTemplate: getModelViewTemplate(item) || "",
  Label: item?.Label || "",
  Key: item?.Key || "",
  Description: item?.Description || "",
  BuildSequence: item?.BuildSequence ?? DEFAULT_BUILD_SEQUENCE,
  PrimaryDateColumnName: getModelPrimaryDateColumnName(item) || "",
  DefaultSorts: getModelDefaultSorts(item).map((sort) => ({ ...sort })),
  PermissionPolicy: item?.PermissionPolicy || "",
  Columns: getModelSemanticColumns(item).map((column, index) => buildModelColumnFromSemanticColumn(column, index)),
  Measures: getModelSemanticMeasures(item).map((measure, index) => buildModelMeasureFromSemanticMeasure(measure, index)),
  ColumnsInitializedForReferenceId:
    getModelType(item) === VIEW_TYPE
      ? getModelViewPrimaryModelId(item)
      : getModelReferenceId(item) && getModelSemanticColumns(item).length > 0
        ? getModelReferenceId(item)
        : "",
  SelectedSourceSystemId: getModelSourceType(item) === 1 ? String(sourceSystemId || "") : "",
  SelectedSourceObjectId: getModelSourceType(item) === 1 ? String(getModelReferenceId(item) || "") : "",
  SelectedSchemaId: getModelSourceType(item) === 2 ? String(schemaId || "") : "",
  SelectedEntityObjectId: getModelSourceType(item) === 2 ? String(getModelReferenceId(item) || "") : "",
  SelectedWarehouseId: getModelWarehouseId(item) ? String(getModelWarehouseId(item)) : "",
  SelectedWarehouseDeploymentId: getModelWarehouseDeploymentId(item) ? String(getModelWarehouseDeploymentId(item)) : "",
  FilterDraft: getModelFilterDraft(item) ?? [],
  IsActive: item?.IsActive !== false,
  IsDeleted: item?.IsDeleted === true,
  _rawItem: item,
});

const DataModelDesigner = ({ dashboardMode = false, createMode = false }) => {
  const navigate = useNavigate();
  const { dataModelId, workspaceId, dashboardId } = useParams();
  const dashboardDomain = useDashboardDomainContext() ?? {};
  const activeDashboard = dashboardDomain?.activeDashboard ?? null;
  const refreshDataModelCount = dashboardDomain?.refreshDataModelCount;
  const dashboardIsViewMode = dashboardDomain?.isViewMode === true;
  const switchDashboardMode = dashboardDomain?.switchMode;
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const { getRoute } = useCoreRoute();
  const location = useLocation();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const modelWorkspacesBaseRoute = useMemo(
    () => toAbsoluteRoutePath(
      getRoute("/model-workspaces/")?.RoutePath ??
      getRoute("/dynamic/datamodelworkspace/")?.RoutePath,
      "/model-workspaces"
    ),
    [getRoute]
  );
  const routeWorkspaceId = dashboardMode ? "" : String(workspaceId ?? "").trim();
  const routeDataModelId = dashboardMode ? "" : String(dataModelId ?? "").trim();
  const isStandaloneCreateMode = !dashboardMode && createMode === true;

  const modelMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModel);
  const dataQueryMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataQuery);
  const workspaceMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModelWorkspace);
  const buildRunMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModelWorkspaceBuildRun);
  const dashboardMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Dashboard);
  const sourceSystemMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.SourceSystem);
  const sourceObjectMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.SourceObject);
  const schemaMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Schema);
  const entityObjectMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.EntityObject);
  const warehouseMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Warehouse);
  const modelService = useMemo(
    () => itemServiceFactory.GetItemService(itemServiceFactory.ItemServiceEnum.DataModel, coreState, coreContext, true),
    [coreContext, coreState, itemServiceFactory]
  );
  const workspaceService = useMemo(
    () => itemServiceFactory.GetItemService(itemServiceFactory.ItemServiceEnum.DataModelWorkspace, coreState, coreContext, true),
    [coreContext, coreState, itemServiceFactory]
  );
  const artifactVersionService = useMemo(
    () => itemServiceFactory.GetItemService(itemServiceFactory.ItemServiceEnum.DataModelWorkspaceArtifactVersion, coreState, coreContext, true),
    [coreContext, coreState, itemServiceFactory]
  );
  const dashboardService = useMemo(
    () => itemServiceFactory.GetItemService(itemServiceFactory.ItemServiceEnum.Dashboard, coreState, coreContext, true),
    [coreContext, coreState, itemServiceFactory]
  );
  const uiFormatOptions = useMemo(
    () => modelMgr?.selectData?.find((item) => item?.DataOperation === SELECTDATA_UIFORMAT_OPERATION)?.jsonData ?? [],
    [modelMgr?.selectData]
  );

  const [models, setModels] = useState([]);
  const [dashboardWorkspaceIdOverride, setDashboardWorkspaceIdOverride] = useState("");
  const [isResolvingDashboardWorkspace, setIsResolvingDashboardWorkspace] = useState(false);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [standaloneWorkspaceSurfaceMode, setStandaloneWorkspaceSurfaceMode] = useState(WORKSPACE_SURFACE_MODE_DESIGN);
  const [designVirtualDbTarget, setDesignVirtualDbTarget] = useState(DESIGN_VIRTUAL_DB_TARGET_DESIGN);
  const [isWorkspaceEditDialogOpen, setIsWorkspaceEditDialogOpen] = useState(false);
  const [workspaceEditDraft, setWorkspaceEditDraft] = useState(() => createWorkspaceEditDraft(null));
  const [workspaceEditError, setWorkspaceEditError] = useState("");
  const [isWorkspaceEditSaving, setIsWorkspaceEditSaving] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [isModelsLoading, setIsModelsLoading] = useState(true);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isSavingModel, setIsSavingModel] = useState(false);
  const [isValidatingExpression, setIsValidatingExpression] = useState(false);
  const [isDeletingModel, setIsDeletingModel] = useState(false);
  const [validatedSemanticExpressionKey, setValidatedSemanticExpressionKey] = useState(null);
  const [panelError, setPanelError] = useState("");
  const [panelValidationEntries, setPanelValidationEntries] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);
  const [workingModel, setWorkingModel] = useState(null);
  const [panelMode, setPanelMode] = useState("create");
  const [panelWorkflow, setPanelWorkflow] = useState(PANEL_WORKFLOW_RELATION);
  const [labelTouched, setLabelTouched] = useState(false);
  const [keyTouched, setKeyTouched] = useState(false);
  const [sourceSystems, setSourceSystems] = useState([]);
  const [sourceObjects, setSourceObjects] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [entityObjects, setEntityObjects] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isPanelLookupLoading, setIsPanelLookupLoading] = useState(false);
  const [selectedModelSourceName, setSelectedModelSourceName] = useState("");
  const [selectedModelContext, setSelectedModelContext] = useState({
    sourceTypeLabel: "",
    containerLabel: "",
  });
  const [selectedModelWarehouseContext, setSelectedModelWarehouseContext] = useState({
    warehouseLabel: "",
    deploymentLabel: "",
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSemanticDeleteDialogOpen, setIsSemanticDeleteDialogOpen] = useState(false);
  const [pendingSemanticDeleteKind, setPendingSemanticDeleteKind] = useState(SEMANTIC_DELETE_KIND_COLUMN);
  const [pendingSemanticDeleteColumnKey, setPendingSemanticDeleteColumnKey] = useState(null);
  const [pendingSemanticDeleteColumnLabel, setPendingSemanticDeleteColumnLabel] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [panelActiveTab, setPanelActiveTab] = useState(DETAILS_TAB_MODEL);
  const [semanticEditorTab, setSemanticEditorTab] = useState(SEMANTIC_EDITOR_TAB_DETAILS);
  const [editingColumnFieldName, setEditingColumnFieldName] = useState(null);
  const [selectedSemanticColumnFieldName, setSelectedSemanticColumnFieldName] = useState(null);
  const [selectedSemanticMeasureKey, setSelectedSemanticMeasureKey] = useState(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [filterLoadError, setFilterLoadError] = useState("");
  const [panelFilterDraft, setPanelFilterDraft] = useState(createDefaultFilterDraft);
  const [, setSavedPanelFilterDraft] = useState(createDefaultFilterDraft);
  const [selectedModelBackingObject, setSelectedModelBackingObject] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewTotalItemCount, setPreviewTotalItemCount] = useState(null);
  const [previewRequested, setPreviewRequested] = useState(false);
  const [previewTableMgr, setPreviewTableMgr] = useState(null);
  const [previewTableMetadata, setPreviewTableMetadata] = useState(null);
  const [previewExecutionMode, setPreviewExecutionMode] = useState(DATA_EXECUTION_MODE.VIRTUAL_DB_COLUMNS);
  const [isModelSqlDialogOpen, setIsModelSqlDialogOpen] = useState(false);
  const [modelSqlText, setModelSqlText] = useState("");
  const [modelSqlError, setModelSqlError] = useState("");
  const [isModelSqlLoading, setIsModelSqlLoading] = useState(false);
  const [isRuntimeLoadDialogOpen, setIsRuntimeLoadDialogOpen] = useState(false);
  const [runtimeLoadResult, setRuntimeLoadResult] = useState(null);
  const [runtimeLoadError, setRuntimeLoadError] = useState("");
  const [isRuntimeLoadLoading, setIsRuntimeLoadLoading] = useState(false);
  const [runtimeLoadActionLabel, setRuntimeLoadActionLabel] = useState("Load");
  const [runtimeStatusDialogTab, setRuntimeStatusDialogTab] = useState(RUNTIME_STATUS_TAB_OVERVIEW);
  const [isRuntimeDropDialogOpen, setIsRuntimeDropDialogOpen] = useState(false);
  const [runtimeDropConfirmText, setRuntimeDropConfirmText] = useState("");
  const [runtimeDropTarget, setRuntimeDropTarget] = useState(RUNTIME_DROP_TARGET_VIRTUAL);
  const [runtimeStatusSummary, setRuntimeStatusSummary] = useState(null);
  const [runtimeStatusError, setRuntimeStatusError] = useState("");
  const [artifactStatusSummary, setArtifactStatusSummary] = useState(null);
  const [artifactStatusError, setArtifactStatusError] = useState("");
  const [isArtifactActionDialogOpen, setIsArtifactActionDialogOpen] = useState(false);
  const [artifactActionResult, setArtifactActionResult] = useState(null);
  const [artifactActionError, setArtifactActionError] = useState("");
  const [isArtifactActionLoading, setIsArtifactActionLoading] = useState(false);
  const [artifactActionLabel, setArtifactActionLabel] = useState("Status");
  const [artifactStatusDialogTab, setArtifactStatusDialogTab] = useState(ARTIFACT_STATUS_TAB_OVERVIEW);
  const [isPublishArtifactConfirmDialogOpen, setIsPublishArtifactConfirmDialogOpen] = useState(false);
  const [isPublishArtifactConfirmSaving, setIsPublishArtifactConfirmSaving] = useState(false);
  const [publishArtifactPublicState, setPublishArtifactPublicState] = useState(false);
  const [publishArtifactConfirmError, setPublishArtifactConfirmError] = useState("");
  const [isRebuildArtifactConfirmDialogOpen, setIsRebuildArtifactConfirmDialogOpen] = useState(false);
  const [expressionFieldSearch, setExpressionFieldSearch] = useState("");
  const [expressionUndoByColumnKey, setExpressionUndoByColumnKey] = useState({});

  const modelMgrRef = useRef(modelMgr);
  const dataQueryMgrRef = useRef(dataQueryMgr);
  const workspaceMgrRef = useRef(workspaceMgr);
  const buildRunMgrRef = useRef(buildRunMgr);
  const dashboardMgrRef = useRef(dashboardMgr);
  const sourceSystemMgrRef = useRef(sourceSystemMgr);
  const sourceObjectMgrRef = useRef(sourceObjectMgr);
  const schemaMgrRef = useRef(schemaMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const warehouseMgrRef = useRef(warehouseMgr);
  const workspaceServiceRef = useRef(workspaceService);
  const artifactVersionServiceRef = useRef(artifactVersionService);
  const dashboardServiceRef = useRef(dashboardService);
  const previewRequestIdRef = useRef(0);
  const loadPreviewForModelRef = useRef(null);
  const autoPreviewOnModeChangeRef = useRef(false);
  const semanticListRef = useRef(null);
  const semanticExpressionInputRef = useRef(null);
  const ensuringDashboardWorkspaceRef = useRef(false);
  const currentWorkspaceRequestIdRef = useRef("");

  const getWorkspaceProcessorTypeOverride = useCallback((workspaceId) => {
    const normalizedWorkspaceId = String(workspaceId ?? "").trim();
    const currentWorkspaceKey = String(currentWorkspace?._id ?? currentWorkspace?.UniqueId ?? "").trim();
    if (!normalizedWorkspaceId || !currentWorkspaceKey || normalizedWorkspaceId !== currentWorkspaceKey) {
      return undefined;
    }

    return Number(currentWorkspace?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL)
      === DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH
      ? PROCESSOR_TYPE_REMOTE
      : undefined;
  }, [currentWorkspace?._id, currentWorkspace?.ServerExecutionHost, currentWorkspace?.UniqueId]);

  useEffect(() => {
    modelMgrRef.current = modelMgr;
  }, [modelMgr]);

  useEffect(() => {
    dataQueryMgrRef.current = dataQueryMgr;
  }, [dataQueryMgr]);

  useEffect(() => {
    workspaceMgrRef.current = workspaceMgr;
  }, [workspaceMgr]);

  useEffect(() => {
    buildRunMgrRef.current = buildRunMgr;
  }, [buildRunMgr]);

  useEffect(() => {
    dashboardMgrRef.current = dashboardMgr;
  }, [dashboardMgr]);

  useEffect(() => {
    sourceSystemMgrRef.current = sourceSystemMgr;
  }, [sourceSystemMgr]);

  useEffect(() => {
    sourceObjectMgrRef.current = sourceObjectMgr;
  }, [sourceObjectMgr]);

  useEffect(() => {
    schemaMgrRef.current = schemaMgr;
  }, [schemaMgr]);

  useEffect(() => {
    entityObjectMgrRef.current = entityObjectMgr;
  }, [entityObjectMgr]);

  useEffect(() => {
    warehouseMgrRef.current = warehouseMgr;
  }, [warehouseMgr]);

  useEffect(() => {
    workspaceServiceRef.current = workspaceService;
  }, [workspaceService]);

  useEffect(() => {
    artifactVersionServiceRef.current = artifactVersionService;
  }, [artifactVersionService]);

  useEffect(() => {
    dashboardServiceRef.current = dashboardService;
  }, [dashboardService]);

  useEffect(() => {
    if (!dashboardMode) {
      return;
    }

    setDashboardWorkspaceIdOverride("");
    setIsResolvingDashboardWorkspace(true);
    currentWorkspaceRequestIdRef.current = "";
    setCurrentWorkspace(null);
  }, [dashboardId, dashboardMode]);

  const currentWorkspaceId = useMemo(
    () => String(
      dashboardMode
        ? (dashboardWorkspaceIdOverride || activeDashboard?.DataModelWorkspaceId || "")
        : routeWorkspaceId
    ).trim(),
    [activeDashboard?.DataModelWorkspaceId, dashboardMode, dashboardWorkspaceIdOverride, routeWorkspaceId]
  );
  const workspaceSurfaceMode = dashboardMode
    ? (dashboardIsViewMode ? WORKSPACE_SURFACE_MODE_PUBLISHED : WORKSPACE_SURFACE_MODE_DESIGN)
    : standaloneWorkspaceSurfaceMode;
  const isWorkspaceDesignMode = workspaceSurfaceMode === WORKSPACE_SURFACE_MODE_DESIGN;
  const isWorkspacePublishedMode = workspaceSurfaceMode === WORKSPACE_SURFACE_MODE_PUBLISHED;
  const isWorkspacePanelReadOnly = !isWorkspaceDesignMode && panelMode === "edit";
  const modelWorkspacesHomeRoute = useMemo(() => {
    if (dashboardMode) {
      return "";
    }

    const currentPath = toAbsoluteRoutePath(location?.pathname, "");
    const normalizedWorkspaceId = String(routeWorkspaceId ?? "").trim();
    if (currentPath && normalizedWorkspaceId) {
      const workspaceMarker = `/${normalizedWorkspaceId}`;
      const markerIndex = currentPath.toLowerCase().indexOf(workspaceMarker.toLowerCase());
      if (markerIndex > 0) {
        return currentPath.slice(0, markerIndex);
      }
    }

    return toAbsoluteRoutePath(
      getRoute("/model-workspaces/")?.RoutePath ??
      getRoute("/dynamic/datamodelworkspace/")?.RoutePath,
      "/model-workspaces"
    );
  }, [dashboardMode, getRoute, location?.pathname, routeWorkspaceId]);

  const buildStandaloneWorkspaceRoute = useCallback((targetWorkspaceId) => {
    const normalizedWorkspaceId = String(targetWorkspaceId ?? "").trim();
    return normalizedWorkspaceId ? `${modelWorkspacesBaseRoute}/${normalizedWorkspaceId}` : modelWorkspacesBaseRoute;
  }, [modelWorkspacesBaseRoute]);

  const buildStandaloneDataModelRoute = useCallback((workspaceValue, modelId) => {
    const normalizedWorkspaceId = String(workspaceValue ?? "").trim();
    const normalizedModelId = String(modelId ?? "").trim();
    return normalizedWorkspaceId && normalizedModelId
      ? `${modelWorkspacesBaseRoute}/${normalizedWorkspaceId}/models/${normalizedModelId}`
      : buildStandaloneWorkspaceRoute(normalizedWorkspaceId);
  }, [buildStandaloneWorkspaceRoute, modelWorkspacesBaseRoute]);

  const handleNavigateToWorkspaceHome = useCallback(() => {
    if (dashboardMode) {
      return;
    }

    navigate(modelWorkspacesHomeRoute, { replace: false });
  }, [dashboardMode, modelWorkspacesHomeRoute, navigate]);

  const handleSetWorkspaceSurfaceMode = useCallback((nextMode) => {
    const normalizedMode = String(nextMode ?? "").trim().toLowerCase() === WORKSPACE_SURFACE_MODE_PUBLISHED
      ? WORKSPACE_SURFACE_MODE_PUBLISHED
      : WORKSPACE_SURFACE_MODE_DESIGN;

    autoPreviewOnModeChangeRef.current = true;

    if (dashboardMode) {
      switchDashboardMode?.(normalizedMode === WORKSPACE_SURFACE_MODE_PUBLISHED ? "view" : "design");
      return;
    }

    setStandaloneWorkspaceSurfaceMode(normalizedMode);
  }, [dashboardMode, switchDashboardMode]);

  useEffect(() => {
    if (modelMgr?.selectData?.find((item) => item?.DataOperation === SELECTDATA_UIFORMAT_OPERATION)) {
      return;
    }

    modelMgr?.GetSelectData?.({
      DisplayName: "Format",
      FieldName: "Format",
      DataOperation: SELECTDATA_UIFORMAT_OPERATION,
    });
  }, [modelMgr]);

  const selectedModel = useMemo(
    () => models.find((model) => String(model?.id) === String(selectedModelId)) ?? null,
    [models, selectedModelId]
  );

  const isSystemAdmin = useMemo(
    () => Array.isArray(coreContext?.ProfileRoles) && coreContext.ProfileRoles.some((role) => role?.IsSystemAdmin === true),
    [coreContext?.ProfileRoles]
  );

  const previewExecutionModeOptions = useMemo(
    () => buildModelPreviewExecutionModeOptions(selectedModel?.type),
    [selectedModel?.type]
  );

  const isRealtimePreviewMode = isRealtimePreviewExecutionMode(previewExecutionMode);
  const isServerPreviewMode = isServerPreviewExecutionMode(previewExecutionMode);
  const isClientPreviewMode = isClientPreviewExecutionMode(previewExecutionMode);
  const isMeasuresOnlyPreviewMode = isMeasuresOnlyPreviewExecutionMode(previewExecutionMode);
  const isRealtimePreviewAvailable = !!selectedModel && selectedModel.type === TABLE_TYPE && isRealtimePreviewMode;
  const isServerPreviewAvailable = !!selectedModel && isServerPreviewMode;

  const selectedSourceObject = useMemo(
    () => sourceObjects.find((item) => String(getItemId(item)) === String(workingModel?.SelectedSourceObjectId ?? "")) ?? null,
    [sourceObjects, workingModel?.SelectedSourceObjectId]
  );

  const selectedEntityObject = useMemo(
    () => entityObjects.find((item) => String(getItemId(item)) === String(workingModel?.SelectedEntityObjectId ?? "")) ?? null,
    [entityObjects, workingModel?.SelectedEntityObjectId]
  );

  const selectedPrimaryModel = useMemo(
    () => models.find((item) => String(item?.id) === String(workingModel?.ViewPrimaryModelId ?? ""))?.item ?? null,
    [models, workingModel?.ViewPrimaryModelId]
  );

  const selectedModelPrimaryModelListItem = useMemo(
    () => models.find((item) => String(item?.id) === String(getModelViewPrimaryModelId(selectedModel?.item))) ?? null,
    [models, selectedModel?.item]
  );

  const selectedPrimaryModelFields = useMemo(
    () => buildBackingFieldsFromModelColumns(getModelSemanticColumns(selectedPrimaryModel)),
    [selectedPrimaryModel]
  );

  const selectedBackingObject = useMemo(() => {
    if (workingModel?.UiModelKind === MODEL_KIND_VIEW) {
      if (
        getModelViewAuthoringMode(workingModel) !== VIEW_AUTHORING_MODE_BUILDER ||
        !selectedPrimaryModel
      ) {
        return null;
      }

      return {
        _id: selectedPrimaryModel?._id,
        Name: selectedPrimaryModel?.Label || selectedPrimaryModel?.Key || "Primary Model",
        DisplayName: selectedPrimaryModel?.Label || selectedPrimaryModel?.Key || "Primary Model",
        Fields: selectedPrimaryModelFields,
      };
    }

    if (Number(workingModel?.SourceType) === 1) {
      return selectedSourceObject;
    }

    if (Number(workingModel?.SourceType) === 2) {
      return selectedEntityObject;
    }

    return null;
  }, [selectedEntityObject, selectedPrimaryModel, selectedPrimaryModelFields, selectedSourceObject, workingModel]);

  useEffect(() => {
    if (!selectedModel) {
      setPreviewExecutionMode(DATA_EXECUTION_MODE.VIRTUAL_DB_COLUMNS);
      return;
    }

    if (selectedModel.type === VIEW_TYPE && isRealtimePreviewExecutionMode(previewExecutionMode)) {
      setPreviewExecutionMode(DATA_EXECUTION_MODE.VIRTUAL_DB_COLUMNS);
    }
  }, [previewExecutionMode, selectedModel]);

  useEffect(() => {
    previewRequestIdRef.current += 1;
    setPreviewLoading(false);
    setPreviewRequested(false);
    setPreviewRows([]);
    setPreviewTotalItemCount(null);
    setPreviewError("");
    setPreviewTableMgr(null);
    setPreviewTableMetadata(null);
  }, [previewExecutionMode]);

  const columnSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const sourceSystemOptions = useMemo(
    () => sourceSystems.map(normalizeSourceSystemOption).filter((item) => item.Value),
    [sourceSystems]
  );

  const sourceObjectOptions = useMemo(
    () => sourceObjects.map(normalizeSourceObjectOption).filter((item) => item.Value),
    [sourceObjects]
  );

  const schemaOptions = useMemo(
    () => schemas.map(normalizeSchemaOption).filter((item) => item.Value),
    [schemas]
  );

  const entityObjectOptions = useMemo(
    () => entityObjects.map(normalizeEntityObjectOption).filter((item) => item.Value),
    [entityObjects]
  );

  const warehouseOptions = useMemo(
    () => warehouses.map(normalizeWarehouseOption).filter((item) => item.Value),
    [warehouses]
  );

  const selectedWarehouse = useMemo(
    () => warehouses.find((item) => String(getItemId(item)) === String(workingModel?.SelectedWarehouseId ?? "")) ?? null,
    [warehouses, workingModel?.SelectedWarehouseId]
  );

  const warehouseDeploymentOptions = useMemo(
    () =>
      getWarehouseDeployments(selectedWarehouse)
        .filter((item) => item?.IsActive !== false && item?.IsDeleted !== true)
        .map(normalizeWarehouseDeploymentOption)
        .filter((item) => item.Value),
    [selectedWarehouse]
  );

  const primaryDateOptions = useMemo(() => getPrimaryDateOptions(workingModel?.Columns ?? []), [workingModel?.Columns]);
  const primaryDateFieldOptions = useMemo(
    () => [{ Text: "None", Value: "" }, ...primaryDateOptions],
    [primaryDateOptions]
  );

  const viewModelOptions = useMemo(
    () =>
      models
        .filter((model) => String(model?.id) !== String(workingModel?._id ?? ""))
        .map((model) => ({
          Text: model?.title || model?.subtitle || "Untitled Model",
          Value: String(model?.id ?? ""),
        }))
        .filter((option) => option.Value),
    [models, workingModel?._id]
  );

  const columnSelectionOptions = useMemo(
    () => normalizeColumnSelectionOptions(selectedBackingObject?.Fields ?? []),
    [selectedBackingObject?.Fields]
  );

  const selectedColumnFieldNames = useMemo(
    () => [...(Array.isArray(workingModel?.Columns) ? workingModel.Columns : []).filter(isRelationBaseColumn)]
      .sort((a, b) => Number(a?.GridSequence ?? 0) - Number(b?.GridSequence ?? 0))
      .map((column) => column?.FieldName)
      .filter(Boolean),
    [workingModel?.Columns]
  );

  const relationColumns = useMemo(
    () => [...(Array.isArray(workingModel?.Columns) ? workingModel.Columns : []).filter(isRelationBaseColumn)]
      .sort((a, b) => Number(a?.GridSequence ?? 0) - Number(b?.GridSequence ?? 0)),
    [workingModel?.Columns]
  );

  const relationColumnDeleteStateByFieldName = useMemo(
    () =>
      Object.fromEntries(
        relationColumns.map((column) => {
          const fieldName = String(column?.FieldName ?? "");
          const usageReasons = getRelationColumnUsageReasons(workingModel, column);
          const deleteTitle = usageReasons.length > 0
            ? `Column is in use by ${usageReasons.join(", ")}.`
            : "Remove column";

          return [
            fieldName,
            {
              deleteDisabled: usageReasons.length > 0,
              deleteTitle,
            },
          ];
        })
      ),
    [relationColumns, workingModel]
  );

  const editingColumn = useMemo(
    () => (workingModel?.Columns ?? []).find((column) => String(column?.FieldName) === String(editingColumnFieldName)) ?? null,
    [editingColumnFieldName, workingModel?.Columns]
  );

  const selectedSemanticColumn = useMemo(
    () =>
      (workingModel?.Columns ?? []).find(
        (column, index) => getSemanticColumnKey(column, `column-${index}`) === String(selectedSemanticColumnFieldName)
      ) ?? null,
    [selectedSemanticColumnFieldName, workingModel?.Columns]
  );
  const selectedSemanticMeasure = useMemo(
    () =>
      (workingModel?.Measures ?? []).find(
        (measure, index) => getSemanticMeasureKey(measure, `measure-${index}`) === String(selectedSemanticMeasureKey)
      ) ?? null,
    [selectedSemanticMeasureKey, workingModel?.Measures]
  );
  const selectedSemanticColumnDisplay = useMemo(() => {
    if (!selectedSemanticColumn) {
      return {
        columnName: "",
        displayName: "",
        typeName: "",
        title: "",
      };
    }

    const columnName = getEffectiveColumnName(selectedSemanticColumn) || selectedSemanticColumn.FieldName || "";
    const displayName = selectedSemanticColumn.DisplayName || columnName;
    const typeName = formatColumnDataTypeName(selectedSemanticColumn.DataTypeName);

    return {
      columnName,
      displayName,
      typeName,
      title: typeName ? `${columnName} (${typeName})` : columnName,
    };
  }, [selectedSemanticColumn]);
  const selectedSemanticMeasureDisplay = useMemo(() => {
    if (!selectedSemanticMeasure) {
      return {
        measureName: "",
        displayName: "",
        title: "",
      };
    }

    const measureName = getEffectiveMeasureName(selectedSemanticMeasure);
    const displayName = selectedSemanticMeasure.DisplayName || measureName;

    return {
      measureName,
      displayName,
      title: measureName,
    };
  }, [selectedSemanticMeasure]);
  const selectedSemanticColumnExpressionValidationPaths = useMemo(() => {
    if (!selectedSemanticColumn) {
      return ["SemanticDefinition.Columns.ValueDefinition.Value"];
    }

    return [
      buildSemanticColumnValidationPath(selectedSemanticColumn, "ValueDefinition.Value"),
      "SemanticDefinition.Columns.ValueDefinition.Value",
    ];
  }, [selectedSemanticColumn]);
  const selectedSemanticMeasureExpressionValidationPaths = useMemo(() => {
    if (!selectedSemanticMeasure) {
      return ["SemanticDefinition.Measures.MeasureDefinition.Value"];
    }

    return [
      buildSemanticMeasureValidationPath(selectedSemanticMeasure, "MeasureDefinition.Value"),
      "SemanticDefinition.Measures.MeasureDefinition.Value",
    ];
  }, [selectedSemanticMeasure]);
  const isSelectedSemanticExpressionValidated = useMemo(
    () => {
      if (selectedSemanticColumn) {
        return String(validatedSemanticExpressionKey ?? "") === String(getSemanticColumnKey(selectedSemanticColumn));
      }

      if (selectedSemanticMeasure) {
        return String(validatedSemanticExpressionKey ?? "") === String(getSemanticMeasureKey(selectedSemanticMeasure));
      }

      return false;
    },
    [selectedSemanticColumn, selectedSemanticMeasure, validatedSemanticExpressionKey]
  );
  const selectedSemanticColumnKey = useMemo(
    () => (selectedSemanticColumn ? String(getSemanticColumnKey(selectedSemanticColumn)) : ""),
    [selectedSemanticColumn]
  );
  const selectedSemanticMeasureIdentityKey = useMemo(
    () => (selectedSemanticMeasure ? String(getSemanticMeasureKey(selectedSemanticMeasure)) : ""),
    [selectedSemanticMeasure]
  );
  const selectedSemanticExpressionOwnerKey = selectedSemanticColumnKey || selectedSemanticMeasureIdentityKey;
  const focusSemanticValidationIssue = useCallback((entries = []) => {
    const issue = findSemanticValidationIssue(entries, workingModel?.Columns ?? [], workingModel?.Measures ?? []);

    if (!issue) {
      return null;
    }

    setPanelWorkflow(PANEL_WORKFLOW_SEMANTIC);

    if (issue.itemType === "measure") {
      setSelectedSemanticColumnFieldName(null);
      setSelectedSemanticMeasureKey(issue.itemKey);
    } else {
      setSelectedSemanticMeasureKey(null);
      setSelectedSemanticColumnFieldName(issue.itemKey);
    }

    const shouldShowExpressionTab = issue.itemType === "measure"
      ? String(issue.leafPath ?? "").startsWith("MeasureDefinition.Value")
      : Number(issue?.item?.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) === SEMANTIC_COLUMN_KIND_CALCULATED
        && String(issue.leafPath ?? "").startsWith("ValueDefinition.Value");

    setSemanticEditorTab(shouldShowExpressionTab ? SEMANTIC_EDITOR_TAB_EXPRESSION : SEMANTIC_EDITOR_TAB_DETAILS);

    return issue;
  }, [workingModel?.Columns, workingModel?.Measures]);
  const clearPanelValidationForPaths = useCallback((candidatePaths) => {
    const nextValidationEntries = removeValidationEntriesForPaths(
      panelValidationEntries,
      candidatePaths
    );

    setPanelValidationEntries(nextValidationEntries);
    setPanelError(buildPanelErrorFromValidationEntries(
      nextValidationEntries,
      workingModel?.Columns ?? [],
      workingModel?.Measures ?? []
    ));
  }, [panelValidationEntries, workingModel?.Columns, workingModel?.Measures]);
  const canUndoSelectedSemanticExpression = useMemo(
    () => !!selectedSemanticExpressionOwnerKey && (expressionUndoByColumnKey[selectedSemanticExpressionOwnerKey]?.length ?? 0) > 0,
    [expressionUndoByColumnKey, selectedSemanticExpressionOwnerKey]
  );
  const expressionFieldInsertOptions = useMemo(
    () =>
      (workingModel?.Columns ?? [])
        .filter((column, index) => getSemanticColumnKey(column, `column-${index}`) !== String(selectedSemanticColumnFieldName))
        .map((column) => ({
          key: getSemanticColumnKey(column),
          label: getEffectiveColumnName(column),
          typeName: formatColumnDataTypeName(column?.DataTypeName),
          insertText: buildExpressionFieldReference(column),
        }))
        .filter((item) => item.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [selectedSemanticColumnFieldName, workingModel?.Columns]
  );
  const expressionMeasureInsertOptions = useMemo(
    () =>
      (workingModel?.Measures ?? [])
        .filter((measure, index) => getSemanticMeasureKey(measure, `measure-${index}`) !== String(selectedSemanticMeasureKey))
        .map((measure) => ({
          key: getSemanticMeasureKey(measure),
          label: getEffectiveMeasureName(measure),
          displayName: measure?.DisplayName || getEffectiveMeasureName(measure),
          insertText: buildExpressionMeasureReference(measure),
        }))
        .filter((item) => item.label),
    [selectedSemanticMeasureKey, workingModel?.Measures]
  );
  const filteredExpressionFieldInsertOptions = useMemo(() => {
    const searchText = String(expressionFieldSearch ?? "").trim().toLowerCase();
    if (!searchText) {
      return expressionFieldInsertOptions;
    }

    return expressionFieldInsertOptions.filter((item) => {
      const label = String(item?.label ?? "").toLowerCase();
      const typeName = String(item?.typeName ?? "").toLowerCase();
      return label.includes(searchText) || typeName.includes(searchText);
    });
  }, [expressionFieldInsertOptions, expressionFieldSearch]);
  const filteredExpressionMeasureInsertOptions = useMemo(() => {
    const searchText = String(expressionFieldSearch ?? "").trim().toLowerCase();
    if (!searchText) {
      return expressionMeasureInsertOptions;
    }

    return expressionMeasureInsertOptions.filter((item) => {
      const label = String(item?.label ?? "").toLowerCase();
      const displayName = String(item?.displayName ?? "").toLowerCase();
      return label.includes(searchText) || displayName.includes(searchText);
    });
  }, [expressionFieldSearch, expressionMeasureInsertOptions]);
  const aggregateExpressionInsertOptions = useMemo(
    () => [
      ...expressionFieldInsertOptions.map((item) => ({
        ...item,
        key: `field:${item.key}`,
      })),
      ...expressionMeasureInsertOptions.map((item) => ({
        ...item,
        key: `measure:${item.key}`,
        typeName: item.displayName && item.displayName !== item.label
          ? `${item.displayName} | measure`
          : "measure",
      })),
    ].sort((a, b) => String(a?.label ?? "").localeCompare(String(b?.label ?? ""))),
    [expressionFieldInsertOptions, expressionMeasureInsertOptions]
  );
  const filteredAggregateExpressionInsertOptions = useMemo(() => {
    const searchText = String(expressionFieldSearch ?? "").trim().toLowerCase();
    if (!searchText) {
      return aggregateExpressionInsertOptions;
    }

    return aggregateExpressionInsertOptions.filter((item) => {
      const label = String(item?.label ?? "").toLowerCase();
      const typeName = String(item?.typeName ?? "").toLowerCase();
      return label.includes(searchText) || typeName.includes(searchText);
    });
  }, [aggregateExpressionInsertOptions, expressionFieldSearch]);
  const measureTargetOptions = useMemo(
    () => {
      const aggregationKind = Number(selectedSemanticMeasure?.AggregationKind ?? AGGREGATION_KIND_SUM);

      return getCompatibleMeasureTargetColumns(workingModel?.Columns ?? [], aggregationKind)
        .map((column) => {
          const columnName = getEffectiveColumnName(column);
          const displayName = column?.DisplayName || columnName;
          const typeName = formatColumnDataTypeName(column?.DataTypeName);

          return {
            Text: typeName ? `${displayName} (${typeName})` : displayName,
            Value: columnName,
          };
        })
        .filter((option) => option.Value);
    },
    [selectedSemanticMeasure?.AggregationKind, workingModel?.Columns]
  );
  const selectedSemanticMeasureDefaultSortDirection = useMemo(
    () => getMeasureDefaultSortDirection(workingModel?.DefaultSorts, getEffectiveMeasureName(selectedSemanticMeasure)),
    [selectedSemanticMeasure, workingModel?.DefaultSorts]
  );
  const semanticListItems = useMemo(
    () => buildSemanticListItemsFromCollections(workingModel?.Columns ?? [], workingModel?.Measures ?? []),
    [workingModel?.Columns, workingModel?.Measures]
  );
  const nextTopSemanticSortOrder = useMemo(() => {
    if (semanticListItems.length === 0) {
      return 0;
    }

    return Math.min(...semanticListItems.map((item) => Number(item?.sortOrder ?? 0))) - 1;
  }, [semanticListItems]);

  useEffect(() => {
    if (!selectedSemanticColumn && !selectedSemanticMeasure) {
      setSemanticEditorTab(SEMANTIC_EDITOR_TAB_DETAILS);
      return;
    }

    if (
      selectedSemanticColumn &&
      Number(selectedSemanticColumn.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) === SEMANTIC_COLUMN_KIND_STATIC &&
      semanticEditorTab === SEMANTIC_EDITOR_TAB_EXPRESSION
    ) {
      setSemanticEditorTab(SEMANTIC_EDITOR_TAB_DETAILS);
    }

    if (
      selectedSemanticMeasure &&
      Number(selectedSemanticMeasure.MeasureKind ?? MEASURE_KIND_AGGREGATE) !== MEASURE_KIND_CALCULATED &&
      semanticEditorTab === SEMANTIC_EDITOR_TAB_EXPRESSION
    ) {
      setSemanticEditorTab(SEMANTIC_EDITOR_TAB_DETAILS);
    }
  }, [selectedSemanticColumn, selectedSemanticMeasure, semanticEditorTab]);

  useEffect(() => {
    if (!selectedSemanticMeasureKey || !selectedSemanticMeasure) {
      return;
    }

    if (Number(selectedSemanticMeasure.MeasureKind ?? MEASURE_KIND_AGGREGATE) !== MEASURE_KIND_AGGREGATE) {
      return;
    }

    const compatibleColumns = getCompatibleMeasureTargetColumns(
      workingModel?.Columns ?? [],
      Number(selectedSemanticMeasure.AggregationKind ?? AGGREGATION_KIND_SUM)
    );
    const hasCurrentTarget = compatibleColumns.some(
      (column) =>
        String(getEffectiveColumnName(column)).trim().toLowerCase()
        === String(selectedSemanticMeasure.TargetName ?? "").trim().toLowerCase()
    );

    if (hasCurrentTarget) {
      return;
    }

    const nextTargetName = getEffectiveColumnName(compatibleColumns[0]) || "";
    if (String(nextTargetName).trim().toLowerCase() === String(selectedSemanticMeasure.TargetName ?? "").trim().toLowerCase()) {
      return;
    }

    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        Measures: (prev.Measures ?? []).map((measure, index) =>
          getSemanticMeasureKey(measure, `measure-${index}`) === String(selectedSemanticMeasureKey)
            ? {
                ...measure,
                TargetName: nextTargetName,
              }
            : measure
        ),
      };
    });
  }, [
    selectedSemanticMeasure,
    selectedSemanticMeasureKey,
    workingModel?.Columns,
  ]);

  useEffect(() => {
    setExpressionFieldSearch("");
  }, [selectedSemanticColumnFieldName, selectedSemanticMeasureKey]);

  const getPanelFieldValidationMessages = useCallback(
    (candidatePaths) => getValidationMessagesForPaths(panelValidationEntries, candidatePaths),
    [panelValidationEntries]
  );
  const renderValidatedField = useCallback(
    (candidatePaths, fieldControl, options = {}) => {
      const messages = getPanelFieldValidationMessages(candidatePaths);
      const primaryMessage = messages[0] || "";
      const useMinimalInlineValidationState = shouldSuppressInlineValidationState(candidatePaths, primaryMessage);
      const showSuccess = !!options?.success && !primaryMessage;

      return (
        <div className={`model-builder-field${primaryMessage && !useMinimalInlineValidationState ? " model-builder-field--error" : ""}${primaryMessage && useMinimalInlineValidationState ? " model-builder-field--input-error-only" : ""}${showSuccess ? " model-builder-field--success" : ""}`}>
          {fieldControl}
          {primaryMessage && !useMinimalInlineValidationState ? <div className="model-builder-field__error">{primaryMessage}</div> : null}
        </div>
      );
    },
    [getPanelFieldValidationMessages]
  );

  const panelFilterTargetId = useMemo(
    () =>
      workingModel?.UiModelKind === MODEL_KIND_VIEW
        ? String(workingModel?.ViewPrimaryModelId ?? "")
        : String(workingModel?.ReferenceId ?? ""),
    [workingModel?.ReferenceId, workingModel?.UiModelKind, workingModel?.ViewPrimaryModelId]
  );

  const panelFilterMetadata = useMemo(() => {
    if (workingModel?.UiModelKind === MODEL_KIND_VIEW) {
      return null;
    }

    return Number(workingModel?.SourceType) === 2 ? entityObjectMgr?.uiMetadata : sourceObjectMgr?.uiMetadata;
  }, [entityObjectMgr?.uiMetadata, sourceObjectMgr?.uiMetadata, workingModel?.SourceType, workingModel?.UiModelKind]);

  const objectLevelFilterFields = useMemo(
    () => (Array.isArray(selectedBackingObject?.Fields) ? selectedBackingObject.Fields : []),
    [selectedBackingObject?.Fields]
  );

  const panelFilterFields = useMemo(
    () => {
      if (objectLevelFilterFields.length > 0) {
        return objectLevelFilterFields;
      }

      const metadataItem = Array.isArray(panelFilterMetadata) ? panelFilterMetadata[0] : panelFilterMetadata;
      return Array.isArray(metadataItem?.Fields) ? metadataItem.Fields : [];
    },
    [objectLevelFilterFields, panelFilterMetadata]
  );

  const panelFilterables = useMemo(
    () => panelFilterFields.filter((field) => field?.CanFilter !== false && !field?.SubUIItemId),
    [panelFilterFields]
  );

  const sanitizedPanelFilterDraft = useMemo(
    () => sanitizeFilterDraft(panelFilterDraft),
    [panelFilterDraft]
  );

  const hasPanelFilter = useMemo(
    () => sanitizedPanelFilterDraft.length > 0,
    [sanitizedPanelFilterDraft]
  );

  const selectedModelFilterSummary = useMemo(
    () => summarizeFilters(getModelFilterDraft(selectedModel?.item)),
    [selectedModel?.item]
  );

  const selectedModelFilterState = selectedModelFilterSummary === "All Data" ? "All Data" : "Filtered";
  const selectedModelFilterChipText = useMemo(() => {
    if (previewTotalItemCount === null || previewTotalItemCount === undefined) {
      return selectedModelFilterState;
    }

    return `${Number(previewTotalItemCount).toLocaleString()} Records`;
  }, [previewTotalItemCount, selectedModelFilterState]);
  const selectedModelSemanticCounts = useMemo(() => {
    const columnCount = getActiveSemanticColumns(selectedModel?.item).length;
    const measureCount = getActiveSemanticMeasures(selectedModel?.item).length;

    return {
      columnCount,
      measureCount,
      totalCount: columnCount + measureCount,
    };
  }, [selectedModel?.item]);

  const previewFieldOverrides = useMemo(
    () =>
      buildPreviewFieldOverrides(
        selectedModelBackingObject?.Fields ?? [],
        getModelSemanticColumns(selectedModel?.item).map((column, index) => buildModelColumnFromSemanticColumn(column, index))
      ),
    [selectedModel?.item, selectedModelBackingObject?.Fields]
  );

  const canSaveModel = useMemo(() => {
    if (!workingModel) {
      return false;
    }

    if (!String(workingModel?.Label ?? "").trim() || !String(workingModel?.Key ?? "").trim()) {
      return false;
    }

    if (workingModel.UiModelKind === MODEL_KIND_TABLE) {
      return Number(workingModel?.SourceType) > 0 && String(workingModel?.ReferenceId ?? "").trim() !== "";
    }

    if (workingModel.UiModelKind === MODEL_KIND_VIEW) {
      if (Number(workingModel?.ViewAuthoringMode) === VIEW_AUTHORING_MODE_SQL) {
        return String(workingModel?.ViewTemplate ?? "").trim() !== "";
      }

      return String(workingModel?.ViewPrimaryModelId ?? "").trim() !== "";
    }

    return false;
  }, [workingModel]);

  const fetchWorkspaceItemById = useCallback(async (workspaceId) => {
    const normalizedWorkspaceId = String(workspaceId ?? "").trim();
    if (!normalizedWorkspaceId) {
      return null;
    }

    let workspaceItem = null;
    const workspaceResult = await workspaceServiceRef.current?.single(normalizedWorkspaceId);
    const normalizedWorkspaceResult = Array.isArray(workspaceResult) ? workspaceResult[0] : workspaceResult;
    workspaceItem = normalizedWorkspaceResult?.Items?.[0] ?? normalizedWorkspaceResult?.Item ?? null;

    if (!workspaceItem) {
      const workspaceListResult = await workspaceMgrRef.current?.Query?.({
        model: "x => x.IsActive == true && x.IsDeleted == false",
      });
      const workspaceItems = Array.isArray(workspaceListResult?.Items) ? workspaceListResult.Items : [];
      workspaceItem = workspaceItems.find((item) => {
        const itemId = String(item?._id ?? item?.UniqueId ?? item?.Value ?? "").trim();
        const itemUniqueId = String(item?.UniqueId ?? "").trim();
        return itemId === normalizedWorkspaceId || itemUniqueId === normalizedWorkspaceId;
      }) ?? null;
    }

    return workspaceItem;
  }, []);

  const fetchArtifactVersionById = useCallback(async (artifactId) => {
    const normalizedArtifactId = String(artifactId ?? "").trim();
    if (!normalizedArtifactId) {
      return null;
    }

    const artifactResult = await artifactVersionServiceRef.current?.single(normalizedArtifactId);
    const normalizedArtifactResult = Array.isArray(artifactResult) ? artifactResult[0] : artifactResult;
    return normalizedArtifactResult?.Items?.[0] ?? normalizedArtifactResult?.Item ?? null;
  }, []);

  const loadArtifactStatusSummary = useCallback(async (workspaceId, workspaceItemOverride = null) => {
    const normalizedWorkspaceId = String(workspaceId ?? "").trim();
    if (!normalizedWorkspaceId) {
      return null;
    }

    const workspaceItem = workspaceItemOverride ?? await fetchWorkspaceItemById(normalizedWorkspaceId);
    if (!workspaceItem) {
      return null;
    }

    const currentPublishedArtifactVersionId = String(workspaceItem?.CurrentPublishedArtifactVersionId ?? "").trim();
    const latestSuccessfulArtifactVersionId = String(workspaceItem?.LatestSuccessfulArtifactVersionId ?? "").trim();
    const artifactIds = Array.from(
      new Set(
        [currentPublishedArtifactVersionId, latestSuccessfulArtifactVersionId]
          .map((value) => String(value ?? "").trim())
          .filter(Boolean)
      )
    );

    const artifactResults = await Promise.all(
      artifactIds.map(async (artifactId) => ({
        artifactId,
        artifact: await fetchArtifactVersionById(artifactId).catch(() => null),
      }))
    );
    const artifactById = artifactResults.reduce((accumulator, entry) => {
      if (entry?.artifactId) {
        accumulator[entry.artifactId] = entry?.artifact ?? null;
      }
      return accumulator;
    }, {});

    const buildRunResult = await buildRunMgrRef.current?.Query?.({
      model: "x => x.IsActive == true && x.IsDeleted == false && x.DataModelWorkspaceId == workspaceId",
      params: { workspaceId: normalizedWorkspaceId },
    });
    const buildRuns = Array.isArray(buildRunResult?.Items) ? buildRunResult.Items : [];
    const latestBuildRun = [...buildRuns].sort((left, right) => (
      getBuildRunSortDateValue(right) - getBuildRunSortDateValue(left)
    ))[0] ?? null;

    return {
      workspace: workspaceItem,
      currentPublishedArtifact: artifactById[currentPublishedArtifactVersionId] ?? null,
      latestSuccessfulArtifact: artifactById[latestSuccessfulArtifactVersionId] ?? null,
      latestBuildRun,
    };
  }, [fetchArtifactVersionById, fetchWorkspaceItemById]);

  useEffect(() => {
    let alive = true;

    const loadCurrentWorkspace = async () => {
      if (!currentWorkspaceId) {
        currentWorkspaceRequestIdRef.current = "";
        setCurrentWorkspace(null);
        return;
      }

      if (currentWorkspaceRequestIdRef.current === currentWorkspaceId) {
        return;
      }

      currentWorkspaceRequestIdRef.current = currentWorkspaceId;

      try {
        const workspaceItem = await fetchWorkspaceItemById(currentWorkspaceId);

        if (!alive) {
          return;
        }

        setCurrentWorkspace(workspaceItem);
      } finally {
        if (currentWorkspaceRequestIdRef.current === currentWorkspaceId) {
          currentWorkspaceRequestIdRef.current = "";
        }
      }
    };

    void loadCurrentWorkspace();

    return () => {
      alive = false;
    };
  }, [currentWorkspaceId, fetchWorkspaceItemById]);

  useEffect(() => {
    if (!isWorkspaceEditDialogOpen) {
      return;
    }

    setWorkspaceEditDraft(createWorkspaceEditDraft(currentWorkspace));
    setWorkspaceEditError("");
  }, [currentWorkspace, isWorkspaceEditDialogOpen]);

  useEffect(() => {
    let alive = true;

    const ensureDashboardWorkspace = async () => {
      if (!dashboardMode) {
        setIsResolvingDashboardWorkspace(false);
        return;
      }

      if (dashboardWorkspaceIdOverride) {
        setIsResolvingDashboardWorkspace(false);
        return;
      }

      if (!dashboardId || ensuringDashboardWorkspaceRef.current) {
        return;
      }

      ensuringDashboardWorkspaceRef.current = true;
      if (alive) {
        setIsResolvingDashboardWorkspace(true);
      }

      let resolvedDashboardItem = activeDashboard?._id ? activeDashboard : null;
      if (!resolvedDashboardItem && dashboardId) {
        const dashboardResult = await dashboardServiceRef.current?.single(dashboardId);
        const normalizedDashboardResult = Array.isArray(dashboardResult) ? dashboardResult[0] : dashboardResult;
        resolvedDashboardItem = normalizedDashboardResult?.Items?.[0] ?? normalizedDashboardResult?.Item ?? null;
      }

      if (!alive || !resolvedDashboardItem?._id) {
        ensuringDashboardWorkspaceRef.current = false;
        if (alive) {
          setIsResolvingDashboardWorkspace(false);
        }
        return;
      }

      const existingWorkspaceId = String(resolvedDashboardItem?.DataModelWorkspaceId ?? "").trim();
      if (existingWorkspaceId) {
        setDashboardWorkspaceIdOverride(existingWorkspaceId);
        setIsResolvingDashboardWorkspace(false);
        ensuringDashboardWorkspaceRef.current = false;
        return;
      }

      const dashboardName = String(resolvedDashboardItem?.DraftDetails?.Name ?? resolvedDashboardItem?.Name ?? resolvedDashboardItem?.Key ?? "Dashboard").trim() || "Dashboard";
      try {
        const workspaceResponse = await workspaceServiceRef.current?.save({
          Name: `${dashboardName} Model Workspace`,
          Description: `Workspace for dashboard ${dashboardName}.`,
          Version: "1.0.0",
          IsActive: true,
          IsDeleted: false,
        });
        const normalizedWorkspaceResponse = Array.isArray(workspaceResponse) ? workspaceResponse[0] : workspaceResponse;
        const savedWorkspace = normalizedWorkspaceResponse?.Items?.first?.() ?? normalizedWorkspaceResponse?.Items?.[0] ?? null;

        if (!alive || !savedWorkspace?._id) {
          return;
        }

        const dashboardResponse = await dashboardServiceRef.current?.save({
          ...resolvedDashboardItem,
          DataModelWorkspaceId: savedWorkspace._id,
          UniqueId: resolvedDashboardItem?._id,
          _id: resolvedDashboardItem?._id,
        });
        const normalizedDashboardResponse = Array.isArray(dashboardResponse) ? dashboardResponse[0] : dashboardResponse;
        if (!alive || normalizedDashboardResponse?.Success === false) {
          try {
            await workspaceMgrRef.current?.deleteItem?.(savedWorkspace._id, false);
          } catch (cleanupError) {
            console.error("Model workspace cleanup failed after dashboard workspace attach error:", cleanupError);
          }
          if (alive) {
            setToastMessage("Unable to attach a model workspace to this dashboard.");
          }
          return;
        }

        setDashboardWorkspaceIdOverride(savedWorkspace._id);
        setCurrentWorkspace(savedWorkspace);
      } finally {
        if (alive) {
          setIsResolvingDashboardWorkspace(false);
        }
        ensuringDashboardWorkspaceRef.current = false;
      }
    };

    void ensureDashboardWorkspace();

    return () => {
      alive = false;
    };
  }, [activeDashboard, dashboardId, dashboardMode, dashboardWorkspaceIdOverride]);

  const loadModels = useCallback(async () => {
    if (dashboardMode && !currentWorkspaceId && isResolvingDashboardWorkspace) {
      setIsModelsLoading(true);
      return;
    }

    setIsModelsLoading(true);
    try {
      if (!currentWorkspaceId) {
        setModels([]);
        setSelectedModelId(null);
        return;
      }

      const modelResult = await modelMgrRef.current?.Query({
        model: "x => x.IsActive == true && x.IsDeleted == false",
      });

      const nextModels = buildModelList(
        (Array.isArray(modelResult?.Items) ? modelResult.Items : []).filter((item) =>
          String(item?.DataModelWorkspaceId ?? "").trim() === currentWorkspaceId
        )
      );
      setModels(nextModels);
      setSelectedModelId((prev) => {
        if (!dashboardMode) {
          if (isStandaloneCreateMode) {
            return null;
          }

          if (routeDataModelId && nextModels.some((model) => String(model?.id) === routeDataModelId)) {
            return routeDataModelId;
          }

          return nextModels.some((model) => String(model?.id) === String(prev)) ? prev : null;
        }

        if (nextModels.some((model) => String(model?.id) === String(prev))) {
          return prev;
        }

        return nextModels[0]?.id ?? null;
      });
    } finally {
      setIsModelsLoading(false);
    }
  }, [currentWorkspaceId, dashboardMode, isResolvingDashboardWorkspace, isStandaloneCreateMode, routeDataModelId]);

  useEffect(() => {
    void loadModels();
  }, [loadModels]);

  useEffect(() => {
    if (dashboardMode) {
      return;
    }

    if (isStandaloneCreateMode) {
      setSelectedModelId(null);
      return;
    }

    setSelectedModelId(routeDataModelId || null);
  }, [dashboardMode, isStandaloneCreateMode, routeDataModelId]);

  useEffect(() => {
    if (dashboardMode || isStandaloneCreateMode || isModelsLoading || !routeDataModelId) {
      return;
    }

    if (models.length > 0 && !models.some((model) => String(model?.id) === routeDataModelId)) {
      navigate(buildStandaloneWorkspaceRoute(currentWorkspaceId), { replace: true });
    }
  }, [buildStandaloneWorkspaceRoute, currentWorkspaceId, dashboardMode, isModelsLoading, isStandaloneCreateMode, models, navigate, routeDataModelId]);

  useEffect(() => {
    let alive = true;

    const loadPanelLookups = async () => {
      if (!isPanelOpen) {
        return;
      }

      setIsPanelLookupLoading(true);
      try {
        const [sourceSystemResult, schemaResult] = await Promise.all([
          sourceSystemMgrRef.current?.Query({
            model: "x => x.IsActive == true && x.IsDeleted == false",
          }),
          schemaMgrRef.current?.Query({
            model: "x => x.IsActive == true && x.IsDeleted == false",
          }),
        ]);

        if (!alive) {
          return;
        }

        setSourceSystems(sourceSystemResult?.Items ?? []);
        setSchemas(schemaResult?.Items ?? []);
      } finally {
        if (alive) {
          setIsPanelLookupLoading(false);
        }
      }
    };

    void loadPanelLookups();

    return () => {
      alive = false;
    };
  }, [isPanelOpen]);

  useEffect(() => {
    let alive = true;

    const loadSourceObjects = async () => {
      if (!isPanelOpen || Number(workingModel?.SourceType) !== 1 || !workingModel?.SelectedSourceSystemId) {
        setSourceObjects([]);
        return;
      }

      setIsPanelLookupLoading(true);
      try {
        const result = await sourceObjectMgrRef.current?.Query({
          model: "x => x.IsActive == true && x.IsDeleted == false && x.SourceSystemId == sourceSystemId",
          params: { sourceSystemId: workingModel.SelectedSourceSystemId },
        });

        if (!alive) {
          return;
        }

        setSourceObjects(result?.Items ?? []);
      } finally {
        if (alive) {
          setIsPanelLookupLoading(false);
        }
      }
    };

    void loadSourceObjects();

    return () => {
      alive = false;
    };
  }, [isPanelOpen, workingModel?.SelectedSourceSystemId, workingModel?.SourceType]);

  useEffect(() => {
    let alive = true;

    const loadEntityObjects = async () => {
      if (!isPanelOpen || Number(workingModel?.SourceType) !== 2 || !workingModel?.SelectedSchemaId) {
        setEntityObjects([]);
        setWarehouses([]);
        return;
      }

      setIsPanelLookupLoading(true);
      try {
        const [entityResult, warehouseResult] = await Promise.all([
          entityObjectMgrRef.current?.Query({
            model: "x => x.IsActive == true && x.IsDeleted == false && x.SchemaId == schemaId",
            params: { schemaId: workingModel.SelectedSchemaId },
          }),
          warehouseMgrRef.current?.Query({
            model: "x => x.IsActive == true && x.IsDeleted == false && x.SchemaId == schemaId",
            params: { schemaId: workingModel.SelectedSchemaId },
          }),
        ]);

        if (!alive) {
          return;
        }

        setEntityObjects(entityResult?.Items ?? []);
        setWarehouses(warehouseResult?.Items ?? []);
      } finally {
        if (alive) {
          setIsPanelLookupLoading(false);
        }
      }
    };

    void loadEntityObjects();

    return () => {
      alive = false;
    };
  }, [isPanelOpen, workingModel?.SelectedSchemaId, workingModel?.SourceType]);

  useEffect(() => {
    if (!isPanelOpen || Number(workingModel?.SourceType) !== 2) {
      return;
    }

    if (!workingModel?.SelectedWarehouseDeploymentId || workingModel?.SelectedWarehouseId) {
      return;
    }

    const owningWarehouse = warehouses.find((warehouse) =>
      getWarehouseDeployments(warehouse).some(
        (deployment) => String(getItemId(deployment)) === String(workingModel.SelectedWarehouseDeploymentId)
      )
    );

    if (!owningWarehouse) {
      return;
    }

    setWorkingModel((prev) => {
      if (!prev || prev.SelectedWarehouseId) {
        return prev;
      }

      return {
        ...prev,
        SelectedWarehouseId: String(getItemId(owningWarehouse) ?? ""),
      };
    });
  }, [
    isPanelOpen,
    warehouses,
    workingModel?.SelectedWarehouseDeploymentId,
    workingModel?.SelectedWarehouseId,
    workingModel?.SourceType,
  ]);

  useEffect(() => {
    if (!isPanelOpen || Number(workingModel?.SourceType) !== 2) {
      return;
    }

    if (isPanelLookupLoading || !workingModel?.SelectedWarehouseId || !selectedWarehouse) {
      return;
    }

    if (
      workingModel?.SelectedWarehouseDeploymentId &&
      !warehouseDeploymentOptions.some(
        (option) => String(option.Value) === String(workingModel.SelectedWarehouseDeploymentId)
      )
    ) {
      setWorkingModel((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          SelectedWarehouseDeploymentId: "",
        };
      });
    }
  }, [
    isPanelLookupLoading,
    isPanelOpen,
    selectedWarehouse,
    warehouseDeploymentOptions,
    workingModel?.SelectedWarehouseDeploymentId,
    workingModel?.SelectedWarehouseId,
    workingModel?.SourceType,
  ]);

  useEffect(() => {
    if (!isPanelOpen || !workingModel) {
      return;
    }

    const referenceId = selectedBackingObject ? String(getItemId(selectedBackingObject) ?? "") : "";
    const shouldSuggestFromBackingObject = panelMode === "create";
    const suggestedLabel = !shouldSuggestFromBackingObject
      ? ""
      : workingModel.UiModelKind === MODEL_KIND_VIEW
        ? selectedPrimaryModel?.Label || selectedPrimaryModel?.Key || ""
        : Number(workingModel.SourceType) === 1
        ? getSourceObjectLabel(selectedSourceObject)
        : getEntityObjectLabel(selectedEntityObject);
    const suggestedKey = !shouldSuggestFromBackingObject
      ? ""
      : toMachineKey(
        workingModel.UiModelKind === MODEL_KIND_VIEW
          ? selectedPrimaryModel?.Key || selectedPrimaryModel?.Label || ""
          : Number(workingModel.SourceType) === 1
          ? getSourceObjectKeyBase(selectedSourceObject)
          : getEntityObjectKeyBase(selectedEntityObject)
      );

    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const next = { ...prev };
      let changed = false;

      if (prev.UiModelKind === MODEL_KIND_TABLE && (prev.ReferenceId ?? "") !== referenceId) {
        next.ReferenceId = referenceId;
        changed = true;
      }

      if (!labelTouched && suggestedLabel && prev.Label !== suggestedLabel) {
        next.Label = suggestedLabel;
        changed = true;
      }

      if (!keyTouched && suggestedKey && prev.Key !== suggestedKey) {
        next.Key = suggestedKey;
        changed = true;
      }

      if (
        prev.PrimaryDateColumnName &&
        !primaryDateOptions.some((option) => String(option.Value) === String(prev.PrimaryDateColumnName))
      ) {
        next.PrimaryDateColumnName = "";
        changed = true;
      }

      if (
        referenceId &&
        prev.ColumnsInitializedForReferenceId !== referenceId &&
        Array.isArray(selectedBackingObject?.Fields)
      ) {
        const defaultFieldNames = getDefaultColumnFieldNames(selectedBackingObject.Fields);
        next.Columns = buildColumnsFromFieldNames(defaultFieldNames, selectedBackingObject.Fields, prev.Columns);
        next.ColumnsInitializedForReferenceId = referenceId;
        next.DefaultSorts = filterDefaultSortsToColumns(prev.DefaultSorts, next.Columns);
        changed = true;
      }

      const filteredDefaultSorts = filterDefaultSortsToColumns(next.DefaultSorts ?? prev.DefaultSorts, next.Columns ?? prev.Columns);
      if (JSON.stringify(filteredDefaultSorts) !== JSON.stringify(next.DefaultSorts ?? prev.DefaultSorts ?? [])) {
        next.DefaultSorts = filteredDefaultSorts;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [
    isPanelOpen,
    keyTouched,
    labelTouched,
    panelMode,
    primaryDateOptions,
    selectedEntityObject,
    selectedBackingObject,
    selectedPrimaryModel,
    selectedSourceObject,
    workingModel?.Key,
    workingModel?.Label,
    workingModel?.PrimaryDateColumnName,
    workingModel?.ReferenceId,
    workingModel?.SourceType,
    workingModel?.UiModelKind,
    workingModel,
  ]);

  useEffect(() => {
    if (!isPanelOpen || !workingModel) {
      return;
    }

    setPanelFilterDraft(getInitialFilterDraft(workingModel));
  }, [
    isPanelOpen,
    workingModel?.ReferenceId,
    workingModel?.SourceType,
    workingModel?.UiModelKind,
    workingModel?.ViewPrimaryModelId,
    workingModel?.ViewAuthoringMode,
    workingModel?.SelectedSourceSystemId,
    workingModel?.SelectedSchemaId,
    workingModel?.SelectedWarehouseId,
    workingModel?.SelectedWarehouseDeploymentId,
    workingModel,
  ]);

  useEffect(() => {
    let alive = true;

    const loadSelectedModelSourceName = async () => {
      if (!selectedModel?.item || selectedModel.type !== TABLE_TYPE) {
        setSelectedModelBackingObject(null);
        setSelectedModelSourceName("");
        setSelectedModelContext({
          sourceTypeLabel: "",
          containerLabel: "",
        });
        setSelectedModelWarehouseContext({
          warehouseLabel: "",
          deploymentLabel: "",
        });
        return;
      }

      const referenceId = getModelReferenceId(selectedModel.item);
      if (!referenceId) {
        setSelectedModelBackingObject(null);
        setSelectedModelSourceName("");
        setSelectedModelContext({
          sourceTypeLabel: "",
          containerLabel: "",
        });
        setSelectedModelWarehouseContext({
          warehouseLabel: "",
          deploymentLabel: "",
        });
        return;
      }

      try {
        if (getModelSourceType(selectedModel.item) === 1) {
          const result = await sourceObjectMgrRef.current?.getItem?.(referenceId);
          if (!alive) {
            return;
          }

          const item = result?.Items?.first?.() ?? result?.Items?.[0] ?? null;
          setSelectedModelBackingObject(item);
          const sourceName = getSourceObjectLabel(item) || getModelReferenceId(selectedModel.item) || "";
          let containerLabel = "";
          if (item?.SourceSystemId) {
            const sourceSystemResult = await sourceSystemMgrRef.current?.getItem?.(item.SourceSystemId);
            if (!alive) {
              return;
            }

            const sourceSystem = sourceSystemResult?.Items?.first?.() ?? sourceSystemResult?.Items?.[0] ?? null;
            containerLabel = sourceSystem?.Name || sourceSystem?.DisplayName || sourceSystem?.SystemName || "";
          }

          setSelectedModelSourceName(sourceName);
          setSelectedModelContext({
            sourceTypeLabel: "Source Object",
            containerLabel,
          });
          setSelectedModelWarehouseContext({
            warehouseLabel: "",
            deploymentLabel: "",
          });
          return;
        }

        if (getModelSourceType(selectedModel.item) === 2) {
          const result = await entityObjectMgrRef.current?.getItem?.(referenceId);
          if (!alive) {
            return;
          }

          const item = result?.Items?.first?.() ?? result?.Items?.[0] ?? null;
          setSelectedModelBackingObject(item);
          const sourceName = getEntityObjectLabel(item) || getModelReferenceId(selectedModel.item) || "";
          let containerLabel = "";
          let warehouseLabel = "";
          let deploymentLabel = "";
          if (item?.SchemaId) {
            const schemaResult = await schemaMgrRef.current?.getItem?.(item.SchemaId);
            if (!alive) {
              return;
            }

            const schema = schemaResult?.Items?.first?.() ?? schemaResult?.Items?.[0] ?? null;
            containerLabel = schema?.Name || schema?.DisplayName || "";
          }

          if (getModelWarehouseDeploymentId(selectedModel.item)) {
            const warehouseResult = await warehouseMgrRef.current?.Query({
              model: "x => x.IsActive == true && x.IsDeleted == false && x.SchemaId == schemaId",
              params: { schemaId: item?.SchemaId ?? "" },
            });

            if (!alive) {
              return;
            }

            const warehouseItems = warehouseResult?.Items ?? [];
            const matchedWarehouse = warehouseItems.find((warehouse) =>
              getWarehouseDeployments(warehouse).some(
                (deployment) => String(getItemId(deployment)) === String(getModelWarehouseDeploymentId(selectedModel.item))
              )
            );

            if (matchedWarehouse) {
              warehouseLabel =
                matchedWarehouse?.Name || matchedWarehouse?.DisplayName || matchedWarehouse?.DefaultDBName || "";
              const matchedDeployment = getWarehouseDeployments(matchedWarehouse).find(
                (deployment) => String(getItemId(deployment)) === String(getModelWarehouseDeploymentId(selectedModel.item))
              );
              deploymentLabel =
                matchedDeployment?.DBName || matchedDeployment?.Name || matchedDeployment?.DisplayName || "";
            }
          }

          setSelectedModelSourceName(sourceName);
          setSelectedModelContext({
            sourceTypeLabel: "Entity Object",
            containerLabel,
          });
          setSelectedModelWarehouseContext({
            warehouseLabel,
            deploymentLabel,
          });
          return;
        }

        setSelectedModelSourceName(getModelReferenceId(selectedModel.item) || "");
        setSelectedModelBackingObject(null);
        setSelectedModelContext({
          sourceTypeLabel: "",
          containerLabel: "",
        });
        setSelectedModelWarehouseContext({
          warehouseLabel: "",
          deploymentLabel: "",
        });
      } catch {
        if (alive) {
          setSelectedModelBackingObject(null);
          setSelectedModelSourceName(getModelReferenceId(selectedModel.item) || "");
          setSelectedModelContext({
            sourceTypeLabel: "",
            containerLabel: "",
          });
          setSelectedModelWarehouseContext({
            warehouseLabel: "",
            deploymentLabel: "",
          });
        }
      }
    };

    void loadSelectedModelSourceName();

    return () => {
      alive = false;
    };
  }, [selectedModel]);

  useEffect(() => {
    setPanelActiveTab(DETAILS_TAB_MODEL);
    setEditingColumnFieldName(null);
    setFilterLoadError("");
    setPanelFilterDraft(createDefaultFilterDraft());
  }, [selectedModel?.id]);

  useEffect(() => {
    if (!workingModel) {
      return;
    }

    if (workingModel.UiModelKind !== MODEL_KIND_VIEW && (panelActiveTab === DETAILS_TAB_VIEW || panelActiveTab === DETAILS_TAB_SQL)) {
      setPanelActiveTab(DETAILS_TAB_MODEL);
      return;
    }

    if (
      panelActiveTab === DETAILS_TAB_SQL &&
      !(workingModel.UiModelKind === MODEL_KIND_VIEW && Number(workingModel.ViewAuthoringMode) === VIEW_AUTHORING_MODE_SQL)
    ) {
      setPanelActiveTab(workingModel.UiModelKind === MODEL_KIND_VIEW ? DETAILS_TAB_VIEW : DETAILS_TAB_MODEL);
    }
  }, [panelActiveTab, workingModel]);

  useEffect(() => {
    if (!isPanelOpen || panelWorkflow !== PANEL_WORKFLOW_SEMANTIC) {
      return;
    }

    if (semanticListItems.length === 0) {
      if (selectedSemanticColumnFieldName !== null) {
        setSelectedSemanticColumnFieldName(null);
      }
      if (selectedSemanticMeasureKey !== null) {
        setSelectedSemanticMeasureKey(null);
      }
      return;
    }

    const hasCurrentSelection = semanticListItems.some(
      (item) => item.itemType === "column" && item.key === String(selectedSemanticColumnFieldName)
    );
    const hasCurrentMeasureSelection = semanticListItems.some(
      (item) => item.itemType === "measure" && item.key === String(selectedSemanticMeasureKey)
    );

    if (!hasCurrentSelection && !hasCurrentMeasureSelection) {
      const firstSemanticItem = semanticListItems[0] ?? null;

      if (firstSemanticItem?.itemType === "column") {
        setSelectedSemanticMeasureKey(null);
        setSelectedSemanticColumnFieldName(firstSemanticItem.key);
        return;
      }

      if (firstSemanticItem?.itemType === "measure") {
        setSelectedSemanticColumnFieldName(null);
        setSelectedSemanticMeasureKey(firstSemanticItem.key);
      }
    }
  }, [isPanelOpen, panelWorkflow, selectedSemanticColumnFieldName, selectedSemanticMeasureKey, semanticListItems]);

  const loadWorkspaceRuntimeForPreview = useCallback(async (workspaceId) => {
    const normalizedWorkspaceId = String(workspaceId ?? "").trim();
    if (!normalizedWorkspaceId) {
      throw new Error("A model workspace is required before running Virtual DB preview.");
    }

    const rawLoadResponse = await workspaceMgrRef.current?.RunOperation({
      operationName: LOAD_DATA_MODELS_AND_DATA_OPERATION,
      id: normalizedWorkspaceId,
      isItem: true,
      processorType: getWorkspaceProcessorTypeOverride(normalizedWorkspaceId),
    });
    const loadResponse = unwrapServiceResult(rawLoadResponse);
    if (!loadResponse?.Success) {
      throw buildServiceError(loadResponse, "Unable to load the design Virtual DB.");
    }

    const nextResult = loadResponse?.Item ?? loadResponse?.Items?.[0] ?? null;
    if (!nextResult) {
      throw new Error("Unable to load the design Virtual DB.");
    }

    setRuntimeStatusError("");
    setRuntimeStatusSummary(nextResult);
    return nextResult;
  }, [getWorkspaceProcessorTypeOverride]);

  const runStructuredServerPreviewQuery = useCallback(async (modelItem, tableRequest = {}) => {
    const workspaceId = String(modelItem?.DataModelWorkspaceId ?? currentWorkspaceId ?? "").trim();
    if (!workspaceId) {
      throw new Error("A model workspace is required before running Virtual DB preview.");
    }

    const previewQuery = buildStructuredPreviewTableQuery(modelItem, previewExecutionMode, tableRequest);
    if (!previewQuery) {
      throw new Error(getStructuredPreviewQueryError(previewExecutionMode));
    }

    const useBuiltVirtualDbPreview = isWorkspaceDesignMode
      && designVirtualDbTarget === DESIGN_VIRTUAL_DB_TARGET_BUILT
      && Boolean(artifactStatusSummary?.latestSuccessfulArtifact || artifactStatusSummary?.currentPublishedArtifact);
    const useArtifactBackedPreview = isWorkspacePublishedMode || useBuiltVirtualDbPreview;
    const artifactQueryParams = useBuiltVirtualDbPreview
      ? { artifactSelectionMode: "LatestSuccessful" }
      : isWorkspacePublishedMode
        ? { artifactSelectionMode: "CurrentPublished" }
        : undefined;
    const workspaceProcessorType = getWorkspaceProcessorTypeOverride(workspaceId);

    const executePreviewQuery = async () => {
      const rawResponse = await dataQueryMgrRef.current?.RunOperation({
        operationName: useArtifactBackedPreview
          ? EXECUTE_DATA_MODEL_WORKSPACE_ARTIFACT_QUERY_OPERATION
          : EXECUTE_DATA_QUERY_OPERATION,
        item: previewQuery,
        queryParams: {
          ...(artifactQueryParams ?? {}),
          workspaceId,
        },
        isItem: true,
        processorType: workspaceProcessorType,
      });
      const response = unwrapServiceResult(rawResponse);
      if (!response?.Success) {
        const errorCode = String(response?.Message?.Code ?? "").trim().toUpperCase();
        if (useArtifactBackedPreview && errorCode === DATA_MODEL_WORKSPACE_ARTIFACT_NOT_PUBLISHED_MESSAGE_CODE) {
          throw new Error(
            isWorkspacePublishedMode
              ? "No published Virtual DB is available for this workspace. Build and publish it first."
              : "No built Virtual DB is available for this workspace. Build it first."
          );
        }
        if (useArtifactBackedPreview && errorCode === DATA_MODEL_WORKSPACE_ARTIFACT_MISSING_MESSAGE_CODE) {
          throw new Error(
            isWorkspacePublishedMode
              ? "The published Virtual DB files are missing from storage. Rebuild and publish the Virtual DB again."
              : "The built Virtual DB files are missing from storage. Rebuild the Virtual DB and try again."
          );
        }

        throw buildServiceError(
          response,
          useArtifactBackedPreview
            ? (isWorkspacePublishedMode
              ? "Unable to load the published Virtual DB preview."
              : "Unable to load the built Virtual DB preview.")
            : "Unable to load the design Virtual DB preview."
        );
      }

      return response?.Item ?? response?.Items?.[0] ?? null;
    };

    if (useArtifactBackedPreview) {
      return await executePreviewQuery();
    }

    if (!isRuntimeLoadedStatus(runtimeStatusSummary?.Status)) {
      await loadWorkspaceRuntimeForPreview(workspaceId);
    }

    try {
      return await executePreviewQuery();
    } catch (error) {
      if (!isDataRuntimeUnloadedError(error)) {
        throw error;
      }

      await loadWorkspaceRuntimeForPreview(workspaceId);
      return await executePreviewQuery();
    }
  }, [
    artifactStatusSummary?.currentPublishedArtifact,
    artifactStatusSummary?.latestSuccessfulArtifact,
    currentWorkspaceId,
    designVirtualDbTarget,
    getWorkspaceProcessorTypeOverride,
    isWorkspaceDesignMode,
    isWorkspacePublishedMode,
    loadWorkspaceRuntimeForPreview,
    previewExecutionMode,
    runtimeStatusSummary?.Status,
  ]);

  const loadPreviewForModel = useCallback(async (model) => {
    const modelItem = model?.item;
    const referenceId = getModelReferenceId(modelItem);
    const sourceType = getModelSourceType(modelItem);
    const isRealtimeMode = isRealtimePreviewExecutionMode(previewExecutionMode);
    const isServerMode = isServerPreviewExecutionMode(previewExecutionMode);

    if (isClientPreviewMode || !modelItem) {
      setPreviewRequested(false);
      setPreviewRows([]);
      setPreviewTotalItemCount(null);
      setPreviewError("");
      setPreviewLoading(false);
      setPreviewTableMgr(null);
      setPreviewTableMetadata(null);
      return;
    }

    if (isRealtimeMode && (!referenceId || model?.type !== TABLE_TYPE)) {
      setPreviewRequested(false);
      setPreviewRows([]);
      setPreviewTotalItemCount(null);
      setPreviewError("");
      setPreviewLoading(false);
      setPreviewTableMgr(null);
      setPreviewTableMetadata(null);
      return;
    }

    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;

    const realtimePreviewQuery = isRealtimeMode ? buildPreviewSearchModel(modelItem) : null;
    const serverPreviewTake = 50;
    const serverPreviewQuery = isServerMode
      ? buildStructuredPreviewQuery(modelItem, previewExecutionMode, serverPreviewTake)
      : null;

    if (isServerMode && !serverPreviewQuery) {
      setPreviewRequested(true);
      setPreviewRows([]);
      setPreviewTotalItemCount(null);
      setPreviewError(getStructuredPreviewQueryError(previewExecutionMode));
      setPreviewLoading(false);
      setPreviewTableMgr(null);
      setPreviewTableMetadata(null);
      return;
    }

    setPreviewRequested(true);
    setPreviewRows([]);
    setPreviewTotalItemCount(null);
    setPreviewError("");
    setPreviewLoading(true);

    try {
      if (isServerMode) {
        const queryItem = await runStructuredServerPreviewQuery(modelItem, {
          Skip: 0,
          Take: serverPreviewTake,
        });

        if (previewRequestIdRef.current !== requestId) {
          return;
        }

        const rows = Array.isArray(queryItem?.Data) ? queryItem.Data : [];
        const columns = Array.isArray(queryItem?.Columns) ? queryItem.Columns : [];
        const totalCount = Number(queryItem?.TotalCount ?? rows.length ?? 0);

        setPreviewTableMgr(buildServerPreviewManager(totalCount));
        setPreviewTableMetadata(buildServerPreviewMetadata(columns, rows, true));
        setPreviewRows(rows);
        setPreviewTotalItemCount(totalCount);
        setPreviewError("");
        return;
      }

      if (sourceType === 1) {
        const mgr = new ConvokeSourceObjectManager(
          referenceId,
          coreContext,
          coreState,
          Operations.SourceObjectItem.SearchSourceObject,
          true
        );

        const metadataResult = await mgr.LoadMetadata({
          id: referenceId,
          forceReload: true,
        });

        const metadataItems = metadataResult?.Items ?? mgr?.uiMetadata ?? null;
        const normalizedMetadata = normalizePreviewMetadata(metadataItems, sourceType);
        const take = getPreviewTakeFromMetadata(normalizedMetadata);
        const result = await mgr.search({
          id: referenceId,
          queryParams: { search: true },
          take,
          forceUpdate: true,
          ...(realtimePreviewQuery ? { searchModel: realtimePreviewQuery } : {}),
        });

        if (previewRequestIdRef.current !== requestId) {
          return;
        }

        const rows = Array.isArray(result?.Items) ? result.Items : [];
        setPreviewTableMgr(mgr);
        setPreviewTableMetadata(normalizedMetadata);
        setPreviewRows(rows);
        setPreviewTotalItemCount(result?.TotalItemCount ?? rows.length ?? 0);
        setPreviewError("");
        return;
      }

      if (sourceType === 2) {
        const mgr = new ConvokeEntityObjectManager(
          referenceId,
          coreContext,
          coreState,
          Operations.EntityObjectItem.SearchEntityObject,
          true
        );

        const metadataResult = await mgr.LoadMetadata({
          id: referenceId,
          forceReload: true,
        });

        const metadataItems = metadataResult?.Items ?? mgr?.uiMetadata ?? null;
        const normalizedMetadata = normalizePreviewMetadata(metadataItems, sourceType);
        const take = getPreviewTakeFromMetadata(normalizedMetadata);
        let warehouseId = realtimePreviewQuery?.WarehouseId ?? getModelWarehouseId(modelItem) ?? "";
        const warehouseDeploymentId = realtimePreviewQuery?.WarehouseDeploymentId ?? getModelWarehouseDeploymentId(modelItem) ?? "";

        if (!warehouseId && warehouseDeploymentId) {
          let schemaId = selectedModelBackingObject?.SchemaId ?? "";

          if (!schemaId) {
            const entityResult = await entityObjectMgr?.getItem?.(referenceId);
            const entityItem = getFirstItem(entityResult);
            schemaId = entityItem?.SchemaId ?? "";
          }

          if (schemaId) {
            const warehouseResult = await warehouseMgrRef.current?.Query({
              model: "x => x.IsActive == true && x.IsDeleted == false && x.SchemaId == schemaId",
              params: { schemaId },
            });

            const warehouseItems = warehouseResult?.Items ?? [];
            const owningWarehouse = warehouseItems.find((warehouse) =>
              getWarehouseDeployments(warehouse).some(
                (deployment) => String(getItemId(deployment)) === String(warehouseDeploymentId)
              )
            );

            warehouseId = String(getItemId(owningWarehouse) ?? "");
          }
        }

        if (realtimePreviewQuery && warehouseId && !realtimePreviewQuery.WarehouseId) {
          realtimePreviewQuery.WarehouseId = warehouseId;
        }

        const result = await mgr.search({
          id: referenceId,
          queryParams: {
            search: true,
            ...(warehouseId ? { wId: warehouseId } : {}),
            ...(warehouseDeploymentId ? { wdId: warehouseDeploymentId } : {}),
          },
          take,
          forceUpdate: true,
          ...(realtimePreviewQuery ? { searchModel: realtimePreviewQuery } : {}),
        });

        if (previewRequestIdRef.current !== requestId) {
          return;
        }

        const rows = Array.isArray(result?.Items) ? result.Items : [];
        setPreviewTableMgr(mgr);
        setPreviewTableMetadata(normalizedMetadata);
        setPreviewRows(rows);
        setPreviewTotalItemCount(result?.TotalItemCount ?? rows.length ?? 0);
        setPreviewError("");
        return;
      }

      setPreviewRows([]);
      setPreviewTotalItemCount(null);
      setPreviewError("Unable to load model preview.");
      setPreviewTableMgr(null);
      setPreviewTableMetadata(null);
    } catch (error) {
      console.error("Data model preview load failed:", error);
      if (previewRequestIdRef.current === requestId) {
        setPreviewRows([]);
        setPreviewTotalItemCount(null);
        setPreviewError(String(error?.message ?? "Unable to load model preview."));
        setPreviewTableMgr(null);
        setPreviewTableMetadata(null);
      }
    } finally {
      if (previewRequestIdRef.current === requestId) {
        setPreviewLoading(false);
      }
    }
  }, [coreContext, coreState, entityObjectMgr, isClientPreviewMode, previewExecutionMode, runStructuredServerPreviewQuery, selectedModelBackingObject?.SchemaId]);

  useEffect(() => {
    loadPreviewForModelRef.current = loadPreviewForModel;
  }, [loadPreviewForModel]);

  useEffect(() => {
    if (!autoPreviewOnModeChangeRef.current) {
      return;
    }

    if (!selectedModel) {
      autoPreviewOnModeChangeRef.current = false;
      return;
    }

    if (isClientPreviewMode) {
      autoPreviewOnModeChangeRef.current = false;
      return;
    }

    if (!(isRealtimePreviewAvailable || isServerPreviewAvailable)) {
      return;
    }

    autoPreviewOnModeChangeRef.current = false;
    void loadPreviewForModel(selectedModel);
  }, [
    isClientPreviewMode,
    isRealtimePreviewAvailable,
    isServerPreviewAvailable,
    designVirtualDbTarget,
    loadPreviewForModel,
    previewExecutionMode,
    selectedModel,
    workspaceSurfaceMode,
  ]);

  const handleServerPreviewTableQuery = useCallback(async (queryRequest) => {
    if (!selectedModel?.item || !isServerPreviewMode) {
      return {
        Items: Array.isArray(previewRows) ? previewRows : [],
        TotalItemCount: Number(previewTotalItemCount ?? previewRows?.length ?? 0),
      };
    }

    const nextTake = Math.max(1, Number(queryRequest?.Take ?? queryRequest?.take ?? 50) || 50);
    const nextSkip = Math.max(0, Number(queryRequest?.Skip ?? queryRequest?.skip ?? 0) || 0);
    const nextSortBy = String(queryRequest?.SortBy ?? queryRequest?.sortBy ?? "").trim();

    if (
      nextSkip === 0 &&
      nextTake === 50 &&
      isDefaultServerPreviewSort(nextSortBy) &&
      Array.isArray(previewRows) &&
      previewRows.length > 0 &&
      Number.isFinite(Number(previewTotalItemCount))
    ) {
      return {
        Items: previewRows,
        TotalItemCount: Number(previewTotalItemCount),
      };
    }

    try {
      const queryItem = await runStructuredServerPreviewQuery(selectedModel.item, queryRequest);
      const rows = Array.isArray(queryItem?.Data) ? queryItem.Data : [];
      const columns = Array.isArray(queryItem?.Columns) ? queryItem.Columns : [];
      const totalCount = Number(queryItem?.TotalCount ?? rows.length ?? 0);

      setPreviewRows(rows);
      setPreviewTotalItemCount(totalCount);
      setPreviewTableMgr(buildServerPreviewManager(totalCount));
      setPreviewTableMetadata(buildServerPreviewMetadata(columns, rows, true));
      setPreviewError("");

      return {
        Items: rows,
        TotalItemCount: totalCount,
      };
    } catch (error) {
      console.error("Server preview table query failed:", error);
      const nextError = String(error?.message ?? "Unable to load model preview.");
      setPreviewRows([]);
      setPreviewTotalItemCount(null);
      setPreviewTableMgr(null);
      setPreviewError(nextError);
      throw error;
    }
  }, [isServerPreviewMode, previewRows, previewTotalItemCount, runStructuredServerPreviewQuery, selectedModel]);

  const requestPreview = useCallback(() => {
    if (selectedModel && !isClientPreviewMode && (isRealtimePreviewAvailable || isServerPreviewAvailable)) {
      void loadPreviewForModel(selectedModel);
    }
  }, [isClientPreviewMode, isRealtimePreviewAvailable, isServerPreviewAvailable, loadPreviewForModel, selectedModel]);

  const handleSelectModel = useCallback((modelId) => {
    const targetModel = models.find((model) => String(model?.id) === String(modelId)) ?? null;
    setPreviewRequested(false);
    setPreviewRows([]);
    setPreviewTotalItemCount(null);
    setPreviewError("");
    setPreviewLoading(false);
    setPreviewTableMgr(null);
    setPreviewTableMetadata(null);
    setSelectedModelId(modelId);
    if (!dashboardMode) {
      navigate(buildStandaloneDataModelRoute(currentWorkspaceId, modelId));
    }
    if (
      targetModel &&
      (
        (targetModel.type === TABLE_TYPE && isRealtimePreviewExecutionMode(previewExecutionMode))
        || isServerPreviewExecutionMode(previewExecutionMode)
      )
    ) {
      void loadPreviewForModel(targetModel);
    }
  }, [buildStandaloneDataModelRoute, currentWorkspaceId, dashboardMode, loadPreviewForModel, models, navigate, previewExecutionMode]);

  const handlePreviewExecutionModeChange = useCallback((event) => {
    const nextMode = Number(event?.target?.value ?? DATA_EXECUTION_MODE.VIRTUAL_DB_COLUMNS);
    if (!Number.isFinite(nextMode)) {
      return;
    }

    autoPreviewOnModeChangeRef.current = true;
    setPreviewExecutionMode(nextMode);
    setModelSqlError("");
  }, []);

  const handleSetDesignVirtualDbTarget = useCallback((nextTarget) => {
    const normalizedTarget = String(nextTarget ?? "").trim().toLowerCase();
    if (
      normalizedTarget !== DESIGN_VIRTUAL_DB_TARGET_DESIGN
      && normalizedTarget !== DESIGN_VIRTUAL_DB_TARGET_BUILT
    ) {
      return;
    }

    autoPreviewOnModeChangeRef.current = true;
    setDesignVirtualDbTarget(normalizedTarget);
    setModelSqlError("");
  }, []);

  const closeModelSqlDialog = useCallback(() => {
    setIsModelSqlDialogOpen(false);
    setModelSqlError("");
  }, []);

  const closeRuntimeLoadDialog = useCallback(() => {
    setIsRuntimeLoadDialogOpen(false);
    setRuntimeLoadError("");
  }, []);

  const closeArtifactActionDialog = useCallback(() => {
    if (isArtifactActionLoading) {
      return;
    }

    setIsArtifactActionDialogOpen(false);
    setArtifactActionError("");
  }, [isArtifactActionLoading]);

  const closeRuntimeDropDialog = useCallback(() => {
    if (isRuntimeLoadLoading) {
      return;
    }

    setIsRuntimeDropDialogOpen(false);
    setRuntimeDropConfirmText("");
    setRuntimeDropTarget(RUNTIME_DROP_TARGET_VIRTUAL);
  }, [isRuntimeLoadLoading]);

  const runDashboardRuntimeOperation = useCallback(async (operationName, actionLabel) => {
    if (!currentWorkspaceId) {
      const nextError = "A model workspace is required before running Virtual DB actions.";
      setRuntimeLoadError(nextError);
      setRuntimeStatusError(nextError);
      setIsRuntimeLoadDialogOpen(true);
      setRuntimeLoadResult(null);
      setRuntimeLoadActionLabel(actionLabel);
      return;
    }

    if (
      !isWorkspaceDesignMode &&
      operationName !== GET_DATA_MODELS_AND_DATA_STATUS_OPERATION
    ) {
      const nextError = WORKSPACE_READ_ONLY_MESSAGE;
      setRuntimeLoadError(nextError);
      setRuntimeStatusError(nextError);
      setIsRuntimeLoadDialogOpen(true);
      setRuntimeLoadResult(null);
      setRuntimeLoadActionLabel(actionLabel);
      return;
    }

    setIsRuntimeLoadDialogOpen(true);
    setIsRuntimeLoadLoading(true);
    setRuntimeLoadResult(null);
    setRuntimeLoadError("");
    setRuntimeLoadActionLabel(actionLabel);

    try {
      const rawResponse = await workspaceMgrRef.current?.RunOperation({
        operationName,
        id: currentWorkspaceId,
        isItem: true,
        processorType: getWorkspaceProcessorTypeOverride(currentWorkspaceId),
      });

      const response = unwrapServiceResult(rawResponse);
      if (!response?.Success) {
        const validationMessages = getValidationDisplayMessages(response);
        const nextError =
          validationMessages.length > 0
            ? validationMessages.join(" ")
            : response?.DisplayMessage || response?.Message?.DisplayMessage || `Unable to ${String(actionLabel || "run").toLowerCase()} data models and data.`;
        const normalizedError = nextError === `Unable to ${String(actionLabel || "run").toLowerCase()} data models and data.`
          ? `Unable to ${String(actionLabel || "run").toLowerCase()} the Virtual DB.`
          : nextError;
        setRuntimeLoadError(normalizedError);
        setRuntimeStatusError(normalizedError);
        return;
      }

      const nextResult = response?.Item ?? response?.Items?.[0] ?? null;
      if (!nextResult) {
        const nextError = `No result was returned for ${String(actionLabel || "the operation").toLowerCase()}.`;
        setRuntimeLoadError(nextError);
        setRuntimeStatusError(nextError);
        return;
      }

      setRuntimeStatusError("");
      setRuntimeStatusSummary(nextResult);
      setRuntimeLoadResult(nextResult);
    } catch (error) {
      console.error("Data runtime operation failed:", error);
      const nextError = `Unable to ${String(actionLabel || "run").toLowerCase()} the Virtual DB.`;
      setRuntimeLoadError(nextError);
      setRuntimeStatusError(nextError);
    } finally {
      setIsRuntimeLoadLoading(false);
    }
  }, [currentWorkspaceId, getWorkspaceProcessorTypeOverride, isWorkspaceDesignMode]);

  const runArtifactOperation = useCallback(async (operationName, actionLabel, queryParams = null) => {
    if (!currentWorkspaceId) {
      const nextError = "A model workspace is required before running published Virtual DB actions.";
      setArtifactActionError(nextError);
      setArtifactStatusError(nextError);
      setIsArtifactActionDialogOpen(true);
      setArtifactActionResult(null);
      setArtifactActionLabel(actionLabel);
      return false;
    }

    if (!isWorkspaceDesignMode) {
      const nextError = WORKSPACE_READ_ONLY_MESSAGE;
      setArtifactActionError(nextError);
      setArtifactStatusError(nextError);
      setIsArtifactActionDialogOpen(true);
      setArtifactActionResult(null);
      setArtifactActionLabel(actionLabel);
      return false;
    }

    setIsArtifactActionDialogOpen(true);
    setIsArtifactActionLoading(true);
    setArtifactActionResult(null);
    setArtifactActionError("");
    setArtifactActionLabel(actionLabel);

    try {
      const rawResponse = await workspaceMgrRef.current?.RunOperation({
        operationName,
        id: currentWorkspaceId,
        queryParams: queryParams ?? undefined,
        isItem: true,
        processorType: getWorkspaceProcessorTypeOverride(currentWorkspaceId),
      });

      const response = unwrapServiceResult(rawResponse);
      if (!response?.Success) {
        const validationMessages = getValidationDisplayMessages(response);
        const nextError =
          validationMessages.length > 0
            ? validationMessages.join(" ")
            : response?.DisplayMessage || response?.Message?.DisplayMessage || `Unable to ${String(actionLabel || "run").toLowerCase()} the published Virtual DB.`;
        setArtifactActionError(nextError);
        setArtifactStatusError(nextError);
        return false;
      }

      const workspaceItem = await fetchWorkspaceItemById(currentWorkspaceId);
      if (workspaceItem) {
        setCurrentWorkspace(workspaceItem);
      }

      const nextSummary = await loadArtifactStatusSummary(currentWorkspaceId, workspaceItem);
      setArtifactStatusError("");
      setArtifactStatusSummary(nextSummary);
      setArtifactActionResult({
        ...(nextSummary ?? {}),
        operationArtifact: response?.Item ?? response?.Items?.[0] ?? null,
        statusMessage:
          response?.DisplayMessage
          || response?.Message?.DisplayMessage
          || `Published Virtual DB ${String(actionLabel || "updated").toLowerCase()} successfully.`,
      });
      setToastMessage(
        response?.DisplayMessage
        || response?.Message?.DisplayMessage
        || `Published Virtual DB ${String(actionLabel || "updated").toLowerCase()} successfully.`
      );
      return true;
    } catch (error) {
      console.error("Published Virtual DB operation failed:", error);
      const nextError = `Unable to ${String(actionLabel || "run").toLowerCase()} the published Virtual DB.`;
      setArtifactActionError(nextError);
      setArtifactStatusError(nextError);
      return false;
    } finally {
      setIsArtifactActionLoading(false);
    }
  }, [currentWorkspaceId, fetchWorkspaceItemById, getWorkspaceProcessorTypeOverride, isWorkspaceDesignMode, loadArtifactStatusSummary]);

  const handleOpenModelSqlDialog = useCallback(async () => {
    const modelItem = selectedModel?.item?._rawItem ?? selectedModel?.item ?? null;
    if (!modelItem || isRealtimePreviewExecutionMode(previewExecutionMode)) {
      return;
    }

    const previewQueryState = buildStructuredPreviewQuery(modelItem, previewExecutionMode);
    if (!previewQueryState) {
      setIsModelSqlDialogOpen(true);
      setIsModelSqlLoading(false);
      setModelSqlText("");
      setModelSqlError(getStructuredPreviewQueryError(previewExecutionMode));
      return;
    }

    setIsModelSqlDialogOpen(true);
    setIsModelSqlLoading(true);
    setModelSqlText("");
    setModelSqlError("");

    try {
      const rawResponse = await modelMgrRef.current?.RunOperation({
        operationName: COMPILE_DATA_MODEL_SQL_OPERATION,
        id: modelItem?._id,
        item: deepCopy(modelItem),
        queryParams: {
          executionMode: getPreviewExecutionBaseMode(previewExecutionMode),
          queryState: JSON.stringify(previewQueryState),
        },
        isItem: true,
      });

      const response = unwrapServiceResult(rawResponse);
      if (!response?.Success) {
        const messages = getValidationDisplayMessages(response);
        setModelSqlError(
          messages.length > 0
            ? messages.join(" ")
            : response?.DisplayMessage || response?.Message?.DisplayMessage || "Unable to compile model SQL."
        );
        return;
      }

      const nextSql = typeof response?.Value === "string"
        ? response.Value
        : String(response?.Value?.Sql ?? response?.Value ?? "").trim();

      setModelSqlText(nextSql);
      if (!nextSql) {
        setModelSqlError("No SQL was returned for this model.");
      }
    } catch (error) {
      console.error("Data model SQL compile failed:", error);
      setModelSqlError("Unable to compile model SQL.");
    } finally {
      setIsModelSqlLoading(false);
    }
  }, [previewExecutionMode, selectedModel]);

  const handleOpenRuntimeLoadDialog = useCallback(async () => {
    await runDashboardRuntimeOperation(LOAD_DATA_MODELS_AND_DATA_OPERATION, "Load");
  }, [runDashboardRuntimeOperation]);

  const handleReloadRuntime = useCallback(async () => {
    await runDashboardRuntimeOperation(LOAD_DATA_MODELS_AND_DATA_OPERATION, "Reload");
  }, [runDashboardRuntimeOperation]);

  const handleStatusRuntime = useCallback(async () => {
    await runDashboardRuntimeOperation(GET_DATA_MODELS_AND_DATA_STATUS_OPERATION, "Status");
  }, [runDashboardRuntimeOperation]);

  const handleStatusArtifact = useCallback(async () => {
    if (!currentWorkspaceId) {
      const nextError = "A model workspace is required before checking published Virtual DB status.";
      setArtifactActionError(nextError);
      setArtifactStatusError(nextError);
      setIsArtifactActionDialogOpen(true);
      setArtifactActionResult(null);
      setArtifactActionLabel("Status");
      return;
    }

    setIsArtifactActionDialogOpen(true);
    setIsArtifactActionLoading(true);
    setArtifactActionResult(null);
    setArtifactActionError("");
    setArtifactActionLabel("Status");

    try {
      const workspaceItem = await fetchWorkspaceItemById(currentWorkspaceId);
      if (workspaceItem) {
        setCurrentWorkspace(workspaceItem);
      }

      const nextSummary = await loadArtifactStatusSummary(currentWorkspaceId, workspaceItem);
      setArtifactStatusError("");
      setArtifactStatusSummary(nextSummary);
      setArtifactActionResult({
        ...(nextSummary ?? {}),
        operationArtifact: null,
        statusMessage: "Published Virtual DB status refreshed.",
      });
    } catch (error) {
      console.error("Published Virtual DB status load failed:", error);
      const nextError = "Unable to check published Virtual DB status.";
      setArtifactActionError(nextError);
      setArtifactStatusError(nextError);
    } finally {
      setIsArtifactActionLoading(false);
    }
  }, [currentWorkspaceId, fetchWorkspaceItemById, loadArtifactStatusSummary]);

  const handleBuildArtifact = useCallback(async ({ forceRebuild = false } = {}) => {
    const wasSuccessful = await runArtifactOperation(
      BUILD_DATA_MODEL_WORKSPACE_ARTIFACT_ON_DEMAND_OPERATION,
      forceRebuild ? "Rebuild" : "Build",
      forceRebuild
        ? {
            forceRebuild: true,
            preferExistingRecentVersion: false,
          }
        : undefined
    );

    if (
      wasSuccessful
      && forceRebuild
      && previewRequested
      && selectedModel
      && designVirtualDbTarget === DESIGN_VIRTUAL_DB_TARGET_BUILT
      && isServerPreviewExecutionMode(previewExecutionMode)
    ) {
      previewRequestIdRef.current += 1;
      void loadPreviewForModelRef.current?.(selectedModel);
    }
  }, [
    designVirtualDbTarget,
    previewExecutionMode,
    previewRequested,
    runArtifactOperation,
    selectedModel,
  ]);

  const handlePublishArtifact = useCallback(async ({ switchToPublishedMode = false } = {}) => {
    const wasSuccessful = await runArtifactOperation(PUBLISH_DATA_MODEL_WORKSPACE_ARTIFACT_OPERATION, "Publish");
    if (wasSuccessful && currentWorkspaceId) {
      try {
        const rawRuntimeStatusResponse = await workspaceMgrRef.current?.RunOperation({
          operationName: GET_DATA_MODELS_AND_DATA_STATUS_OPERATION,
          id: currentWorkspaceId,
          isItem: true,
          processorType: getWorkspaceProcessorTypeOverride(currentWorkspaceId),
        });
        const runtimeStatusResponse = unwrapServiceResult(rawRuntimeStatusResponse);
        if (runtimeStatusResponse?.Success) {
          const nextRuntimeStatus = runtimeStatusResponse?.Item ?? runtimeStatusResponse?.Items?.[0] ?? null;
          setRuntimeStatusError("");
          setRuntimeStatusSummary(nextRuntimeStatus);
          setRuntimeLoadResult(nextRuntimeStatus);
        }
      } catch (error) {
        console.error("Virtual DB status refresh after publish failed:", error);
      }
    }

    if (wasSuccessful && switchToPublishedMode) {
      handleSetWorkspaceSurfaceMode(WORKSPACE_SURFACE_MODE_PUBLISHED);
    }

    return wasSuccessful;
  }, [currentWorkspaceId, getWorkspaceProcessorTypeOverride, handleSetWorkspaceSurfaceMode, runArtifactOperation]);

  const openVirtualDbDropDialog = useCallback(() => {
    if (isRuntimeLoadLoading || !currentWorkspaceId) {
      return;
    }

    setRuntimeDropTarget(RUNTIME_DROP_TARGET_VIRTUAL);
    setRuntimeDropConfirmText("");
    setIsRuntimeDropDialogOpen(true);
  }, [currentWorkspaceId, isRuntimeLoadLoading]);

  const openLocalDbDropDialog = useCallback(() => {
    if (isRuntimeLoadLoading) {
      return;
    }

    setRuntimeDropTarget(RUNTIME_DROP_TARGET_LOCAL);
    setRuntimeDropConfirmText("");
    setIsRuntimeDropDialogOpen(true);
  }, [isRuntimeLoadLoading]);

  const handleLocalDbStubAction = useCallback((actionLabel = "Status") => {
    const normalizedActionLabel = String(actionLabel ?? "Status").trim() || "Status";
    setToastMessage(`Local DB ${normalizedActionLabel.toLowerCase()} is not wired yet.`);
  }, []);

  const handleConfirmRuntimeDrop = useCallback(async () => {
    if (String(runtimeDropConfirmText ?? "").trim().toUpperCase() !== "DROP") {
      return;
    }

    setIsRuntimeDropDialogOpen(false);
    setRuntimeDropConfirmText("");

    if (runtimeDropTarget === RUNTIME_DROP_TARGET_LOCAL) {
      handleLocalDbStubAction("Drop");
      setRuntimeDropTarget(RUNTIME_DROP_TARGET_VIRTUAL);
      return;
    }

    await runDashboardRuntimeOperation(DROP_DATA_MODELS_AND_DATA_OPERATION, "Drop");
    setRuntimeDropTarget(RUNTIME_DROP_TARGET_VIRTUAL);
  }, [handleLocalDbStubAction, runDashboardRuntimeOperation, runtimeDropConfirmText, runtimeDropTarget]);

  const openWorkspaceEditDialog = useCallback(() => {
    if (!isWorkspaceDesignMode) {
      return;
    }

    if (!currentWorkspace) {
      return;
    }

    setWorkspaceEditDraft(createWorkspaceEditDraft(currentWorkspace));
    setWorkspaceEditError("");
    setIsWorkspaceEditDialogOpen(true);
  }, [currentWorkspace, isWorkspaceDesignMode]);

  const openPublishArtifactConfirmDialog = useCallback(() => {
    if (!isWorkspaceDesignMode) {
      setToastMessage(WORKSPACE_READ_ONLY_MESSAGE);
      return;
    }

    if (!currentWorkspaceId || isArtifactActionLoading) {
      return;
    }

    setPublishArtifactPublicState(currentWorkspace?.IsPublic === true);
    setPublishArtifactConfirmError("");
    setIsPublishArtifactConfirmDialogOpen(true);
  }, [currentWorkspace?.IsPublic, currentWorkspaceId, isArtifactActionLoading, isWorkspaceDesignMode]);

  const closeWorkspaceEditDialog = useCallback(() => {
    if (isWorkspaceEditSaving) {
      return;
    }

    setIsWorkspaceEditDialogOpen(false);
    setWorkspaceEditDraft(createWorkspaceEditDraft(currentWorkspace));
    setWorkspaceEditError("");
  }, [currentWorkspace, isWorkspaceEditSaving]);

  const closePublishArtifactConfirmDialog = useCallback(() => {
    if (isArtifactActionLoading || isPublishArtifactConfirmSaving) {
      return;
    }

    setIsPublishArtifactConfirmDialogOpen(false);
    setPublishArtifactConfirmError("");
  }, [isArtifactActionLoading, isPublishArtifactConfirmSaving]);

  const openRebuildArtifactConfirmDialog = useCallback(() => {
    if (!currentWorkspaceId || isArtifactActionLoading || !isWorkspaceDesignMode) {
      return;
    }

    setIsRebuildArtifactConfirmDialogOpen(true);
  }, [currentWorkspaceId, isArtifactActionLoading, isWorkspaceDesignMode]);

  const closeRebuildArtifactConfirmDialog = useCallback(() => {
    if (isArtifactActionLoading) {
      return;
    }

    setIsRebuildArtifactConfirmDialogOpen(false);
  }, [isArtifactActionLoading]);

  const handleConfirmRebuildArtifact = useCallback(async () => {
    setIsRebuildArtifactConfirmDialogOpen(false);
    await handleBuildArtifact({ forceRebuild: true });
  }, [handleBuildArtifact]);

  const handleConfirmPublishArtifact = useCallback(async () => {
    if (!currentWorkspace?._id) {
      setPublishArtifactConfirmError("A model workspace is required before publishing.");
      return;
    }

    setIsPublishArtifactConfirmSaving(true);
    setPublishArtifactConfirmError("");

    try {
      if ((currentWorkspace?.IsPublic === true) !== (publishArtifactPublicState === true)) {
        const rawWorkspaceResponse = await workspaceService.save({
          ...currentWorkspace,
          _id: currentWorkspace?._id,
          UniqueId: currentWorkspace?.UniqueId ?? currentWorkspace?._id,
          IsPublic: publishArtifactPublicState === true,
        });

        const workspaceResponse = unwrapServiceResult(rawWorkspaceResponse);
        if (!workspaceResponse?.Success) {
          const validationMessages = getValidationDisplayMessages(workspaceResponse);
          setPublishArtifactConfirmError(
            validationMessages[0]
            || workspaceResponse?.DisplayMessage
            || workspaceResponse?.Message?.DisplayMessage
            || "Unable to update model workspace visibility."
          );
          return;
        }

        const savedWorkspace = workspaceResponse?.Items?.first?.() ?? workspaceResponse?.Items?.[0] ?? workspaceResponse?.Item ?? null;
        if (savedWorkspace) {
          setCurrentWorkspace(savedWorkspace);
        }
      }

      const wasSuccessful = await handlePublishArtifact({ switchToPublishedMode: true });
      if (wasSuccessful) {
        setIsPublishArtifactConfirmDialogOpen(false);
        setPublishArtifactConfirmError("");
      }
    } catch (error) {
      console.error("Model workspace publish failed:", error);
      setPublishArtifactConfirmError("Unable to publish the Virtual DB.");
    } finally {
      setIsPublishArtifactConfirmSaving(false);
    }
  }, [currentWorkspace, handlePublishArtifact, publishArtifactPublicState, workspaceService]);

  const handleSaveWorkspaceEdit = useCallback(async () => {
    if (!isWorkspaceDesignMode) {
      setWorkspaceEditError(WORKSPACE_READ_ONLY_MESSAGE);
      setToastMessage(WORKSPACE_READ_ONLY_MESSAGE);
      return;
    }

    const workspaceName = String(workspaceEditDraft?.Name ?? "").trim();
    const workspaceVersion = normalizeUserControlledWorkspaceVersion(workspaceEditDraft?.Version, currentWorkspace?.Version);
    if (!workspaceName) {
      setWorkspaceEditError("Workspace name is required.");
      return;
    }

    if (!currentWorkspace?._id) {
      setWorkspaceEditError("A model workspace is required.");
      return;
    }

    setIsWorkspaceEditSaving(true);
    setWorkspaceEditError("");

    try {
      const rawResponse = await workspaceService.save({
        ...currentWorkspace,
        _id: currentWorkspace?._id,
        UniqueId: currentWorkspace?.UniqueId ?? currentWorkspace?._id,
        Name: workspaceName,
        Description: String(workspaceEditDraft?.Description ?? "").trim(),
        Version: workspaceVersion,
        ServerExecutionHost: isSystemAdmin
          ? Number(workspaceEditDraft?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL)
          : Number(currentWorkspace?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL),
      });

      const response = unwrapServiceResult(rawResponse);
      if (!response?.Success) {
        const validationMessages = getValidationDisplayMessages(response);
        setWorkspaceEditError(
          validationMessages[0]
          || response?.DisplayMessage
          || response?.Message?.DisplayMessage
          || "Unable to save model workspace."
        );
        return;
      }

      const savedWorkspace = response?.Items?.first?.() ?? response?.Items?.[0] ?? response?.Item ?? null;
      if (savedWorkspace) {
        setCurrentWorkspace(savedWorkspace);
      }
      setIsWorkspaceEditDialogOpen(false);
      setToastMessage("Model workspace updated.");
    } catch (error) {
      console.error("Model workspace update failed:", error);
      setWorkspaceEditError("Unable to save model workspace.");
    } finally {
      setIsWorkspaceEditSaving(false);
    }
  }, [currentWorkspace, isSystemAdmin, isWorkspaceDesignMode, workspaceEditDraft, workspaceService]);

  useEffect(() => {
    let alive = true;

    const loadRuntimeStatusSummary = async () => {
      if (!currentWorkspaceId) {
        if (alive) {
          setRuntimeStatusSummary(null);
          setRuntimeStatusError("");
        }
        return;
      }

      try {
        const rawResponse = await workspaceMgrRef.current?.RunOperation({
          operationName: GET_DATA_MODELS_AND_DATA_STATUS_OPERATION,
          id: currentWorkspaceId,
          isItem: true,
          processorType: getWorkspaceProcessorTypeOverride(currentWorkspaceId),
        });

        if (!alive) {
          return;
        }

        const response = unwrapServiceResult(rawResponse);
        if (!response?.Success) {
          setRuntimeStatusSummary(null);
          const validationMessages = getValidationDisplayMessages(response);
          setRuntimeStatusError(
            validationMessages.length > 0
              ? validationMessages.join(" ")
              : response?.DisplayMessage || response?.Message?.DisplayMessage || "Unable to check virtual DB status."
          );
          return;
        }

        const nextResult = response?.Item ?? response?.Items?.[0] ?? null;
        setRuntimeStatusError("");
        setRuntimeStatusSummary(nextResult);
      } catch (error) {
        console.error("Dashboard runtime status load failed:", error);
        if (alive) {
          setRuntimeStatusSummary(null);
          setRuntimeStatusError("Unable to check virtual DB status.");
        }
      }
    };

    void loadRuntimeStatusSummary();

    return () => {
      alive = false;
    };
  }, [currentWorkspaceId, getWorkspaceProcessorTypeOverride]);

  useEffect(() => {
    if (!isRuntimeLoadDialogOpen) {
      return;
    }

    setRuntimeStatusDialogTab(RUNTIME_STATUS_TAB_OVERVIEW);
  }, [isRuntimeLoadDialogOpen, runtimeLoadActionLabel]);

  useEffect(() => {
    let alive = true;

    const refreshArtifactStatusSummary = async () => {
      if (!currentWorkspaceId) {
        if (alive) {
          setArtifactStatusSummary(null);
          setArtifactStatusError("");
        }
        return;
      }

      try {
        const nextSummary = await loadArtifactStatusSummary(currentWorkspaceId, currentWorkspace);
        if (!alive) {
          return;
        }

        setArtifactStatusSummary(nextSummary);
        setArtifactStatusError("");
      } catch (error) {
        console.error("Published Virtual DB status load failed:", error);
        if (alive) {
          setArtifactStatusSummary(null);
          setArtifactStatusError("Unable to check published Virtual DB status.");
        }
      }
    };

    void refreshArtifactStatusSummary();

    return () => {
      alive = false;
    };
  }, [
    currentWorkspace?.CurrentModelDefinitionVersionId,
    currentWorkspace?.CurrentPublishedArtifactVersionId,
    currentWorkspace?.LatestSuccessfulArtifactVersionId,
    currentWorkspace,
    currentWorkspaceId,
    loadArtifactStatusSummary,
  ]);

  useEffect(() => {
    if (!isArtifactActionDialogOpen) {
      return;
    }

    const hasBuiltVirtualDbCandidate = Boolean(
      artifactStatusSummary?.latestSuccessfulArtifact || artifactStatusSummary?.currentPublishedArtifact
    );
    const isBuiltVirtualDbStatusActive = isWorkspaceDesignMode
      && designVirtualDbTarget === DESIGN_VIRTUAL_DB_TARGET_BUILT
      && hasBuiltVirtualDbCandidate;

    if (isWorkspacePublishedMode) {
      setArtifactStatusDialogTab(ARTIFACT_STATUS_TAB_PUBLISHED);
      return;
    }

    if (isBuiltVirtualDbStatusActive) {
      setArtifactStatusDialogTab(ARTIFACT_STATUS_TAB_BUILT);
      return;
    }

    setArtifactStatusDialogTab(ARTIFACT_STATUS_TAB_OVERVIEW);
  }, [
    artifactActionLabel,
    artifactStatusSummary?.currentPublishedArtifact,
    artifactStatusSummary?.latestSuccessfulArtifact,
    designVirtualDbTarget,
    isArtifactActionDialogOpen,
    isWorkspaceDesignMode,
    isWorkspacePublishedMode,
  ]);

  useEffect(() => {
    if (selectedModel) {
      return;
    }

    setPreviewRequested(false);
    setPreviewRows([]);
    setPreviewTotalItemCount(null);
    setPreviewError("");
    setPreviewLoading(false);
    setPreviewTableMgr(null);
    setPreviewTableMetadata(null);
    previewRequestIdRef.current += 1;
  }, [selectedModel]);

  useEffect(() => {
    let alive = true;

    const loadFilterTabMetadata = async () => {
      if (
        !isPanelOpen ||
        panelActiveTab !== DETAILS_TAB_FILTER ||
        !workingModel ||
        workingModel.UiModelKind !== MODEL_KIND_TABLE ||
        !panelFilterTargetId ||
        objectLevelFilterFields.length > 0
      ) {
        return;
      }

      setIsFilterLoading(true);
      setFilterLoadError("");

      try {
        if (Number(workingModel.SourceType) === 1) {
          if (!sourceObjectMgrRef.current?.uiMetadata) {
            await sourceObjectMgrRef.current?.LoadMetadata?.({ id: panelFilterTargetId, forceReload: true });
          }
          return;
        }

        if (Number(workingModel.SourceType) === 2) {
          if (!entityObjectMgrRef.current?.uiMetadata) {
            await entityObjectMgrRef.current?.LoadMetadata?.({ id: panelFilterTargetId, forceReload: true });
          }
        }
      } catch (error) {
        console.error("Data model filter preview load failed:", error);
        if (alive) {
          setFilterLoadError("Unable to load filter fields.");
        }
      } finally {
        if (alive) {
          setIsFilterLoading(false);
        }
      }
    };

    void loadFilterTabMetadata();

    return () => {
      alive = false;
    };
  }, [
    isPanelOpen,
    panelActiveTab,
    panelFilterTargetId,
    objectLevelFilterFields.length,
    workingModel,
  ]);

  const clearPanelFilter = useCallback(() => {
    setPanelFilterDraft(createDefaultFilterDraft());
    setWorkingModel((prev) => (prev ? { ...prev, FilterDraft: [] } : prev));
  }, []);

  const handlePanelFilterDraftChange = useCallback(
    (nextFilters) => {
      setPanelFilterDraft(nextFilters);
      setWorkingModel((prev) => (prev ? { ...prev, FilterDraft: deepCopy(nextFilters) } : prev));
      setFilterLoadError("");
    },
    []
  );

  const loadEditableWorkingModel = useCallback(async (modelItem) => {
    if (!modelItem) {
      return null;
    }

    if (getModelType(modelItem) === TABLE_TYPE && getModelSourceType(modelItem) === 1 && getModelReferenceId(modelItem)) {
      const sourceObjectResult = await sourceObjectMgrRef.current?.getItem?.(getModelReferenceId(modelItem));
      const sourceObject = getFirstItem(sourceObjectResult);

      return createEditableModel({
        item: modelItem,
        sourceSystemId: sourceObject?.SourceSystemId,
      });
    }

    if (getModelType(modelItem) === TABLE_TYPE && getModelSourceType(modelItem) === 2 && getModelReferenceId(modelItem)) {
      const entityObjectResult = await entityObjectMgrRef.current?.getItem?.(getModelReferenceId(modelItem));
      const entityObject = getFirstItem(entityObjectResult);

      return createEditableModel({
        item: modelItem,
        schemaId: entityObject?.SchemaId,
      });
    }

    return createEditableModel({
      item: modelItem,
    });
  }, []);

  const openCreateModelPanel = useCallback(() => {
    if (!isWorkspaceDesignMode) {
      setToastMessage(WORKSPACE_READ_ONLY_MESSAGE);
      return;
    }

    setPanelError("");
    setPanelMode("create");
    setPanelWorkflow(PANEL_WORKFLOW_RELATION);
    setPanelActiveTab(DETAILS_TAB_MODEL);
    setWorkingModel(createBlankModel());
    setPanelFilterDraft(createDefaultFilterDraft());
    setSavedPanelFilterDraft(createDefaultFilterDraft());
    setEditingColumnFieldName(null);
    setSelectedSemanticColumnFieldName(null);
    setSelectedSemanticMeasureKey(null);
    setPanelValidationEntries([]);
    setLabelTouched(false);
    setKeyTouched(false);
    setIsPanelOpen(true);
  }, [isWorkspaceDesignMode]);

  const openEditModelPanel = async () => {
    if (!selectedModel?.item) {
      return;
    }

    setPanelError("");
    setPanelMode("edit");
    setPanelWorkflow(PANEL_WORKFLOW_RELATION);
    setPanelActiveTab(DETAILS_TAB_MODEL);
    const initialFilterDraft = getInitialFilterDraft(selectedModel.item);
    setPanelFilterDraft(initialFilterDraft);
    setSavedPanelFilterDraft(deepCopy(initialFilterDraft));
    setEditingColumnFieldName(null);
    setSelectedSemanticColumnFieldName(null);
    setSelectedSemanticMeasureKey(null);
    setPanelValidationEntries([]);
    setLabelTouched(false);
    setKeyTouched(false);
    setIsPanelLookupLoading(true);

    try {
      const editableModel = await loadEditableWorkingModel(selectedModel.item);
      setWorkingModel(editableModel);

      setIsPanelOpen(true);
    } catch (error) {
      console.error("Data model edit open failed:", error);
      setPanelError("Unable to load model.");
    } finally {
      setIsPanelLookupLoading(false);
    }
  };

  const openSemanticPanel = async () => {
    if (!selectedModel?.item || selectedModel.type !== TABLE_TYPE) {
      return;
    }

    setPanelError("");
    setPanelMode("edit");
    setPanelWorkflow(PANEL_WORKFLOW_SEMANTIC);
    setPanelActiveTab(DETAILS_TAB_MODEL);
    const initialFilterDraft = getInitialFilterDraft(selectedModel.item);
    setPanelFilterDraft(initialFilterDraft);
    setSavedPanelFilterDraft(deepCopy(initialFilterDraft));
    setEditingColumnFieldName(null);
    setSelectedSemanticColumnFieldName(null);
    setSelectedSemanticMeasureKey(null);
    setPanelValidationEntries([]);
    setLabelTouched(false);
    setKeyTouched(false);
    setIsPanelLookupLoading(true);

    try {
      const editableModel = await loadEditableWorkingModel(selectedModel.item);
      setWorkingModel(editableModel);
      setIsPanelOpen(true);
    } catch (error) {
      console.error("Data model semantic edit open failed:", error);
      setPanelError("Unable to load model semantics.");
    } finally {
      setIsPanelLookupLoading(false);
    }
  };

  const closeModelPanel = async () => {
    if (isSavingModel) {
      return;
    }

    setIsPanelOpen(false);
    setWorkingModel(null);
    setPanelFilterDraft(createDefaultFilterDraft());
    setSavedPanelFilterDraft(createDefaultFilterDraft());
    setPanelActiveTab(DETAILS_TAB_MODEL);
    setEditingColumnFieldName(null);
    setSelectedSemanticColumnFieldName(null);
    setSelectedSemanticMeasureKey(null);
    setPanelValidationEntries([]);
    setPanelWorkflow(PANEL_WORKFLOW_RELATION);
    setPanelError("");
    setLabelTouched(false);
    setKeyTouched(false);
    if (isStandaloneCreateMode) {
      navigate(buildStandaloneWorkspaceRoute(currentWorkspaceId), { replace: true });
    }
  };

  useEffect(() => {
    if (!isWorkspaceDesignMode || !isStandaloneCreateMode || isModelsLoading || isPanelOpen || workingModel) {
      return;
    }

    openCreateModelPanel();
  }, [isModelsLoading, isPanelOpen, isStandaloneCreateMode, isWorkspaceDesignMode, openCreateModelPanel, workingModel]);

  const closeDeleteDialog = () => {
    if (isDeletingModel) {
      return;
    }

    setIsDeleteDialogOpen(false);
    setDeleteError("");
  };

  useEffect(() => {
    if (isWorkspaceDesignMode) {
      return;
    }

    if (!isSavingModel && isPanelOpen && panelMode !== "edit") {
      setIsPanelOpen(false);
      setWorkingModel(null);
      setPanelFilterDraft(createDefaultFilterDraft());
      setSavedPanelFilterDraft(createDefaultFilterDraft());
      setPanelActiveTab(DETAILS_TAB_MODEL);
      setEditingColumnFieldName(null);
      setSelectedSemanticColumnFieldName(null);
      setSelectedSemanticMeasureKey(null);
      setPanelValidationEntries([]);
      setPanelWorkflow(PANEL_WORKFLOW_RELATION);
      setPanelError("");
      setLabelTouched(false);
      setKeyTouched(false);
      if (isStandaloneCreateMode) {
        navigate(buildStandaloneWorkspaceRoute(currentWorkspaceId), { replace: true });
      }
    }

    if (!isSavingModel && isStandaloneCreateMode && !isPanelOpen) {
      navigate(buildStandaloneWorkspaceRoute(currentWorkspaceId), { replace: true });
    }

    if (!isWorkspaceEditSaving && isWorkspaceEditDialogOpen) {
      setIsWorkspaceEditDialogOpen(false);
      setWorkspaceEditDraft(createWorkspaceEditDraft(currentWorkspace));
      setWorkspaceEditError("");
    }

    if (isPublishArtifactConfirmDialogOpen) {
      setIsPublishArtifactConfirmDialogOpen(false);
      setPublishArtifactConfirmError("");
    }

    if (isRebuildArtifactConfirmDialogOpen) {
      setIsRebuildArtifactConfirmDialogOpen(false);
    }

    if (!isDeletingModel && isDeleteDialogOpen) {
      setIsDeleteDialogOpen(false);
      setDeleteError("");
    }

    if (isSemanticDeleteDialogOpen) {
      setIsSemanticDeleteDialogOpen(false);
      setPendingSemanticDeleteKind(SEMANTIC_DELETE_KIND_COLUMN);
      setPendingSemanticDeleteColumnKey(null);
      setPendingSemanticDeleteColumnLabel("");
    }
  }, [
    buildStandaloneWorkspaceRoute,
    currentWorkspace,
    currentWorkspaceId,
    isDeleteDialogOpen,
    isDeletingModel,
    isPanelOpen,
    isPublishArtifactConfirmDialogOpen,
    isPublishArtifactConfirmSaving,
    isRebuildArtifactConfirmDialogOpen,
    isSavingModel,
    isSemanticDeleteDialogOpen,
    isStandaloneCreateMode,
    panelMode,
    isWorkspaceDesignMode,
    isWorkspaceEditDialogOpen,
    isWorkspaceEditSaving,
    navigate,
  ]);

  const buildDataModelSavePayload = useCallback((modelToSave, existingItem, nextFilterDefinition) => {
    if (!modelToSave) {
      return null;
    }

    const isView = modelToSave.UiModelKind === MODEL_KIND_VIEW;
    const isSqlView = isView && Number(modelToSave.ViewAuthoringMode) === VIEW_AUTHORING_MODE_SQL;
    const preserveExistingTableBinding =
      !isView &&
      !!(existingItem?._id || existingItem?.UniqueId) &&
      getModelType(existingItem) === TABLE_TYPE;
    const normalizedColumns = normalizeSemanticColumnsForSave(modelToSave.Columns);
    const normalizedMeasures = normalizeSemanticMeasuresForSave(modelToSave.Measures);
    const normalizedDefaultSorts = normalizeDefaultSortsForSave(modelToSave.DefaultSorts);
    const builderSelectedColumns = buildViewSelectedColumnsForSave(modelToSave, modelToSave.Columns);

    return {
      ...existingItem,
      ...(modelToSave?._id ? { _id: modelToSave._id, UniqueId: modelToSave.UniqueId ?? modelToSave._id } : {}),
      DataModelWorkspaceId: currentWorkspaceId || existingItem?.DataModelWorkspaceId || null,
      ModelKind: isView ? 2 : 1,
      Label: modelToSave.Label,
      Key: modelToSave.Key,
      Description: modelToSave.Description || "",
      BuildSequence: DEFAULT_BUILD_SEQUENCE,
      PermissionPolicy: modelToSave.PermissionPolicy || "",
      TableDefinition: !isView ? {
        ...(existingItem?.TableDefinition ?? {}),
        SourceType: preserveExistingTableBinding
          ? Number(getModelSourceType(existingItem)) || 0
          : Number(modelToSave.SourceType) || 0,
        ReferenceId: preserveExistingTableBinding
          ? getModelReferenceId(existingItem)
          : modelToSave.ReferenceId,
        WarehouseId: preserveExistingTableBinding
          ? (getModelWarehouseId(existingItem) || null)
          : Number(modelToSave.SourceType) === 2 && modelToSave.SelectedWarehouseId
            ? modelToSave.SelectedWarehouseId
            : null,
        WarehouseDeploymentId: preserveExistingTableBinding
          ? (getModelWarehouseDeploymentId(existingItem) || null)
          : Number(modelToSave.SourceType) === 2 && modelToSave.SelectedWarehouseDeploymentId
            ? modelToSave.SelectedWarehouseDeploymentId
            : null,
        ConvokeQueryFilterDefinition: nextFilterDefinition,
      } : null,
      ViewDefinition: isView ? {
        ...(existingItem?.ViewDefinition ?? {}),
        AuthoringMode: Number(modelToSave.ViewAuthoringMode) || VIEW_AUTHORING_MODE_BUILDER,
        PrimaryModelId:
          Number(modelToSave.ViewAuthoringMode) === VIEW_AUTHORING_MODE_BUILDER && modelToSave.ViewPrimaryModelId
            ? modelToSave.ViewPrimaryModelId
            : null,
        DependsOnModelIds:
          Number(modelToSave.ViewAuthoringMode) === VIEW_AUTHORING_MODE_BUILDER && modelToSave.ViewPrimaryModelId
            ? [modelToSave.ViewPrimaryModelId]
            : [],
        BuilderDefinition: isSqlView ? null : {
          ...(existingItem?.ViewDefinition?.BuilderDefinition ?? {}),
          JoinDefinitions: [],
          SelectedColumns: builderSelectedColumns,
          CalculatedColumns: [],
          StaticColumns: [],
        },
        ViewTemplate: isSqlView ? modelToSave.ViewTemplate || "" : null,
        PreAggregateFilterDefinition: nextFilterDefinition,
      } : null,
      SemanticDefinition: {
        ...(existingItem?.SemanticDefinition ?? {}),
        Columns: normalizedColumns,
        Measures: normalizedMeasures,
        Groupings: existingItem?.SemanticDefinition?.Groupings ?? [],
        PrimaryDateColumnName: modelToSave.PrimaryDateColumnName || "",
        DefaultSorts: normalizedDefaultSorts,
      },
      IsActive: true,
      IsDeleted: false,
    };
  }, [currentWorkspaceId]);

  const isExistingWorkingModel = panelMode === "edit" && !!workingModel?._rawItem;
  const isTableSourceBindingLocked = isExistingWorkingModel && workingModel?.UiModelKind === MODEL_KIND_TABLE;
  const sourceBindingLockedMessage = "Source binding is fixed after model creation. Create a new model to use a different source.";

  const handleValidateSemanticExpression = useCallback(async () => {
    const validateTargetType = selectedSemanticMeasure ? "measure" : "column";
    const validateTargetKey = selectedSemanticMeasure
      ? getSemanticMeasureKey(selectedSemanticMeasure)
      : selectedSemanticColumn
        ? getSemanticColumnKey(selectedSemanticColumn)
        : "";
    const validationCandidatePaths = selectedSemanticMeasure
      ? selectedSemanticMeasureExpressionValidationPaths
      : selectedSemanticColumnExpressionValidationPaths;
    const validateTargetIsSupported = selectedSemanticMeasure
      ? Number(selectedSemanticMeasure.MeasureKind ?? MEASURE_KIND_AGGREGATE) === MEASURE_KIND_CALCULATED
      : !!selectedSemanticColumn && Number(selectedSemanticColumn.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) !== SEMANTIC_COLUMN_KIND_STATIC;

    if (!workingModel || !validateTargetKey || !validateTargetIsSupported) {
      return;
    }

    setIsValidatingExpression(true);
    setValidatedSemanticExpressionKey(null);
    setPanelError("");
    setPanelValidationEntries([]);

    try {
      const existingItem = panelMode === "edit" ? workingModel._rawItem ?? {} : {};
      const nextFilterDefinition = buildModelFilterDefinition(panelFilterDraft);
      const payload = buildDataModelSavePayload(workingModel, existingItem, nextFilterDefinition);

      if (!payload) {
        setPanelError("Unable to validate expression.");
        return;
      }

      const rawResponse = await modelMgrRef.current?.RunOperation({
        operationName: VALIDATE_DATA_MODEL_EXPRESSION_OPERATION,
        id: workingModel?._id,
        item: payload,
        queryParams: {
          targetType: validateTargetType,
          targetKey: validateTargetKey,
        },
        isItem: true,
      });

      const response = unwrapServiceResult(rawResponse);
      if (!response?.Success) {
        const validationMessages = getValidationDisplayMessages(response);
        const nextValidationEntries = getValidationEntries(response);
        const hasExpressionPath = nextValidationEntries.some((entry) =>
          validationCandidatePaths.some((candidatePath) =>
            entry?.path === candidatePath || String(entry?.path ?? "").endsWith(candidatePath)
          )
        );
        const failureMessage =
          validationMessages[0]
            || response?.DisplayMessage
            || response?.Message?.DisplayMessage
            || "Expression validation failed.";

        setPanelValidationEntries(
          hasExpressionPath
            ? nextValidationEntries
            : [
              ...nextValidationEntries,
              {
                path: validationCandidatePaths[0] || "SemanticDefinition.Columns.ValueDefinition.Value",
                message: failureMessage,
              },
            ]
        );
        setPanelError(
          validationMessages.length > 0
            ? validationMessages
            : failureMessage
        );
        return;
      }

      const compileResult = response?.Value ?? null;
      const outputDataTypeName = String(compileResult?.OutputDataTypeName ?? "").trim();

      if (outputDataTypeName) {
        setWorkingModel((prev) => {
          if (!prev) {
            return prev;
          }

          if (validateTargetType === "measure") {
            return {
              ...prev,
              Measures: (prev.Measures ?? []).map((measure, index) =>
                getSemanticMeasureKey(measure, `measure-${index}`) === String(validateTargetKey)
                  ? {
                    ...measure,
                    DataTypeName: outputDataTypeName,
                  }
                  : measure
              ),
            };
          }

          return {
            ...prev,
            Columns: (prev.Columns ?? []).map((column, index) =>
              getSemanticColumnKey(column, `column-${index}`) === String(validateTargetKey)
                ? {
                  ...column,
                  DataTypeName: outputDataTypeName,
                }
                : column
            ),
          };
        });
      }

      setPanelValidationEntries([]);
      setPanelError("");
      setValidatedSemanticExpressionKey(validateTargetKey);
    } catch (error) {
      console.error("Data model expression validation failed:", error);
      setPanelValidationEntries([
        {
          path: validationCandidatePaths[0] || "SemanticDefinition.Columns.ValueDefinition.Value",
          message: "Unable to validate expression.",
        },
      ]);
      setPanelError("Unable to validate expression.");
    } finally {
      setIsValidatingExpression(false);
    }
  }, [
    buildDataModelSavePayload,
    panelMode,
    panelFilterDraft,
    selectedSemanticColumn,
    selectedSemanticColumnExpressionValidationPaths,
    selectedSemanticMeasure,
    selectedSemanticMeasureExpressionValidationPaths,
    workingModel,
  ]);

  const handleSaveModel = async ({ keepOpen = false } = {}) => {
    if (!isWorkspaceDesignMode) {
      setPanelError(WORKSPACE_READ_ONLY_MESSAGE);
      setToastMessage(WORKSPACE_READ_ONLY_MESSAGE);
      return;
    }

    if (!workingModel) {
      return;
    }

    setIsSavingModel(true);
    setPanelError("");
    setPanelValidationEntries([]);

    try {
      const existingItem = panelMode === "edit" ? workingModel._rawItem ?? {} : {};
      const nextFilterDefinition = buildModelFilterDefinition(panelFilterDraft);
      const payload = buildDataModelSavePayload(workingModel, existingItem, nextFilterDefinition);
      if (!payload?.DataModelWorkspaceId) {
        setPanelError("A model workspace is required before saving a data model.");
        return;
      }
      const rawResponse = await modelService.save(payload);

      const response = unwrapServiceResult(rawResponse);

      if (!response?.Success) {
        const validationMessages = getValidationDisplayMessages(response);
        const nextValidationEntries = getValidationEntries(response);
        const focusedIssue = focusSemanticValidationIssue(nextValidationEntries);
        const focusedIssueMessage = buildSemanticValidationSummary(focusedIssue);

        setPanelValidationEntries(nextValidationEntries);
        setPanelError(
          focusedIssueMessage
            || (validationMessages.length > 0
              ? validationMessages
              : response?.DisplayMessage || response?.Message?.DisplayMessage || "Unable to save model.")
        );
        return;
      }

      const savedModel = getFirstItem(response);
      setSavedPanelFilterDraft(
        nextFilterDefinition.FilterDraft.length > 0 ? deepCopy(nextFilterDefinition.FilterDraft) : createDefaultFilterDraft()
      );
      setToastMessage(
        panelWorkflow === PANEL_WORKFLOW_SEMANTIC
          ? "Model semantics updated."
          : panelMode === "edit"
            ? "Model updated."
            : "Model saved."
      );
      await loadModels();
      if (currentWorkspaceId) {
        const workspaceItem = await fetchWorkspaceItemById(currentWorkspaceId);
        if (workspaceItem) {
          setCurrentWorkspace(workspaceItem);
        }
      }
      if (dashboardMode && typeof refreshDataModelCount === "function") {
        await refreshDataModelCount();
      }

      if (savedModel?._id) {
        setSelectedModelId(savedModel._id);
        if (!dashboardMode) {
          navigate(buildStandaloneDataModelRoute(currentWorkspaceId, savedModel._id), { replace: true });
        }
        const previewModel = {
          id: savedModel._id,
          type: getModelType(savedModel),
          item: savedModel,
        };

        if (keepOpen) {
          const editableSavedModel = await loadEditableWorkingModel(savedModel);
          setWorkingModel(editableSavedModel);
          setPanelMode("edit");
        } else {
          setIsPanelOpen(false);
          setWorkingModel(null);
        }

        void loadPreviewForModel(previewModel);
      } else if (!keepOpen) {
        setIsPanelOpen(false);
        setWorkingModel(null);
      }
    } catch (error) {
      console.error("Data model save failed:", error);
      setPanelError("Unable to save model.");
    } finally {
      setIsSavingModel(false);
    }
  };

  const handleDeleteModel = async () => {
    if (!isWorkspaceDesignMode) {
      setDeleteError(WORKSPACE_READ_ONLY_MESSAGE);
      setToastMessage(WORKSPACE_READ_ONLY_MESSAGE);
      return;
    }

    if (!selectedModel?.id) {
      return;
    }

    setIsDeletingModel(true);
    setDeleteError("");

    try {
      const rawResponse = await modelService.delete(selectedModel.id);
      const response = unwrapServiceResult(rawResponse);

      if (response && response.Success === false) {
        setDeleteError(response?.DisplayMessage || "Unable to delete model.");
        return;
      }

      setToastMessage("Model deleted.");
      setIsDeleteDialogOpen(false);
      await loadModels();
      if (currentWorkspaceId) {
        const workspaceItem = await fetchWorkspaceItemById(currentWorkspaceId);
        if (workspaceItem) {
          setCurrentWorkspace(workspaceItem);
        }
      }
      if (dashboardMode && typeof refreshDataModelCount === "function") {
        await refreshDataModelCount();
      }
      if (!dashboardMode) {
        navigate(buildStandaloneWorkspaceRoute(currentWorkspaceId), { replace: true });
      }
    } catch (error) {
      console.error("Data model delete failed:", error);
      setDeleteError("Unable to delete model.");
    } finally {
      setIsDeletingModel(false);
    }
  };

  const handleModelKindChange = (value) => {
    setPanelError("");
    setLabelTouched(false);
    setKeyTouched(false);
    setWorkingModel((prev) => ({
      ...(prev ?? createBlankModel()),
      UiModelKind: value,
      SourceType: 0,
      ReferenceId: "",
      ViewAuthoringMode: VIEW_AUTHORING_MODE_BUILDER,
      ViewPrimaryModelId: "",
      ViewTemplate: "",
      Label: "",
      Key: "",
      PrimaryDateColumnName: "",
      DefaultSorts: [],
      FilterDraft: [],
      Columns: [],
      ColumnsInitializedForReferenceId: "",
      SelectedSourceSystemId: "",
      SelectedSourceObjectId: "",
      SelectedSchemaId: "",
      SelectedEntityObjectId: "",
      SelectedWarehouseId: "",
      SelectedWarehouseDeploymentId: "",
    }));
  };

  const handleTableSourceTypeChange = (value) => {
    setPanelError("");
    setLabelTouched(false);
    setKeyTouched(false);
    setWorkingModel((prev) => ({
      ...(prev ?? createBlankModel()),
      SourceType: Number(value) || 0,
      ReferenceId: "",
      Label: "",
      Key: "",
      PrimaryDateColumnName: "",
      DefaultSorts: [],
      FilterDraft: [],
      Columns: [],
      ColumnsInitializedForReferenceId: "",
      SelectedSourceSystemId: "",
      SelectedSourceObjectId: "",
      SelectedSchemaId: "",
      SelectedEntityObjectId: "",
      SelectedWarehouseId: "",
      SelectedWarehouseDeploymentId: "",
    }));
  };

  const handleViewAuthoringModeChange = (value) => {
    setPanelError("");
    setWorkingModel((prev) => ({
      ...(prev ?? createBlankModel()),
      ViewAuthoringMode: Number(value) || VIEW_AUTHORING_MODE_BUILDER,
      ViewTemplate: Number(value) === VIEW_AUTHORING_MODE_SQL ? (prev?.ViewTemplate ?? "") : "",
      FilterDraft: [],
      Columns: [],
      ColumnsInitializedForReferenceId: "",
      DefaultSorts: [],
      ...(Number(value) === VIEW_AUTHORING_MODE_SQL
        ? { ViewPrimaryModelId: "" }
        : {}),
      PrimaryDateColumnName: "",
    }));
  };

  const handleViewPrimaryModelChange = (value) => {
    setPanelError("");
    setLabelTouched(false);
    setKeyTouched(false);
    setWorkingModel((prev) => ({
      ...(prev ?? createBlankModel()),
      ViewPrimaryModelId: value,
      FilterDraft: [],
      Columns: [],
      ColumnsInitializedForReferenceId: "",
      DefaultSorts: [],
      PrimaryDateColumnName: "",
    }));
  };

  const handleLabelChange = (value) => {
    setLabelTouched(true);
    setWorkingModel((prev) => ({ ...(prev ?? {}), Label: value }));
  };

  const handleKeyChange = (value) => {
    setKeyTouched(true);
    setWorkingModel((prev) => ({ ...(prev ?? {}), Key: toMachineKey(value) }));
  };

  const handleSelectedColumnsChange = useCallback((nextFieldNames = []) => {
    setWorkingModel((prev) => {
      if (!prev || !selectedBackingObject) {
        return prev;
      }

      const { derivedColumns } = splitRelationAndDerivedColumns(prev.Columns ?? []);
      const nextRelationColumns = buildColumnsFromFieldNames(nextFieldNames, selectedBackingObject.Fields ?? [], prev.Columns ?? []);
      const nextColumns = [...nextRelationColumns, ...derivedColumns];
      const nextDefaultSorts = filterDefaultSortsToColumns(prev.DefaultSorts, nextColumns);
      const primaryDateColumnName = nextColumns.some(
        (column) => getEffectiveColumnName(column).toLowerCase() === String(prev.PrimaryDateColumnName ?? "").trim().toLowerCase()
      )
        ? prev.PrimaryDateColumnName
        : "";

      return {
        ...prev,
        Columns: nextColumns,
        DefaultSorts: nextDefaultSorts,
        PrimaryDateColumnName: primaryDateColumnName,
        ColumnsInitializedForReferenceId:
          prev.UiModelKind === MODEL_KIND_VIEW ? prev.ViewPrimaryModelId || "" : prev.ReferenceId || "",
      };
    });
  }, [selectedBackingObject]);

  const handleRemoveColumn = useCallback((fieldName) => {
    setEditingColumnFieldName((prev) => (String(prev) === String(fieldName) ? null : prev));
    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const { relationColumns, derivedColumns } = splitRelationAndDerivedColumns(prev.Columns ?? []);
      const nextRelationColumns = relationColumns
        .filter((column) => String(column?.FieldName) !== String(fieldName))
        .map((column, index) => ({
          ...column,
          GridSequence: index,
        }));
      const nextColumns = [...nextRelationColumns, ...derivedColumns];
      const nextDefaultSorts = filterDefaultSortsToColumns(prev.DefaultSorts, nextColumns);
      const nextPrimaryDateColumnName = nextColumns.some(
        (column) => getEffectiveColumnName(column).toLowerCase() === String(prev.PrimaryDateColumnName ?? "").trim().toLowerCase()
      )
        ? prev.PrimaryDateColumnName
        : "";

      return {
        ...prev,
        Columns: nextColumns,
        DefaultSorts: nextDefaultSorts,
        PrimaryDateColumnName: nextPrimaryDateColumnName,
        ColumnsInitializedForReferenceId:
          prev.UiModelKind === MODEL_KIND_VIEW ? prev.ViewPrimaryModelId || "" : prev.ReferenceId || "",
      };
    });
  }, []);

  const handleSemanticColumnFieldChange = useCallback((fieldName, value, currentValueObject = null) => {
    if (fieldName === "ValueDefinitionValue") {
      setValidatedSemanticExpressionKey(null);
      clearPanelValidationForPaths(selectedSemanticColumnExpressionValidationPaths);
    }
    if (fieldName === "ColumnName" && selectedSemanticColumn) {
      clearPanelValidationForPaths(getSemanticColumnValidationPaths(selectedSemanticColumn, "ColumnName"));
    }
    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const previousColumn = (prev.Columns ?? []).find(
        (column, index) => getSemanticColumnKey(column, `column-${index}`) === String(selectedSemanticColumnFieldName)
      );
      const previousColumnName = getEffectiveColumnName(previousColumn);

      const nextColumns = (prev.Columns ?? []).map((column, index) =>
        getSemanticColumnKey(column, `column-${index}`) === String(selectedSemanticColumnFieldName)
          ? (() => {
              const resolvedValue = getSelectControlValue(value, currentValueObject);
              const nextColumn = {
                ...column,
                [fieldName]: resolvedValue,
                GridSequence: index,
              };

              if (fieldName === "ColumnKind") {
                const nextColumnKind = Number(getSelectControlValue(value, currentValueObject));
                nextColumn.ColumnKind = Number.isFinite(nextColumnKind) && nextColumnKind > 0
                  ? nextColumnKind
                  : Number(column?.ColumnKind ?? SEMANTIC_COLUMN_KIND_CALCULATED);
                nextColumn.ValueKind = getSemanticColumnValueKind(nextColumn.ColumnKind);

                if (nextColumn.ColumnKind === SEMANTIC_COLUMN_KIND_STATIC && !nextColumn.DataTypeName) {
                  nextColumn.DataTypeName = "System.String";
                }
              }

              return nextColumn;
            })()
          : column
      );

      const updatedColumn = nextColumns.find(
        (column, index) => getSemanticColumnKey(column, `column-${index}`) === String(selectedSemanticColumnFieldName)
      );
      const nextColumnName = getEffectiveColumnName(updatedColumn);

      const nextModel = {
        ...prev,
        Columns: nextColumns,
      };

      if (fieldName === "ColumnName" && previousColumnName && nextColumnName) {
        if (String(prev.PrimaryDateColumnName ?? "").trim().toLowerCase() === previousColumnName.toLowerCase()) {
          nextModel.PrimaryDateColumnName = nextColumnName;
        }

        nextModel.DefaultSorts = renameColumnDefaultSortTarget(prev.DefaultSorts, previousColumnName, nextColumnName);
      }

      return {
        ...nextModel,
        DefaultSorts: nextModel.DefaultSorts ?? prev.DefaultSorts,
      };
    });
  }, [clearPanelValidationForPaths, selectedSemanticColumn, selectedSemanticColumnExpressionValidationPaths, selectedSemanticColumnFieldName]);

  const handleSemanticMeasureFieldChange = useCallback((fieldName, value) => {
    if (fieldName === "ExpressionValue") {
      setValidatedSemanticExpressionKey(null);
      clearPanelValidationForPaths(selectedSemanticMeasureExpressionValidationPaths);
    }
    if (fieldName === "MeasureName" && selectedSemanticMeasure) {
      clearPanelValidationForPaths(getSemanticMeasureValidationPaths(selectedSemanticMeasure, "MeasureName"));
    }

    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const previousMeasure = (prev.Measures ?? []).find(
        (measure, index) => getSemanticMeasureKey(measure, `measure-${index}`) === String(selectedSemanticMeasureKey)
      );
      const previousMeasureName = getEffectiveMeasureName(previousMeasure);
      const previousAutoGeneratedNames = buildAutoGeneratedMeasureNames(previousMeasure, prev.Columns ?? []);

      const nextMeasures = (prev.Measures ?? []).map((measure, index) =>
        getSemanticMeasureKey(measure, `measure-${index}`) === String(selectedSemanticMeasureKey)
          ? (() => {
              const currentColumns = Array.isArray(prev.Columns) ? prev.Columns : [];
              const nextMeasure = {
                ...measure,
                [fieldName]: value,
                SemanticType: "measure",
                SortOrder: Number.isFinite(Number(measure?.SortOrder)) ? Number(measure.SortOrder) : index,
              };

              if (fieldName === "MeasureKind") {
                nextMeasure.MeasureKind = Number(value);
                if (Number(value) === MEASURE_KIND_CALCULATED) {
                  nextMeasure.ExpressionValue = nextMeasure.ExpressionValue || "";
                } else {
                  const compatibleColumns = getCompatibleMeasureTargetColumns(
                    currentColumns,
                    Number(nextMeasure.AggregationKind ?? AGGREGATION_KIND_SUM)
                  );
                  nextMeasure.TargetName = nextMeasure.TargetName || getEffectiveColumnName(compatibleColumns[0] ?? currentColumns[0]);
                }
              }

              if (fieldName === "AggregationKind") {
                nextMeasure.AggregationKind = Number(value);
                const compatibleColumns = getCompatibleMeasureTargetColumns(currentColumns, nextMeasure.AggregationKind);
                const hasCurrentTarget = compatibleColumns.some(
                  (column) =>
                    String(getEffectiveColumnName(column)).trim().toLowerCase()
                    === String(nextMeasure.TargetName ?? "").trim().toLowerCase()
                );
                nextMeasure.TargetName = hasCurrentTarget ? nextMeasure.TargetName : getEffectiveColumnName(compatibleColumns[0]) || "";
              }

              if (
                Number(nextMeasure.MeasureKind ?? MEASURE_KIND_AGGREGATE) === MEASURE_KIND_AGGREGATE
                && ["AggregationKind", "TargetName", "MeasureKind"].includes(String(fieldName))
              ) {
                const nextAutoGeneratedNames = buildAutoGeneratedMeasureNames(nextMeasure, currentColumns);
                const currentMeasureName = String(nextMeasure.MeasureName ?? "").trim().toLowerCase();
                const currentDisplayName = String(nextMeasure.DisplayName ?? "").trim().toLowerCase();
                const previousGeneratedMeasureName = String(previousAutoGeneratedNames.measureName ?? "").trim().toLowerCase();
                const previousGeneratedDisplayName = String(previousAutoGeneratedNames.displayName ?? "").trim().toLowerCase();

                if (!currentMeasureName || currentMeasureName === previousGeneratedMeasureName) {
                  nextMeasure.MeasureName = nextAutoGeneratedNames.measureName || nextMeasure.MeasureName;
                }

                if (!currentDisplayName || currentDisplayName === previousGeneratedDisplayName) {
                  nextMeasure.DisplayName = nextAutoGeneratedNames.displayName || nextMeasure.DisplayName;
                }
              }

              return nextMeasure;
            })()
          : measure
      );

      const updatedMeasure = nextMeasures.find(
        (measure, index) => getSemanticMeasureKey(measure, `measure-${index}`) === String(selectedSemanticMeasureKey)
      );
      const nextMeasureName = getEffectiveMeasureName(updatedMeasure);
      const nextModel = {
        ...prev,
        Measures: nextMeasures,
      };

      if (fieldName === "MeasureName" && previousMeasureName && nextMeasureName) {
        nextModel.DefaultSorts = renameMeasureDefaultSortTarget(prev.DefaultSorts, previousMeasureName, nextMeasureName);
      }

      return {
        ...nextModel,
        DefaultSorts: nextModel.DefaultSorts ?? prev.DefaultSorts,
      };
    });
  }, [clearPanelValidationForPaths, selectedSemanticMeasure, selectedSemanticMeasureExpressionValidationPaths, selectedSemanticMeasureKey]);

  const handleSemanticMeasureDefaultSortChange = useCallback((value) => {
    setWorkingModel((prev) => {
      if (!prev || !selectedSemanticMeasure) {
        return prev;
      }

      return {
        ...prev,
        DefaultSorts: setDefaultSortDirection(
          prev.DefaultSorts,
          2,
          getEffectiveMeasureName(selectedSemanticMeasure),
          Number(value)
        ),
      };
    });
  }, [selectedSemanticMeasure]);

  const pushSemanticExpressionUndo = useCallback((columnKey, currentValue) => {
    const normalizedKey = String(columnKey ?? "");
    if (!normalizedKey) {
      return;
    }

    setExpressionUndoByColumnKey((prev) => {
      const nextStack = [...(prev?.[normalizedKey] ?? []), String(currentValue ?? "")];
      if (nextStack.length > 25) {
        nextStack.shift();
      }

      return {
        ...prev,
        [normalizedKey]: nextStack,
      };
    });
  }, []);

  const clearSemanticExpressionUndo = useCallback((columnKey) => {
    const normalizedKey = String(columnKey ?? "");
    if (!normalizedKey) {
      return;
    }

    setExpressionUndoByColumnKey((prev) => {
      if (!prev?.[normalizedKey]) {
        return prev;
      }

      return {
        ...prev,
        [normalizedKey]: [],
      };
    });
  }, []);

  const handleSemanticExpressionInputChange = useCallback((nextValue) => {
    clearSemanticExpressionUndo(selectedSemanticExpressionOwnerKey);

    if (selectedSemanticMeasure) {
      handleSemanticMeasureFieldChange("ExpressionValue", nextValue);
      return;
    }

    handleSemanticColumnFieldChange("ValueDefinitionValue", nextValue);
  }, [
    clearSemanticExpressionUndo,
    handleSemanticColumnFieldChange,
    handleSemanticMeasureFieldChange,
    selectedSemanticExpressionOwnerKey,
    selectedSemanticMeasure,
  ]);

  const handleInsertSemanticExpressionText = useCallback((insertText) => {
    const currentExpressionValue = normalizeExpressionBracketPairs(selectedSemanticMeasure
      ? String(selectedSemanticMeasure?.ExpressionValue ?? "")
      : selectedSemanticColumn
        ? String(selectedSemanticColumn?.ValueDefinitionValue ?? "")
        : "");

    if (!selectedSemanticColumn && !selectedSemanticMeasure) {
      return;
    }

    pushSemanticExpressionUndo(selectedSemanticExpressionOwnerKey, currentExpressionValue);
    const input = semanticExpressionInputRef.current;
    const selectionStart = Number.isFinite(Number(input?.selectionStart)) ? Number(input.selectionStart) : currentExpressionValue.length;
    const selectionEnd = Number.isFinite(Number(input?.selectionEnd)) ? Number(input.selectionEnd) : currentExpressionValue.length;
    const rawNextValue = `${currentExpressionValue.slice(0, selectionStart)}${insertText}${currentExpressionValue.slice(selectionEnd)}`;
    const nextValue = normalizeExpressionBracketPairs(rawNextValue);
    const nextCursorPosition = Math.max(0, selectionStart + insertText.length - (rawNextValue.length - nextValue.length));

    if (selectedSemanticMeasure) {
      handleSemanticMeasureFieldChange("ExpressionValue", nextValue);
    } else {
      handleSemanticColumnFieldChange("ValueDefinitionValue", nextValue);
    }

    requestAnimationFrame(() => {
      const nextInput = semanticExpressionInputRef.current;
      if (!nextInput || typeof nextInput.focus !== "function") {
        return;
      }

      nextInput.focus();
      if (typeof nextInput.setSelectionRange === "function") {
        nextInput.setSelectionRange(nextCursorPosition, nextCursorPosition);
      }
    });
  }, [
    handleSemanticColumnFieldChange,
    handleSemanticMeasureFieldChange,
    pushSemanticExpressionUndo,
    selectedSemanticColumn,
    selectedSemanticExpressionOwnerKey,
    selectedSemanticMeasure,
  ]);

  const handleInsertSemanticFieldReference = useCallback((insertText) => {
    const currentExpressionValue = normalizeExpressionBracketPairs(selectedSemanticMeasure
      ? String(selectedSemanticMeasure?.ExpressionValue ?? "")
      : selectedSemanticColumn
        ? String(selectedSemanticColumn?.ValueDefinitionValue ?? "")
        : "");

    if (!selectedSemanticColumn && !selectedSemanticMeasure) {
      return;
    }

    const placeholderMatch = EXPRESSION_FIELD_PLACEHOLDER_REGEX.exec(currentExpressionValue);

    if (!placeholderMatch) {
      handleInsertSemanticExpressionText(insertText);
      return;
    }

    pushSemanticExpressionUndo(selectedSemanticExpressionOwnerKey, currentExpressionValue);
    const rawNextValue = `${currentExpressionValue.slice(0, placeholderMatch.index)}${insertText}${currentExpressionValue.slice(placeholderMatch.index + placeholderMatch[0].length)}`;
    const nextValue = normalizeExpressionBracketPairs(rawNextValue);
    const nextCursorPosition = nextValue.length;

    if (selectedSemanticMeasure) {
      handleSemanticMeasureFieldChange("ExpressionValue", nextValue);
    } else {
      handleSemanticColumnFieldChange("ValueDefinitionValue", nextValue);
    }

    requestAnimationFrame(() => {
      const nextInput = semanticExpressionInputRef.current;
      if (!nextInput || typeof nextInput.focus !== "function") {
        return;
      }

      nextInput.focus();
      if (typeof nextInput.setSelectionRange === "function") {
        nextInput.setSelectionRange(nextCursorPosition, nextCursorPosition);
      }
    });
  }, [
    handleInsertSemanticExpressionText,
    handleSemanticColumnFieldChange,
    handleSemanticMeasureFieldChange,
    pushSemanticExpressionUndo,
    selectedSemanticColumn,
    selectedSemanticExpressionOwnerKey,
    selectedSemanticMeasure,
  ]);

  const handleUndoSemanticExpression = useCallback(() => {
    const normalizedKey = String(selectedSemanticExpressionOwnerKey ?? "");
    if (!normalizedKey) {
      return;
    }

    const undoStack = expressionUndoByColumnKey?.[normalizedKey] ?? [];
    if (undoStack.length === 0) {
      return;
    }

    const previousValue = undoStack[undoStack.length - 1];
    setExpressionUndoByColumnKey((prev) => ({
      ...prev,
      [normalizedKey]: (prev?.[normalizedKey] ?? []).slice(0, -1),
    }));
    if (selectedSemanticMeasure) {
      handleSemanticMeasureFieldChange("ExpressionValue", previousValue);
    } else {
      handleSemanticColumnFieldChange("ValueDefinitionValue", previousValue);
    }

    requestAnimationFrame(() => {
      const nextInput = semanticExpressionInputRef.current;
      if (!nextInput || typeof nextInput.focus !== "function") {
        return;
      }

      nextInput.focus();
      if (typeof nextInput.setSelectionRange === "function") {
        const cursorPosition = String(previousValue ?? "").length;
        nextInput.setSelectionRange(cursorPosition, cursorPosition);
      }
    });
  }, [
    expressionUndoByColumnKey,
    handleSemanticColumnFieldChange,
    handleSemanticMeasureFieldChange,
    selectedSemanticExpressionOwnerKey,
    selectedSemanticMeasure,
  ]);

  const handleClearSemanticExpression = useCallback(() => {
    const currentExpressionValue = selectedSemanticMeasure
      ? String(selectedSemanticMeasure?.ExpressionValue ?? "")
      : selectedSemanticColumn
        ? String(selectedSemanticColumn?.ValueDefinitionValue ?? "")
        : "";

    if ((!selectedSemanticColumn && !selectedSemanticMeasure) || !currentExpressionValue) {
      return;
    }

    pushSemanticExpressionUndo(selectedSemanticExpressionOwnerKey, currentExpressionValue);

    if (selectedSemanticMeasure) {
      handleSemanticMeasureFieldChange("ExpressionValue", "");
    } else {
      handleSemanticColumnFieldChange("ValueDefinitionValue", "");
    }

    requestAnimationFrame(() => {
      const nextInput = semanticExpressionInputRef.current;
      if (!nextInput || typeof nextInput.focus !== "function") {
        return;
      }

      nextInput.focus();
      if (typeof nextInput.setSelectionRange === "function") {
        nextInput.setSelectionRange(0, 0);
      }
    });
  }, [
    handleSemanticColumnFieldChange,
    handleSemanticMeasureFieldChange,
    pushSemanticExpressionUndo,
    selectedSemanticColumn,
    selectedSemanticExpressionOwnerKey,
    selectedSemanticMeasure,
  ]);

  const handleStartAddSemanticColumn = useCallback(() => {
    if (isWorkspacePanelReadOnly) {
      return;
    }

    const createdColumn = createSemanticColumnDraft(workingModel?.Columns ?? [], SEMANTIC_COLUMN_KIND_CALCULATED);
    const createdColumnKey = getSemanticColumnKey(createdColumn, "column-0");

    setPanelError("");
    setPanelValidationEntries([]);
    setSemanticEditorTab(SEMANTIC_EDITOR_TAB_DETAILS);
    setSelectedSemanticMeasureKey(null);
    setSelectedSemanticColumnFieldName(createdColumnKey);
    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const nextColumns = [
        {
          ...createdColumn,
          GridSequence: 0,
          DisplaySequence: nextTopSemanticSortOrder + 1,
          SortOrder: nextTopSemanticSortOrder,
        },
        ...(prev.Columns ?? []),
      ];
      const nextSemanticItems = buildSemanticListItemsFromCollections(nextColumns, prev.Measures ?? []);
      const { columns: orderedColumns, measures: orderedMeasures } = resequenceSemanticCollectionsFromItems(nextSemanticItems);

      return {
        ...prev,
        Columns: orderedColumns,
        Measures: orderedMeasures,
      };
    });
    requestAnimationFrame(() => {
      semanticListRef.current?.scrollTo({ top: 0, behavior: "auto" });
    });
  }, [isWorkspacePanelReadOnly, nextTopSemanticSortOrder, workingModel?.Columns]);

  const handleStartAddSemanticMeasure = useCallback(() => {
    if (isWorkspacePanelReadOnly) {
      return;
    }

    const createdMeasure = createSemanticMeasureDraft(workingModel?.Measures ?? [], workingModel?.Columns ?? []);
    const createdMeasureKey = getSemanticMeasureKey(createdMeasure, "measure-0");

    setPanelError("");
    setPanelValidationEntries([]);
    setSemanticEditorTab(SEMANTIC_EDITOR_TAB_DETAILS);
    setSelectedSemanticColumnFieldName(null);
    setSelectedSemanticMeasureKey(createdMeasureKey);
    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const nextMeasures = [
        {
          ...createdMeasure,
          SortOrder: nextTopSemanticSortOrder,
        },
        ...(prev.Measures ?? []),
      ];
      const nextSemanticItems = buildSemanticListItemsFromCollections(prev.Columns ?? [], nextMeasures);
      const { columns: orderedColumns, measures: orderedMeasures } = resequenceSemanticCollectionsFromItems(nextSemanticItems);

      return {
        ...prev,
        Columns: orderedColumns,
        Measures: orderedMeasures,
      };
    });
    requestAnimationFrame(() => {
      semanticListRef.current?.scrollTo({ top: 0, behavior: "auto" });
    });
  }, [isWorkspacePanelReadOnly, nextTopSemanticSortOrder, workingModel?.Columns, workingModel?.Measures]);

  const handleSelectSemanticColumn = useCallback((columnKey) => {
    setSemanticEditorTab(SEMANTIC_EDITOR_TAB_DETAILS);
    setSelectedSemanticColumnFieldName(columnKey);
    setSelectedSemanticMeasureKey(null);
  }, []);

  const handleSelectSemanticMeasure = useCallback((measureKey) => {
    setSemanticEditorTab(SEMANTIC_EDITOR_TAB_DETAILS);
    setSelectedSemanticMeasureKey(measureKey);
    setSelectedSemanticColumnFieldName(null);
  }, []);

  const handleRequestRemoveSemanticColumn = useCallback((columnKey) => {
    if (isWorkspacePanelReadOnly) {
      return;
    }

    const targetColumn = (workingModel?.Columns ?? []).find(
      (column, index) => getSemanticColumnKey(column, `column-${index}`) === String(columnKey)
    );

    setPendingSemanticDeleteKind(SEMANTIC_DELETE_KIND_COLUMN);
    setPendingSemanticDeleteColumnKey(columnKey);
    setPendingSemanticDeleteColumnLabel(
      targetColumn?.DisplayName || getEffectiveColumnName(targetColumn) || "this column"
    );
    setIsSemanticDeleteDialogOpen(true);
  }, [isWorkspacePanelReadOnly, workingModel?.Columns]);

  const handleRequestRemoveSemanticMeasure = useCallback((measureKey) => {
    if (isWorkspacePanelReadOnly) {
      return;
    }

    const targetMeasure = (workingModel?.Measures ?? []).find(
      (measure, index) => getSemanticMeasureKey(measure, `measure-${index}`) === String(measureKey)
    );

    setPendingSemanticDeleteKind(SEMANTIC_DELETE_KIND_MEASURE);
    setPendingSemanticDeleteColumnKey(measureKey);
    setPendingSemanticDeleteColumnLabel(
      targetMeasure?.DisplayName || getEffectiveMeasureName(targetMeasure) || "this measure"
    );
    setIsSemanticDeleteDialogOpen(true);
  }, [isWorkspacePanelReadOnly, workingModel?.Measures]);

  const closeSemanticDeleteDialog = useCallback(() => {
    setIsSemanticDeleteDialogOpen(false);
    setPendingSemanticDeleteKind(SEMANTIC_DELETE_KIND_COLUMN);
    setPendingSemanticDeleteColumnKey(null);
    setPendingSemanticDeleteColumnLabel("");
  }, []);

  const handleRemoveSemanticColumn = useCallback((columnKey) => {
    let nextSelectedColumnKey = null;
    let nextSelectedMeasureKey = null;

    setPanelError("");
    setPanelValidationEntries([]);
    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const currentColumns = Array.isArray(prev.Columns) ? prev.Columns : [];
      const filteredColumns = currentColumns.filter(
        (column, index) => getSemanticColumnKey(column, `column-${index}`) !== String(columnKey)
      );
      const nextSemanticItems = buildSemanticListItemsFromCollections(filteredColumns, prev.Measures ?? []);
      const { columns: nextColumns, measures: nextMeasures } = resequenceSemanticCollectionsFromItems(nextSemanticItems);
      const firstRemainingItem = nextSemanticItems[0] ?? null;

      if (firstRemainingItem?.itemType === "column") {
        nextSelectedColumnKey = firstRemainingItem.key;
      }
      if (firstRemainingItem?.itemType === "measure") {
        nextSelectedMeasureKey = firstRemainingItem.key;
      }

      const nextDefaultSorts = filterDefaultSortsToColumns(prev.DefaultSorts, filteredColumns);
      const nextPrimaryDateColumnName = filteredColumns.some(
        (column) => getEffectiveColumnName(column).toLowerCase() === String(prev.PrimaryDateColumnName ?? "").trim().toLowerCase()
      )
        ? prev.PrimaryDateColumnName
        : "";

      return {
        ...prev,
        Columns: nextColumns,
        Measures: nextMeasures,
        DefaultSorts: nextDefaultSorts,
        PrimaryDateColumnName: nextPrimaryDateColumnName,
      };
    });

    setSelectedSemanticColumnFieldName((current) => (String(current) === String(columnKey) ? nextSelectedColumnKey : current));
    if (String(selectedSemanticColumnFieldName) === String(columnKey)) {
      setSelectedSemanticMeasureKey(nextSelectedMeasureKey);
    }
  }, [selectedSemanticColumnFieldName]);

  const handleRemoveSemanticMeasure = useCallback((measureKey) => {
    let nextSelectedMeasureKey = null;
    let nextSelectedColumnKey = null;

    setPanelError("");
    setPanelValidationEntries([]);
    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const currentMeasures = Array.isArray(prev.Measures) ? prev.Measures : [];
      const filteredMeasures = currentMeasures.filter(
        (measure, index) => getSemanticMeasureKey(measure, `measure-${index}`) !== String(measureKey)
      );
      const nextSemanticItems = buildSemanticListItemsFromCollections(prev.Columns ?? [], filteredMeasures);
      const { columns: nextColumns, measures: nextMeasures } = resequenceSemanticCollectionsFromItems(nextSemanticItems);
      const firstRemainingItem = nextSemanticItems[0] ?? null;

      if (firstRemainingItem?.itemType === "measure") {
        nextSelectedMeasureKey = firstRemainingItem.key;
      }
      if (firstRemainingItem?.itemType === "column") {
        nextSelectedColumnKey = firstRemainingItem.key;
      }

      const deletedMeasure = currentMeasures.find(
        (measure, index) => getSemanticMeasureKey(measure, `measure-${index}`) === String(measureKey)
      );
      const nextDefaultSorts = deletedMeasure
        ? normalizeDefaultSortsForSave(prev.DefaultSorts).filter(
            (sort) =>
              !(Number(sort?.TargetKind ?? 1) === 2 &&
                String(sort?.TargetName ?? "").trim().toLowerCase() === getEffectiveMeasureName(deletedMeasure).toLowerCase())
          )
        : prev.DefaultSorts;

      return {
        ...prev,
        Columns: nextColumns,
        Measures: nextMeasures,
        DefaultSorts: nextDefaultSorts,
      };
    });

    setSelectedSemanticMeasureKey((current) => (String(current) === String(measureKey) ? nextSelectedMeasureKey : current));
    if (String(selectedSemanticMeasureKey) === String(measureKey)) {
      setSelectedSemanticColumnFieldName(nextSelectedColumnKey);
    }
  }, [selectedSemanticMeasureKey]);

  const handleConfirmRemoveSemanticColumn = useCallback(() => {
    if (isWorkspacePanelReadOnly) {
      closeSemanticDeleteDialog();
      return;
    }

    if (!pendingSemanticDeleteColumnKey) {
      closeSemanticDeleteDialog();
      return;
    }

    if (pendingSemanticDeleteKind === SEMANTIC_DELETE_KIND_MEASURE) {
      handleRemoveSemanticMeasure(pendingSemanticDeleteColumnKey);
    } else {
      handleRemoveSemanticColumn(pendingSemanticDeleteColumnKey);
    }
    closeSemanticDeleteDialog();
  }, [closeSemanticDeleteDialog, handleRemoveSemanticColumn, handleRemoveSemanticMeasure, isWorkspacePanelReadOnly, pendingSemanticDeleteColumnKey, pendingSemanticDeleteKind]);

  const handleSemanticFieldDragEnd = useCallback((event) => {
    if (isWorkspacePanelReadOnly) {
      return;
    }

    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) {
      return;
    }

    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const currentSemanticItems = buildSemanticListItemsFromCollections(prev.Columns ?? [], prev.Measures ?? []);
      const oldIndex = currentSemanticItems.findIndex((item) => item.sortableId === String(active.id));
      const newIndex = currentSemanticItems.findIndex((item) => item.sortableId === String(over.id));

      if (oldIndex < 0 || newIndex < 0) {
        return prev;
      }

      const nextSemanticItems = arrayMove(currentSemanticItems, oldIndex, newIndex);
      const { columns: nextColumns, measures: nextMeasures } = resequenceSemanticCollectionsFromItems(nextSemanticItems);

      return {
        ...prev,
        Columns: nextColumns,
        Measures: nextMeasures,
      };
    });
  }, [isWorkspacePanelReadOnly]);

  const handleColumnDragEnd = useCallback((event) => {
    if (isWorkspacePanelReadOnly) {
      return;
    }

    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) {
      return;
    }

    setWorkingModel((prev) => {
      if (!prev) {
        return prev;
      }

      const { relationColumns, derivedColumns } = splitRelationAndDerivedColumns(prev.Columns ?? []);
      const oldIndex = relationColumns.findIndex((column) => String(column?.FieldName) === String(active.id));
      const newIndex = relationColumns.findIndex((column) => String(column?.FieldName) === String(over.id));

      if (oldIndex < 0 || newIndex < 0) {
        return prev;
      }

      const nextRelationColumns = arrayMove(relationColumns, oldIndex, newIndex).map((column, index) => ({
        ...column,
        GridSequence: index,
      }));
      const nextColumns = [...nextRelationColumns, ...derivedColumns];

      return {
        ...prev,
        Columns: nextColumns,
        ColumnsInitializedForReferenceId:
          prev.UiModelKind === MODEL_KIND_VIEW ? prev.ViewPrimaryModelId || "" : prev.ReferenceId || "",
      };
    });
  }, [isWorkspacePanelReadOnly]);

  const modelTabContent = workingModel ? (
    <div>
      <div className="model-builder-grid">
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.SelectList,
            FieldName: "UiModelKind",
            DisplayName: "Model Kind",
            Description: "Choose whether this model is backed by a table or a view definition.",
            DataJson: MODEL_KIND_OPTIONS,
          }}
          value={workingModel.UiModelKind}
          onChange={handleModelKindChange}
          disabled={isWorkspacePanelReadOnly}
        />

        {workingModel.UiModelKind === MODEL_KIND_TABLE ? (
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.SelectList,
              FieldName: "SourceType",
              DisplayName: "Source Type",
              Description: "Pick whether this table comes from a source object or an entity object.",
              DataJson: TABLE_SOURCE_TYPE_OPTIONS,
            }}
            value={workingModel.SourceType}
            onChange={handleTableSourceTypeChange}
            disabled={isWorkspacePanelReadOnly || isTableSourceBindingLocked}
          />
        ) : (
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.SelectList,
              FieldName: "ViewAuthoringMode",
              DisplayName: "Authoring Mode",
              Description: "Choose whether the view is authored in the builder or directly in DuckDB SQL.",
              DataJson: VIEW_AUTHORING_MODE_OPTIONS,
            }}
            value={workingModel.ViewAuthoringMode}
            onChange={handleViewAuthoringModeChange}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
      </div>

      {isTableSourceBindingLocked && (
        <div className="model-builder-readonly-note">
          {sourceBindingLockedMessage}
        </div>
      )}

      {workingModel.UiModelKind === MODEL_KIND_TABLE && Number(workingModel.SourceType) === 1 && (
        <div style={{ display: "flex", gap: "1rem", width: "100%", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.SearchSelectList,
                FieldName: "SelectedSourceSystemId",
                DisplayName: "Source System",
                Description: "Active source systems available to this client.",
                DataJson: sourceSystemOptions,
              }}
              value={workingModel.SelectedSourceSystemId}
              disabled={isWorkspacePanelReadOnly || isTableSourceBindingLocked}
              onChange={(value) => setWorkingModel((prev) => ({
                ...(prev ?? {}),
                SelectedSourceSystemId: value,
                SelectedSourceObjectId: "",
                ReferenceId: "",
                PrimaryDateColumnName: "",
                DefaultSorts: [],
                FilterDraft: [],
                Columns: [],
                ColumnsInitializedForReferenceId: "",
              }))}
            />
          </div>
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.SearchSelectList,
                FieldName: "SelectedSourceObjectId",
                DisplayName: "Source Object",
                Description: "Source objects filtered by the selected source system.",
                DataJson: sourceObjectOptions,
              }}
              value={workingModel.SelectedSourceObjectId}
              disabled={isWorkspacePanelReadOnly || isTableSourceBindingLocked || !workingModel.SelectedSourceSystemId}
              onChange={(value) => setWorkingModel((prev) => ({
                ...(prev ?? {}),
                SelectedSourceObjectId: value,
                PrimaryDateColumnName: "",
                DefaultSorts: [],
                FilterDraft: [],
                Columns: [],
                ColumnsInitializedForReferenceId: "",
              }))}
            />
          </div>
        </div>
      )}

      {workingModel.UiModelKind === MODEL_KIND_TABLE && Number(workingModel.SourceType) === 2 && (
        <>
          <div style={{ display: "flex", gap: "1rem", width: "100%", alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <FieldControl
                fieldMetadata={{
                  FieldType: SystemTypes.UIFieldType.SearchSelectList,
                  FieldName: "SelectedSchemaId",
                  DisplayName: "Schema",
                  Description: "Entity schemas available to this client.",
                  DataJson: schemaOptions,
                }}
                value={workingModel.SelectedSchemaId}
                disabled={isWorkspacePanelReadOnly || isTableSourceBindingLocked}
                onChange={(value) => setWorkingModel((prev) => ({
                  ...(prev ?? {}),
                  SelectedSchemaId: value,
                  SelectedEntityObjectId: "",
                  SelectedWarehouseId: "",
                  SelectedWarehouseDeploymentId: "",
                  ReferenceId: "",
                  PrimaryDateColumnName: "",
                  DefaultSorts: [],
                  FilterDraft: [],
                  Columns: [],
                  ColumnsInitializedForReferenceId: "",
                }))}
              />
            </div>
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <FieldControl
                fieldMetadata={{
                  FieldType: SystemTypes.UIFieldType.SearchSelectList,
                  FieldName: "SelectedEntityObjectId",
                  DisplayName: "Entity",
                  Description: "Entities filtered by the selected schema.",
                  DataJson: entityObjectOptions,
                }}
                value={workingModel.SelectedEntityObjectId}
                disabled={isWorkspacePanelReadOnly || isTableSourceBindingLocked || !workingModel.SelectedSchemaId}
                onChange={(value) => setWorkingModel((prev) => ({
                  ...(prev ?? {}),
                  SelectedEntityObjectId: value,
                  PrimaryDateColumnName: "",
                  DefaultSorts: [],
                  FilterDraft: [],
                  Columns: [],
                  ColumnsInitializedForReferenceId: "",
                }))}
              />
            </div>
          </div>

          {warehouses.length > 0 && (
            <div style={{ display: "flex", gap: "1rem", width: "100%", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <FieldControl
                  fieldMetadata={{
                    FieldType: SystemTypes.UIFieldType.SearchSelectList,
                    FieldName: "SelectedWarehouseId",
                    DisplayName: "Warehouse",
                    Description: "Warehouses available for the selected schema.",
                    DataJson: warehouseOptions,
                  }}
                  value={workingModel.SelectedWarehouseId}
                  disabled={isWorkspacePanelReadOnly || isTableSourceBindingLocked || !workingModel.SelectedSchemaId}
                  onChange={(value) => setWorkingModel((prev) => ({
                    ...(prev ?? {}),
                    SelectedWarehouseId: value,
                    SelectedWarehouseDeploymentId: "",
                    FilterDraft: [],
                  }))}
                />
              </div>
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <FieldControl
                  fieldMetadata={{
                    FieldType: SystemTypes.UIFieldType.SearchSelectList,
                    FieldName: "SelectedWarehouseDeploymentId",
                    DisplayName: "Warehouse Deployment",
                    Description: "Deployments available for the selected warehouse.",
                    DataJson: warehouseDeploymentOptions,
                  }}
                  value={workingModel.SelectedWarehouseDeploymentId}
                  disabled={isWorkspacePanelReadOnly || isTableSourceBindingLocked || !workingModel.SelectedWarehouseId || warehouseDeploymentOptions.length === 0}
                  onChange={(value) => setWorkingModel((prev) => ({
                    ...(prev ?? {}),
                    SelectedWarehouseDeploymentId: value,
                    FilterDraft: [],
                  }))}
                />
              </div>
            </div>
          )}
        </>
      )}

      <div className="model-builder-grid">
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.TextBox,
            FieldName: "Label",
            DisplayName: "Display Name",
            Description: "Label shown for this model.",
            IsRequired: true,
          }}
          value={workingModel.Label}
          onChange={handleLabelChange}
          disabled={isWorkspacePanelReadOnly}
        />
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.TextBox,
            FieldName: "Key",
            DisplayName: "Model Key",
            Description: "Machine-friendly model key with no spaces.",
            IsRequired: true,
          }}
          value={workingModel.Key}
          onChange={handleKeyChange}
          disabled={isWorkspacePanelReadOnly}
        />
      </div>

      <div className="model-builder-grid">
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.TextArea,
            FieldName: "Description",
            DisplayName: "Description",
            Description: "Optional description for this model.",
          }}
          value={workingModel.Description || ""}
          onChange={(value) => setWorkingModel((prev) => ({ ...(prev ?? {}), Description: value }))}
          disabled={isWorkspacePanelReadOnly}
        />
      </div>

      <div className="model-builder-grid">
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.SearchSelectList,
            FieldName: "PrimaryDateColumnName",
            DisplayName: "Primary Date Field",
            Description:
              primaryDateOptions.length > 0
                ? "Published date field used as the model's primary date."
                : "Select or publish a date field to choose a primary date.",
            DataJson: primaryDateFieldOptions,
            IsRequired: false,
          }}
          value={workingModel.PrimaryDateColumnName || ""}
          disabled={isWorkspacePanelReadOnly || primaryDateOptions.length === 0}
          onChange={(value) => setWorkingModel((prev) => ({ ...(prev ?? {}), PrimaryDateColumnName: value || "" }))}
        />
      </div>

      {workingModel.UiModelKind === MODEL_KIND_VIEW ? (
        <div className="model-builder-note">
          Use the View tab to configure builder-mode views or the SQL tab for advanced DuckDB SQL authoring.
        </div>
      ) : null}
    </div>
  ) : null;

  const viewTabContent = !workingModel || workingModel.UiModelKind !== MODEL_KIND_VIEW ? null : (
    <div>
      {Number(workingModel.ViewAuthoringMode) === VIEW_AUTHORING_MODE_SQL ? (
        <div className="model-builder-note">
          SQL-authored views use the SQL tab as the source of truth. Relation columns and filters are not configured in the builder for this mode.
        </div>
      ) : (
        <>
          <div className="model-builder-grid">
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.SearchSelectList,
                FieldName: "ViewPrimaryModelId",
                DisplayName: "Primary Model",
                Description: "Root table or view used by the builder.",
                DataJson: viewModelOptions,
              }}
              value={workingModel.ViewPrimaryModelId}
              onChange={handleViewPrimaryModelChange}
              disabled={isWorkspacePanelReadOnly}
            />
          </div>
          <div className="model-builder-note">
            Phase 1 builder views support projection from a primary model. Join authoring stays in the backend contract but is not exposed in this first UI slice.
          </div>
        </>
      )}
    </div>
  );

  const sqlTabContent = !workingModel || workingModel.UiModelKind !== MODEL_KIND_VIEW ? null : (
    <div>
      <FieldControl
        fieldMetadata={{
          FieldType: SystemTypes.UIFieldType.SQL,
          FieldName: "ViewTemplate",
          DisplayName: "DuckDB SQL",
          Description: "Provide the DuckDB select body used to define this view.",
        }}
        value={workingModel.ViewTemplate || ""}
        onChange={(value) => setWorkingModel((prev) => ({ ...(prev ?? {}), ViewTemplate: value }))}
        disabled={isWorkspacePanelReadOnly || Number(workingModel.ViewAuthoringMode) !== VIEW_AUTHORING_MODE_SQL}
      />
    </div>
  );

  const canConfigureRelationColumns =
    !!selectedBackingObject &&
    (
      workingModel?.UiModelKind === MODEL_KIND_TABLE ||
      Number(workingModel?.ViewAuthoringMode) === VIEW_AUTHORING_MODE_BUILDER
    );

  const canConfigureRelationFilter =
    !!panelFilterTargetId &&
    (
      workingModel?.UiModelKind === MODEL_KIND_TABLE ||
      Number(workingModel?.ViewAuthoringMode) === VIEW_AUTHORING_MODE_BUILDER
    );

  const filterTabContent = !canConfigureRelationFilter ? (
    <div>
      <div className="model-builder-note">
        {workingModel?.UiModelKind === MODEL_KIND_VIEW
          ? Number(workingModel?.ViewAuthoringMode) === VIEW_AUTHORING_MODE_SQL
            ? "SQL-authored views do not use the builder filter in phase 1."
            : "Select a primary model on the View tab to configure the builder filter."
          : "Select a source object or entity object on the model tab to configure filtering. If you do not add a filter, the model returns all data."}
      </div>
    </div>
  ) : (
    <div className="model-filter-tab">
      {filterLoadError ? (
        <div className="model-panel-error">{filterLoadError}</div>
      ) : null}

        <div className="model-filter-control">
          {isFilterLoading ? (
            <div className="model-filter-loading">
              <Loading />
            </div>
          ) : panelFilterables.length === 0 ? (
          <div className="model-builder-note">
            No filterable fields are available for the selected object.
          </div>
        ) : (
          <FilterGroupsControl
            filterables={panelFilterables}
            filters={panelFilterDraft}
            onChange={handlePanelFilterDraftChange}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
      </div>

      <div className="model-filter-toolbar">
        <div className="model-filter-actions">
          <ButtonControl
            type="cancel"
            onClick={clearPanelFilter}
            disabled={isWorkspacePanelReadOnly || isFilterLoading || !hasPanelFilter}
          >
            Clear Filter
          </ButtonControl>
        </div>
      </div>
    </div>
  );

  const columnsTabContent = !canConfigureRelationColumns ? (
    <div>
      <div className="model-builder-note">
        {workingModel?.UiModelKind === MODEL_KIND_VIEW
          ? Number(workingModel?.ViewAuthoringMode) === VIEW_AUTHORING_MODE_SQL
            ? "SQL-authored views do not use the builder column selector in phase 1."
            : "Select a primary model on the View tab to configure which columns this view returns."
          : "Select a source object or entity object on the model tab to configure which columns this model returns."}
      </div>
    </div>
  ) : (
    <div className="model-columns-tab">
      <div className="model-columns-toolbar">
        <div className="model-columns-picker">
          <MultiSelectListControl
            metadata={{
              FieldName: "SelectedColumns",
              DataJson: columnSelectionOptions,
              PlaceHolder: "Select columns...",
            }}
            value={selectedColumnFieldNames}
            onChange={handleSelectedColumnsChange}
            disabled={isWorkspacePanelReadOnly || !!editingColumn}
          />
        </div>
      </div>

      <div className="model-columns-content">
        <div className="model-builder-note">
          Relation editing only controls which backing fields are projected and in what order. Published names, display metadata, primary date, and default sorts are edited in the semantic mode.
        </div>

        {selectedColumnFieldNames.length === 0 ? (
          <div className="model-builder-note">
            No columns selected. Use the selector above to add columns back into the model.
          </div>
        ) : (
          <div className="model-columns-list">
            <DndContext sensors={columnSensors} collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
              <SortableContext
                items={relationColumns.map((column) => String(column?.FieldName))}
                strategy={verticalListSortingStrategy}
              >
                {relationColumns.map((column, index) => (
                  <SortableColumnRow
                    key={column?.FieldName || index}
                    column={column}
                    index={index}
                    onRemove={handleRemoveColumn}
                    isReadOnly={isWorkspacePanelReadOnly}
                    deleteDisabled={relationColumnDeleteStateByFieldName[String(column?.FieldName ?? "")]?.deleteDisabled}
                    deleteTitle={relationColumnDeleteStateByFieldName[String(column?.FieldName ?? "")]?.deleteTitle}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    </div>
  );

  const panelFooter = (
    <div
      className="model-panel-footer"
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "0.75rem",
        marginTop: "auto",
        padding: "1rem",
        borderTop: "1px solid var(--primary-border)",
        background: "var(--secondary-background)",
      }}
    >
      <ButtonControl type="cancel" onClick={closeModelPanel} disabled={isSavingModel || !!editingColumn}>
        {isWorkspacePanelReadOnly ? "Close" : "Cancel"}
      </ButtonControl>
      {!isWorkspacePanelReadOnly ? (
        <>
          <ButtonControl
            type="action"
            className="model-panel-save-continue"
            onClick={() => handleSaveModel({ keepOpen: true })}
            disabled={isSavingModel || !canSaveModel || !!editingColumn || !isWorkspaceDesignMode}
          >
            {isSavingModel
              ? "Saving..."
              : "Save and Continue"}
          </ButtonControl>
          <ButtonControl
            type="ok"
            onClick={() => handleSaveModel()}
            disabled={isSavingModel || !canSaveModel || !!editingColumn || !isWorkspaceDesignMode}
          >
            {isSavingModel
              ? "Saving..."
              : "Save"}
          </ButtonControl>
        </>
      ) : null}
    </div>
  );

  const relationPanelLookupOverlay = isPanelLookupLoading ? (
    <div
      style={{
        position: "absolute",
        top: "4.1rem",
        right: "1rem",
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Loading />
    </div>
  ) : null;

  const semanticPanelLookupOverlay = isPanelLookupLoading ? (
    <div
      style={{
        position: "absolute",
        top: "1.75rem",
        right: "2rem",
        zIndex: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Loading />
    </div>
  ) : null;

  const isSelectedSemanticColumnDerived =
    !!selectedSemanticColumn && Number(selectedSemanticColumn.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) !== SEMANTIC_COLUMN_KIND_BASE;
  const isSelectedSemanticColumnStatic =
    !!selectedSemanticColumn && Number(selectedSemanticColumn.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) === SEMANTIC_COLUMN_KIND_STATIC;
  const isSelectedSemanticMeasureCalculated =
    !!selectedSemanticMeasure && Number(selectedSemanticMeasure.MeasureKind ?? MEASURE_KIND_AGGREGATE) === MEASURE_KIND_CALCULATED;

  const semanticDerivedDetailsContent = selectedSemanticColumn ? (
    <>
      <div className="model-builder-grid">
        {renderValidatedField(
          getSemanticColumnValidationPaths(selectedSemanticColumn, "ColumnKind"),
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.SelectList,
              FieldName: "ColumnKind",
              DisplayName: "Column Type",
              Description: "Choose calculated or static.",
              DataJson: SEMANTIC_ADD_COLUMN_KIND_OPTIONS,
            }}
            value={selectedSemanticColumn.ColumnKind}
            onChange={(value, fieldName, currentValueObject) => handleSemanticColumnFieldChange("ColumnKind", value, currentValueObject)}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
        {renderValidatedField(
          getSemanticColumnValidationPaths(selectedSemanticColumn, "DataTypeName"),
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.SelectList,
              FieldName: "DataTypeName",
              DisplayName: "Data Type",
              Description:
                Number(selectedSemanticColumn.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) === SEMANTIC_COLUMN_KIND_STATIC
                  ? "Published data type."
                  : "Published data type. Validate/save will align it to the resolved expression result.",
              DataJson: SEMANTIC_DATA_TYPE_OPTIONS,
            }}
            value={selectedSemanticColumn.DataTypeName || ""}
            onChange={(value) => handleSemanticColumnFieldChange("DataTypeName", value)}
            disabled={isWorkspacePanelReadOnly || Number(selectedSemanticColumn.ColumnKind ?? SEMANTIC_COLUMN_KIND_BASE) !== SEMANTIC_COLUMN_KIND_STATIC}
          />
        )}
      </div>

      <div className="model-builder-grid">
        {renderValidatedField(
          getSemanticColumnValidationPaths(selectedSemanticColumn, "ColumnName"),
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.TextBox,
              FieldName: "ColumnName",
              DisplayName: "Column Name",
              Description: "Published semantic name.",
              IsRequired: true,
            }}
            value={getEffectiveColumnName(selectedSemanticColumn)}
            onChange={(value) => handleSemanticColumnFieldChange("ColumnName", toColumnNameInput(value))}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
        {renderValidatedField(
          getSemanticColumnValidationPaths(selectedSemanticColumn, "DisplayName"),
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.TextBox,
              FieldName: "DisplayName",
              DisplayName: "Display Name",
              Description: "Friendly authoring label.",
            }}
            value={selectedSemanticColumn.DisplayName || ""}
            onChange={(value) => handleSemanticColumnFieldChange("DisplayName", value)}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
      </div>

      <div className="model-builder-grid">
        <div className="model-builder-grid__span-2">
          {renderValidatedField(
            getSemanticColumnValidationPaths(selectedSemanticColumn, "Description"),
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.TextBox,
                FieldName: "Description",
                DisplayName: "Description",
                Description: "Optional description for this semantic column.",
              }}
              value={selectedSemanticColumn.Description || ""}
              onChange={(value) => handleSemanticColumnFieldChange("Description", value)}
              disabled={isWorkspacePanelReadOnly}
            />
          )}
        </div>
      </div>

      {isSelectedSemanticColumnStatic ? (
        <div className="model-builder-grid">
          <div className="model-builder-grid__span-2">
            {renderValidatedField(
              selectedSemanticColumnExpressionValidationPaths,
              <FieldControl
                fieldMetadata={{
                  FieldType: SystemTypes.UIFieldType.TextBox,
                  FieldName: "ValueDefinitionValue",
                  DisplayName: "Static Value",
                  Description: "Literal value returned for every row.",
                }}
                value={selectedSemanticColumn.ValueDefinitionValue || ""}
                onChange={(value) => handleSemanticColumnFieldChange("ValueDefinitionValue", value)}
                disabled={isWorkspacePanelReadOnly}
              />
            )}
          </div>
        </div>
      ) : null}

      <div className="model-builder-grid">
        {renderValidatedField(
          getSemanticColumnValidationPaths(selectedSemanticColumn, "ShowOnGrid"),
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.CheckBox,
              FieldName: "ShowOnGrid",
              DisplayName: "Visible",
              Description: "Whether this column is visible by default in authoring and runtime UX.",
            }}
            value={selectedSemanticColumn.ShowOnGrid ?? true}
            onChange={(value) => handleSemanticColumnFieldChange("ShowOnGrid", value)}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
      </div>
    </>
  ) : null;

  const semanticMeasureDetailsContent = selectedSemanticMeasure ? (
    <>
      <div className="model-builder-grid">
        {renderValidatedField(
          getSemanticMeasureValidationPaths(selectedSemanticMeasure, "MeasureKind"),
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.SelectList,
              FieldName: "MeasureKind",
              DisplayName: "Measure Type",
              Description: "Choose aggregate or calculated.",
              DataJson: MEASURE_KIND_OPTIONS,
            }}
            value={selectedSemanticMeasure.MeasureKind}
            onChange={(value) => handleSemanticMeasureFieldChange("MeasureKind", Number(value))}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
      </div>

      {!isSelectedSemanticMeasureCalculated ? (
        <div className="model-builder-grid">
          {renderValidatedField(
            getSemanticMeasureValidationPaths(selectedSemanticMeasure, "AggregationKind"),
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.SearchSelectList,
                FieldName: "AggregationKind",
                DisplayName: "Aggregation",
                Description: "Aggregate function used for this measure.",
                DataJson: AGGREGATION_KIND_OPTIONS,
              }}
              value={selectedSemanticMeasure.AggregationKind}
              onChange={(value) => handleSemanticMeasureFieldChange("AggregationKind", Number(value))}
              disabled={isWorkspacePanelReadOnly}
            />
          )}
          {renderValidatedField(
            getSemanticMeasureValidationPaths(selectedSemanticMeasure, "MeasureDefinition.TargetName"),
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.SearchSelectList,
                FieldName: "TargetName",
                DisplayName: "Target Column",
                Description: "Published column aggregated by this measure.",
                DataJson: measureTargetOptions,
              }}
              value={selectedSemanticMeasure.TargetName || ""}
              onChange={(value) => handleSemanticMeasureFieldChange("TargetName", value)}
              disabled={isWorkspacePanelReadOnly}
            />
          )}
        </div>
      ) : null}

      <div className="model-builder-grid">
        {renderValidatedField(
          getSemanticMeasureValidationPaths(selectedSemanticMeasure, "MeasureName"),
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.TextBox,
              FieldName: "MeasureName",
              DisplayName: "Measure Name",
              Description: "Published measure name.",
              IsRequired: true,
            }}
            value={getEffectiveMeasureName(selectedSemanticMeasure)}
            onChange={(value) => handleSemanticMeasureFieldChange("MeasureName", toColumnNameInput(value))}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
        {renderValidatedField(
          getSemanticMeasureValidationPaths(selectedSemanticMeasure, "DisplayName"),
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.TextBox,
              FieldName: "DisplayName",
              DisplayName: "Display Name",
              Description: "Friendly authoring label.",
            }}
            value={selectedSemanticMeasure.DisplayName || ""}
            onChange={(value) => handleSemanticMeasureFieldChange("DisplayName", value)}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
      </div>

      <div className="model-builder-grid">
        <div className="model-builder-grid__span-2">
          {renderValidatedField(
            getSemanticMeasureValidationPaths(selectedSemanticMeasure, "Description"),
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.TextBox,
                FieldName: "Description",
                DisplayName: "Description",
                Description: "Optional description for this measure.",
              }}
              value={selectedSemanticMeasure.Description || ""}
              onChange={(value) => handleSemanticMeasureFieldChange("Description", value)}
              disabled={isWorkspacePanelReadOnly}
            />
          )}
        </div>
      </div>

      <div className="model-builder-grid">
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.SearchSelectList,
            FieldName: "Format",
            DisplayName: "Format",
            Description: "Preferred display format for this measure.",
            DataJson: uiFormatOptions,
          }}
          value={selectedSemanticMeasure.Format || ""}
          onChange={(value) => handleSemanticMeasureFieldChange("Format", value)}
          disabled={isWorkspacePanelReadOnly}
        />
        <FieldControl
          fieldMetadata={{
            FieldType: SystemTypes.UIFieldType.SelectList,
            FieldName: "MeasureDefaultSortDirection",
            DisplayName: "Default Sort",
            Description: "Default sort direction when this measure is used in grouped outputs.",
            DataJson: DEFAULT_SORT_DIRECTION_OPTIONS,
          }}
          value={selectedSemanticMeasureDefaultSortDirection}
          onChange={(value) => handleSemanticMeasureDefaultSortChange(Number(value))}
          disabled={isWorkspacePanelReadOnly}
        />
        {renderValidatedField(
          getSemanticMeasureValidationPaths(selectedSemanticMeasure, "IsVisible"),
          <FieldControl
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.CheckBox,
              FieldName: "IsVisible",
              DisplayName: "Visible",
              Description: "Whether this measure is visible by default in authoring and runtime UX.",
            }}
            value={selectedSemanticMeasure.IsVisible ?? true}
            onChange={(value) => handleSemanticMeasureFieldChange("IsVisible", value)}
            disabled={isWorkspacePanelReadOnly}
          />
        )}
      </div>
    </>
  ) : null;

  const semanticDerivedExpressionContent = selectedSemanticColumn ? (
    renderValidatedField(
      selectedSemanticColumnExpressionValidationPaths,
      <div className="expression-editor">
        <div className="expression-editor__header">
          <div>
            <div className="expression-editor__title">Expression</div>
            <div className="expression-editor__copy">
              Row-level expression using published column references like <code>[column_name]</code> and functions like <code>CONCAT(...)</code>.
            </div>
          </div>
          <div className="expression-editor__actions">
            <ButtonControl
              type="icon"
              className="model-pane-icon-action expression-editor__validate-button"
              title={isValidatingExpression ? "Validating expression..." : "Validate expression"}
              onClick={handleValidateSemanticExpression}
              disabled={isWorkspacePanelReadOnly || isValidatingExpression}
            >
              <IconControl iconKey="success" />
            </ButtonControl>
            <ButtonControl
              type="icon"
              className="model-pane-icon-action expression-editor__undo-button"
              title="Undo last expression builder action"
              onClick={handleUndoSemanticExpression}
              disabled={isWorkspacePanelReadOnly || isValidatingExpression || !canUndoSelectedSemanticExpression}
            >
              <IconControl iconKey="undo" />
            </ButtonControl>
            <ButtonControl
              type="icon"
              className="model-pane-icon-action expression-editor__clear-button"
              title="Clear expression"
              onClick={handleClearSemanticExpression}
              disabled={isWorkspacePanelReadOnly || isValidatingExpression || !(selectedSemanticColumn?.ValueDefinitionValue || "")}
            >
              <IconControl iconKey="clearall" />
            </ButtonControl>
          </div>
        </div>

        <textarea
          ref={semanticExpressionInputRef}
          className="expression-editor__input"
          value={selectedSemanticColumn.ValueDefinitionValue || ""}
          onChange={(event) => handleSemanticExpressionInputChange(event.target.value)}
          disabled={isWorkspacePanelReadOnly}
          placeholder="Example: CONCAT(COALESCE([first_name], ''), ' ', COALESCE([last_name], ''))"
          spellCheck={false}
          rows={6}
        />

        <div className="expression-editor__section">
          <div className="expression-editor__section-header">
            <div className="expression-editor__section-label">Insert Field</div>
            <div className="expression-editor__field-search">
              <SearchInput
                className="expression-editor__field-search-input"
                placeholder="Search fields"
                value={expressionFieldSearch}
                onChange={setExpressionFieldSearch}
                disabled={isWorkspacePanelReadOnly}
              />
            </div>
          </div>
          <div className="expression-editor__token-scroll">
            <div className="expression-editor__token-list">
              {filteredExpressionFieldInsertOptions.length === 0 ? (
                <div className="expression-editor__empty">
                  {expressionFieldInsertOptions.length === 0
                    ? "No other published columns available."
                    : "No matching fields found."}
                </div>
              ) : (
                filteredExpressionFieldInsertOptions.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="expression-editor__token expression-editor__token--field"
                    onClick={() => handleInsertSemanticFieldReference(item.insertText)}
                    disabled={isWorkspacePanelReadOnly}
                  >
                    <span className="expression-editor__token-label">{item.label}</span>
                    {item.typeName ? (
                      <span className="expression-editor__token-type">{item.typeName}</span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="expression-editor__section">
          <div className="expression-editor__section-label">Insert Measure</div>
          <div className="expression-editor__token-scroll">
            <div className="expression-editor__token-list">
              {filteredExpressionMeasureInsertOptions.length === 0 ? (
                <div className="expression-editor__empty">
                  {expressionMeasureInsertOptions.length === 0
                    ? "No other published measures available."
                    : "No matching measures found."}
                </div>
              ) : (
                filteredExpressionMeasureInsertOptions.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="expression-editor__token expression-editor__token--field"
                    onClick={() => handleInsertSemanticFieldReference(item.insertText)}
                    disabled={isWorkspacePanelReadOnly}
                    title={`${item.displayName} | ${item.label}`}
                  >
                    <span className="expression-editor__token-label">{item.label}</span>
                    {item.displayName && item.displayName !== item.label ? (
                      <span className="expression-editor__token-type">{item.displayName}</span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="expression-editor__section">
          <div className="expression-editor__section-label">Operators</div>
          <div className="expression-editor__token-list">
            {EXPRESSION_OPERATOR_OPTIONS.map((item) => (
              <button
                key={item.Text}
                type="button"
                className="expression-editor__token expression-editor__token--compact"
                onClick={() => handleInsertSemanticExpressionText(item.Value)}
                disabled={isWorkspacePanelReadOnly}
              >
                {item.Text}
              </button>
            ))}
          </div>
        </div>

        <div className="expression-editor__section">
          <div className="expression-editor__section-label">Functions</div>
          <div className="expression-editor__token-list">
            {EXPRESSION_FUNCTION_OPTIONS.map((item) => (
              <button
                key={item.Text}
                type="button"
                className="expression-editor__token"
                onClick={() => handleInsertSemanticExpressionText(item.Value)}
                disabled={isWorkspacePanelReadOnly}
              >
                {item.Text}
              </button>
            ))}
          </div>
        </div>
      </div>,
      { success: isSelectedSemanticExpressionValidated }
    )
  ) : null;

  const semanticMeasureExpressionContent = selectedSemanticMeasure ? (
    renderValidatedField(
      selectedSemanticMeasureExpressionValidationPaths,
      <div className="expression-editor">
        <div className="expression-editor__header">
          <div>
            <div className="expression-editor__title">Expression</div>
            <div className="expression-editor__copy">
              Aggregate expression using published column and measure references.
            </div>
          </div>
          <div className="expression-editor__actions">
            <ButtonControl
              type="icon"
              className="model-pane-icon-action expression-editor__validate-button"
              title={isValidatingExpression ? "Validating expression..." : "Validate expression"}
              onClick={handleValidateSemanticExpression}
              disabled={isWorkspacePanelReadOnly || isValidatingExpression}
            >
              <IconControl iconKey="success" />
            </ButtonControl>
            <ButtonControl
              type="icon"
              className="model-pane-icon-action expression-editor__undo-button"
              title="Undo last expression builder action"
              onClick={handleUndoSemanticExpression}
              disabled={isWorkspacePanelReadOnly || isValidatingExpression || !canUndoSelectedSemanticExpression}
            >
              <IconControl iconKey="undo" />
            </ButtonControl>
            <ButtonControl
              type="icon"
              className="model-pane-icon-action expression-editor__clear-button"
              title="Clear expression"
              onClick={handleClearSemanticExpression}
              disabled={isWorkspacePanelReadOnly || isValidatingExpression || !(selectedSemanticMeasure?.ExpressionValue || "")}
            >
              <IconControl iconKey="clearall" />
            </ButtonControl>
          </div>
        </div>

        <textarea
          ref={semanticExpressionInputRef}
          className="expression-editor__input"
          value={selectedSemanticMeasure.ExpressionValue || ""}
          onChange={(event) => handleSemanticExpressionInputChange(event.target.value)}
          disabled={isWorkspacePanelReadOnly}
          placeholder="Example: SUM([revenue]) - SUM([cost])"
          spellCheck={false}
          rows={6}
        />

        <div className="expression-editor__section">
          <div className="expression-editor__section-header">
            <div className="expression-editor__section-label">Insert Field / Measure</div>
            <div className="expression-editor__field-search">
              <SearchInput
                className="expression-editor__field-search-input"
                placeholder="Search fields or measures"
                value={expressionFieldSearch}
                onChange={setExpressionFieldSearch}
                disabled={isWorkspacePanelReadOnly}
              />
            </div>
          </div>
          <div className="expression-editor__token-scroll">
            <div className="expression-editor__token-list">
              {filteredAggregateExpressionInsertOptions.length === 0 ? (
                <div className="expression-editor__empty">
                  {aggregateExpressionInsertOptions.length === 0
                    ? "No published fields or measures available."
                    : "No matching fields or measures found."}
                </div>
              ) : (
                filteredAggregateExpressionInsertOptions.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="expression-editor__token expression-editor__token--field"
                    onClick={() => handleInsertSemanticFieldReference(item.insertText)}
                    disabled={isWorkspacePanelReadOnly}
                  >
                    <span className="expression-editor__token-label">{item.label}</span>
                    {item.typeName ? (
                      <span className="expression-editor__token-type">{item.typeName}</span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="expression-editor__section">
          <div className="expression-editor__section-label">Operators</div>
          <div className="expression-editor__token-list">
            {EXPRESSION_OPERATOR_OPTIONS.map((item) => (
              <button
                key={item.Text}
                type="button"
                className="expression-editor__token expression-editor__token--compact"
                onClick={() => handleInsertSemanticExpressionText(item.Value)}
                disabled={isWorkspacePanelReadOnly}
              >
                {item.Text}
              </button>
            ))}
          </div>
        </div>

        <div className="expression-editor__section">
          <div className="expression-editor__section-label">Aggregates</div>
          <div className="expression-editor__token-list">
            {AGGREGATE_EXPRESSION_FUNCTION_OPTIONS.map((item) => (
              <button
                key={item.Text}
                type="button"
                className="expression-editor__token"
                onClick={() => handleInsertSemanticExpressionText(item.Value)}
                disabled={isWorkspacePanelReadOnly}
              >
                {item.Text}
              </button>
            ))}
          </div>
        </div>

        <div className="expression-editor__section">
          <div className="expression-editor__section-label">Functions</div>
          <div className="expression-editor__token-list">
            {EXPRESSION_FUNCTION_OPTIONS.map((item) => (
              <button
                key={item.Text}
                type="button"
                className="expression-editor__token"
                onClick={() => handleInsertSemanticExpressionText(item.Value)}
                disabled={isWorkspacePanelReadOnly}
              >
                {item.Text}
              </button>
            ))}
          </div>
        </div>
      </div>,
      { success: isSelectedSemanticExpressionValidated }
    )
  ) : null;

  const semanticSidebarContent = !workingModel || workingModel.UiModelKind !== MODEL_KIND_TABLE ? (
    <div className="model-panel-manual semantic-panel-pane">
      <div className="model-panel-body semantic-panel-body" style={{ overflow: "hidden", padding: "1rem" }}>
        <div className="model-builder-note">
          Semantic authoring is available for table models only.
        </div>
      </div>
    </div>
  ) : (
    <div className="model-panel-manual semantic-panel-pane">
      <div className="model-panel-body semantic-panel-body" style={{ overflow: "hidden", padding: "1rem" }}>
        <div className="semantic-editor__sidebar">
        <div className="semantic-editor__sidebar-header">
          <div className="semantic-editor__sidebar-header-row">
            <div>
              <div className="semantic-editor__eyebrow">Fields</div>
              <div className="semantic-editor__title">Columns and Measures</div>
            </div>
            <div className="semantic-editor__sidebar-actions">
              <span title="Add Column">
                <ButtonControl
                  className="semantic-editor__add-button"
                  type="icon"
                  onClick={handleStartAddSemanticColumn}
                  title="Add Column"
                  disabled={isWorkspacePanelReadOnly}
                >
                  <IconControl iconKey="column" title="Add Column" />
                </ButtonControl>
              </span>
              <span title="Add Measure">
                <ButtonControl
                  className="semantic-editor__add-button"
                  type="icon"
                  onClick={handleStartAddSemanticMeasure}
                  title="Add Measure"
                  disabled={isWorkspacePanelReadOnly}
                >
                  <IconControl iconKey="measure" title="Add Measure" />
                </ButtonControl>
              </span>
            </div>
          </div>
          <div className="semantic-editor__copy">
            Drag to reorder fields. Select a field to edit it, or add calculated fields and measures as needed.
          </div>
        </div>

          <div className="semantic-editor__list" ref={semanticListRef}>
            {semanticListItems.length === 0 ? (
              <div className="model-builder-note">
                Add relation columns first, then return here to configure semantic fields and measures.
              </div>
            ) : (
              <DndContext sensors={columnSensors} collisionDetection={closestCenter} onDragEnd={handleSemanticFieldDragEnd}>
                <SortableContext
                  items={semanticListItems.map((item) => item.sortableId)}
                  strategy={verticalListSortingStrategy}
                >
                  {semanticListItems.map((semanticItem) =>
                    semanticItem.itemType === "measure" ? (
                      (() => {
                        const validationEntry = getSemanticMeasureValidationEntry(panelValidationEntries, semanticItem.item, `measure-${semanticItem.index}`);

                        return (
                          <SemanticMeasureRow
                            key={semanticItem.sortableId}
                            measure={semanticItem.item}
                            index={semanticItem.index}
                            sortableId={semanticItem.sortableId}
                            isSelected={semanticItem.key === String(selectedSemanticMeasureKey)}
                            hasError={!!validationEntry}
                            errorMessage={validationEntry?.message || ""}
                            defaultSortDirection={getMeasureDefaultSortDirection(workingModel.DefaultSorts, getEffectiveMeasureName(semanticItem.item))}
                            onSelect={handleSelectSemanticMeasure}
                            onRequestDelete={handleRequestRemoveSemanticMeasure}
                            isReadOnly={isWorkspacePanelReadOnly}
                          />
                        );
                      })()
                    ) : (
                      (() => {
                        const validationEntry = getSemanticColumnValidationEntry(panelValidationEntries, semanticItem.item, `column-${semanticItem.index}`);

                        return (
                          <SortableSemanticFieldRow
                            key={semanticItem.sortableId}
                            column={semanticItem.item}
                            index={semanticItem.index}
                            sortableId={semanticItem.sortableId}
                            isSelected={semanticItem.key === String(selectedSemanticColumnFieldName)}
                            hasError={!!validationEntry}
                            errorMessage={validationEntry?.message || ""}
                            defaultSortDirection={getColumnDefaultSortDirection(workingModel.DefaultSorts, getEffectiveColumnName(semanticItem.item))}
                            isPrimaryDate={
                              String(workingModel.PrimaryDateColumnName ?? "").trim().toLowerCase() ===
                              getEffectiveColumnName(semanticItem.item).toLowerCase()
                            }
                            onSelect={handleSelectSemanticColumn}
                            onRequestDelete={handleRequestRemoveSemanticColumn}
                            isReadOnly={isWorkspacePanelReadOnly}
                          />
                        );
                      })()
                    )
                  )}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const semanticEditorContent = (
    <div className="model-panel-manual semantic-panel-pane" style={{ position: "relative" }}>
      {semanticPanelLookupOverlay}
      <div className="model-panel-body semantic-panel-body" style={{ overflow: "auto", padding: "1rem" }}>
        <div className="semantic-editor__content">
          {!selectedSemanticColumn && !selectedSemanticMeasure ? (
            <div className="model-pane-empty wide">
              <div className="empty-title">No field selected</div>
              <div className="empty-copy">
                Choose a column or measure from the list to edit its published settings.
              </div>
            </div>
          ) : selectedSemanticMeasure ? (
            <div className="model-column-editor semantic-editor__column-editor">
              <div className="model-column-editor__header">
                <div className="model-column-editor__header-left">
                  <div
                    className="model-column-editor__title"
                    title={selectedSemanticMeasureDisplay.title}
                  >
                    {middleEllipsis(selectedSemanticMeasureDisplay.title, 48)}
                  </div>
                  <div
                    className="model-column-editor__subtitle"
                    title={selectedSemanticMeasureDisplay.displayName}
                  >
                    {selectedSemanticMeasureDisplay.displayName}
                  </div>
                </div>
              </div>

              <TabPaneControl
                className="semantic-editor__tabs"
                tabs={[
                  { key: SEMANTIC_EDITOR_TAB_DETAILS, title: "Details", content: semanticMeasureDetailsContent },
                  {
                    key: SEMANTIC_EDITOR_TAB_EXPRESSION,
                    title: "Expression",
                    content: semanticMeasureExpressionContent,
                    disabled: !isSelectedSemanticMeasureCalculated,
                  },
                ]}
                activeIndex={Math.max(
                  0,
                  [SEMANTIC_EDITOR_TAB_DETAILS, SEMANTIC_EDITOR_TAB_EXPRESSION].findIndex((tabKey) => tabKey === semanticEditorTab)
                )}
                onChange={(nextIndex) => {
                  const semanticTabKeys = [SEMANTIC_EDITOR_TAB_DETAILS, SEMANTIC_EDITOR_TAB_EXPRESSION];
                  const nextTab = semanticTabKeys[nextIndex] ?? SEMANTIC_EDITOR_TAB_DETAILS;
                  setSemanticEditorTab(!isSelectedSemanticMeasureCalculated && nextTab === SEMANTIC_EDITOR_TAB_EXPRESSION ? SEMANTIC_EDITOR_TAB_DETAILS : nextTab);
                }}
                align="right"
                compact={true}
                framed={false}
                unmountInactive={true}
              />
            </div>
          ) : isSelectedSemanticColumnDerived ? (
            <div className="model-column-editor semantic-editor__column-editor">
              <div className="model-column-editor__header">
                <div className="model-column-editor__header-left">
                  <div
                    className="model-column-editor__title"
                    title={selectedSemanticColumnDisplay.title}
                  >
                    {middleEllipsis(selectedSemanticColumnDisplay.title, 48)}
                  </div>
                  <div
                    className="model-column-editor__subtitle"
                    title={selectedSemanticColumn.DisplayName || selectedSemanticColumnDisplay.columnName}
                  >
                    {selectedSemanticColumn.DisplayName || selectedSemanticColumnDisplay.columnName}
                  </div>
                </div>
              </div>

              <TabPaneControl
                className="semantic-editor__tabs"
                tabs={[
                  { key: SEMANTIC_EDITOR_TAB_DETAILS, title: "Details", content: semanticDerivedDetailsContent },
                  {
                    key: SEMANTIC_EDITOR_TAB_EXPRESSION,
                    title: "Expression",
                    content: semanticDerivedExpressionContent,
                    disabled: isSelectedSemanticColumnStatic,
                  },
                ]}
                activeIndex={Math.max(
                  0,
                  [SEMANTIC_EDITOR_TAB_DETAILS, SEMANTIC_EDITOR_TAB_EXPRESSION].findIndex((tabKey) => tabKey === semanticEditorTab)
                )}
                onChange={(nextIndex) => {
                  const semanticTabKeys = [SEMANTIC_EDITOR_TAB_DETAILS, SEMANTIC_EDITOR_TAB_EXPRESSION];
                  const nextTab = semanticTabKeys[nextIndex] ?? SEMANTIC_EDITOR_TAB_DETAILS;
                  setSemanticEditorTab(isSelectedSemanticColumnStatic && nextTab === SEMANTIC_EDITOR_TAB_EXPRESSION ? SEMANTIC_EDITOR_TAB_DETAILS : nextTab);
                }}
                align="right"
                compact={true}
                framed={false}
                unmountInactive={true}
              />
            </div>
          ) : (
            <div className="model-column-editor semantic-editor__column-editor">
              <div className="model-column-editor__header">
                <div>
                  <div
                    className="model-column-editor__title"
                    title={selectedSemanticColumnDisplay.title}
                  >
                    {middleEllipsis(selectedSemanticColumnDisplay.title, 48)}
                  </div>
                  <div
                    className="model-column-editor__subtitle"
                    title={selectedSemanticColumnDisplay.displayName}
                  >
                    {selectedSemanticColumnDisplay.displayName}
                  </div>
                </div>
              </div>

              <div className="model-builder-grid">
                <div className="model-builder-grid__span-2">
                  <FieldControl
                    fieldMetadata={{
                      FieldType: SystemTypes.UIFieldType.TextBox,
                      FieldName: "FieldName",
                      DisplayName: "Source Field",
                      Description: "Underlying source or entity field used by this published semantic column.",
                    }}
                    value={selectedSemanticColumn.FieldName}
                    disabled={true}
                  />
                </div>
              </div>

              <div className="model-builder-grid">
                <div className="model-builder-grid__span-2">
                  {renderValidatedField(
                    ["SemanticDefinition.Columns.ColumnName"],
                    <FieldControl
                      fieldMetadata={{
                        FieldType: SystemTypes.UIFieldType.TextBox,
                        FieldName: "ColumnName",
                        DisplayName: "Column Name",
                        Description: "Published semantic name.",
                        IsRequired: true,
                    }}
                    value={getEffectiveColumnName(selectedSemanticColumn)}
                    onChange={(value) => handleSemanticColumnFieldChange("ColumnName", toColumnNameInput(value))}
                    disabled={isWorkspacePanelReadOnly}
                  />
                  )}
                </div>
              </div>

              <div className="model-builder-grid">
                <div className="model-builder-grid__span-2">
                  <FieldControl
                    fieldMetadata={{
                      FieldType: SystemTypes.UIFieldType.CheckBox,
                      FieldName: "ShowOnGrid",
                      DisplayName: "Visible",
                      Description: "Whether this column is visible by default in authoring and runtime UX.",
                    }}
                    value={selectedSemanticColumn.ShowOnGrid ?? true}
                    onChange={(value) => handleSemanticColumnFieldChange("ShowOnGrid", value)}
                    disabled={isWorkspacePanelReadOnly}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {renderPanelError(panelError)}
      </div>
      {panelFooter}
    </div>
  );

  const relationPanelContent = (
    <div
      className="model-panel-manual"
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        flex: "1 1 auto",
        height: "100%",
        width: "100%",
        minWidth: 0,
        background: "var(--primary-background)",
      }}
    >
      {relationPanelLookupOverlay}

      <div
        className="model-panel-body"
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          minWidth: 0,
          overflow: "auto",
          padding: "1rem",
        }}
      >
        {workingModel ? (
          <TabPaneControl
            className="model-panel-tabs"
            tabs={[
              { key: DETAILS_TAB_MODEL, title: "Model", content: modelTabContent },
              { key: DETAILS_TAB_COLUMNS, title: "Columns", content: columnsTabContent, disabled: !canConfigureRelationColumns },
              { key: DETAILS_TAB_FILTER, title: "Filter", content: filterTabContent, disabled: !canConfigureRelationFilter },
              ...(workingModel.UiModelKind === MODEL_KIND_VIEW
                ? [{ key: DETAILS_TAB_VIEW, title: "View", content: viewTabContent }]
                : []),
              ...(workingModel.UiModelKind === MODEL_KIND_VIEW && Number(workingModel.ViewAuthoringMode) === VIEW_AUTHORING_MODE_SQL
                ? [{ key: DETAILS_TAB_SQL, title: "SQL", content: sqlTabContent }]
                : []),
            ]}
            activeIndex={Math.max(0, [
              DETAILS_TAB_MODEL,
              DETAILS_TAB_COLUMNS,
              DETAILS_TAB_FILTER,
              ...(workingModel.UiModelKind === MODEL_KIND_VIEW ? [DETAILS_TAB_VIEW] : []),
              ...(workingModel.UiModelKind === MODEL_KIND_VIEW && Number(workingModel.ViewAuthoringMode) === VIEW_AUTHORING_MODE_SQL
                ? [DETAILS_TAB_SQL]
                : []),
            ].findIndex((tabKey) => tabKey === panelActiveTab))}
            onChange={(nextIndex) => {
              const panelTabKeys = [
                DETAILS_TAB_MODEL,
                DETAILS_TAB_COLUMNS,
                DETAILS_TAB_FILTER,
                ...(workingModel.UiModelKind === MODEL_KIND_VIEW ? [DETAILS_TAB_VIEW] : []),
                ...(workingModel.UiModelKind === MODEL_KIND_VIEW && Number(workingModel.ViewAuthoringMode) === VIEW_AUTHORING_MODE_SQL
                  ? [DETAILS_TAB_SQL]
                  : []),
              ];
              setPanelActiveTab(panelTabKeys[nextIndex] ?? DETAILS_TAB_MODEL);
            }}
            align="right"
            compact={true}
            framed={false}
            unmountInactive={true}
          />
        ) : null}

        {renderPanelError(panelError)}
      </div>

      {panelFooter}
    </div>
  );

  const runtimeServerStatusText = runtimeStatusError
    ? "Error"
    : String(runtimeStatusSummary?.Status || "").trim().toLowerCase() === "loaded"
      ? "Online"
      : "Offline";
  const workspaceVersionText = normalizeWorkspaceVersion(currentWorkspace?.Version);
  const designVirtualDbVersionText = runtimeStatusError
    ? "Design Virtual DB Unavailable"
    : `Design ${formatVirtualDbVersionLabel(
      runtimeStatusSummary?.WorkspaceVersion || currentWorkspace?.Version,
      "Virtual DB Unknown"
    )}`;
  const runtimeStatusDetailTone = runtimeStatusError
    ? "error"
    : String(runtimeStatusSummary?.Status || "").trim().toLowerCase() === "loaded"
      ? "success"
      : "offline";
  const builtVirtualDbArtifact = artifactStatusSummary?.latestSuccessfulArtifact ?? artifactStatusSummary?.currentPublishedArtifact ?? null;
  const latestArtifactBuildRunStatus = Number(artifactStatusSummary?.latestBuildRun?.Status ?? 0);
  const publishedVirtualDbStatusText = artifactStatusError
    ? "Error"
    : artifactStatusSummary?.currentPublishedArtifact
      ? "Online"
      : "Offline";
  const publishedVirtualDbStatusTone = artifactStatusError
    ? "error"
    : artifactStatusSummary?.currentPublishedArtifact
      ? "success"
      : "offline";
  const publishedVirtualDbLabelTone = artifactStatusError ? "error" : "";
  const workspaceModeLabelText = getWorkspaceSurfaceModeLabel(workspaceSurfaceMode);
  const publishedVirtualDbLabelText = artifactStatusSummary?.currentPublishedArtifact
    ? formatArtifactVersionChipText(artifactStatusSummary.currentPublishedArtifact, "Published Virtual DB")
    : "Published Virtual DB Unavailable";
  const workspaceVisibilityIconKey = currentWorkspace?.IsPublic ? "public" : "publicoff";
  const isDesignVirtualDbLoaded = isRuntimeLoadedStatus(runtimeStatusSummary?.Status);
  const designVirtualDbLoadedAtText = formatDashboardRuntimeLoadedAt(runtimeStatusSummary?.LoadedAtUtc);
  const hasPublishedVirtualDbBuild = Boolean(
    artifactStatusSummary?.latestSuccessfulArtifact || artifactStatusSummary?.currentPublishedArtifact
  );
  const isBuiltVirtualDbActive = isWorkspaceDesignMode
    && designVirtualDbTarget === DESIGN_VIRTUAL_DB_TARGET_BUILT
    && hasPublishedVirtualDbBuild;
  const builtVirtualDbBuiltAtText = formatDashboardRuntimeLoadedAt(
    builtVirtualDbArtifact?.BuiltAtUtc
    || artifactStatusSummary?.latestBuildRun?.CompletedAtUtc
    || artifactStatusSummary?.latestBuildRun?.StartedAtUtc
    || artifactStatusSummary?.latestBuildRun?.RequestedAtUtc
  );
  const builtVirtualDbStatusText = artifactStatusError || latestArtifactBuildRunStatus === 4
    ? "Error"
    : hasPublishedVirtualDbBuild
      ? "Online"
      : "Offline";
  const builtVirtualDbStatusTone = artifactStatusError || latestArtifactBuildRunStatus === 4
    ? "error"
    : hasPublishedVirtualDbBuild
      ? "success"
      : "offline";
  const builtVirtualDbLabelText = artifactStatusError
    ? "Built Virtual DB Unavailable"
    : builtVirtualDbArtifact
      ? formatArtifactVersionChipText(builtVirtualDbArtifact, "Built Virtual DB")
      : "No Built Virtual DB";
  const builtVirtualDbLabelTone = artifactStatusError || latestArtifactBuildRunStatus === 4 ? "error" : "";
  const activeViewingInfoDetailText = isWorkspacePublishedMode
    ? publishedVirtualDbStatusText
    : isBuiltVirtualDbActive
      ? builtVirtualDbBuiltAtText
        ? `Built on ${builtVirtualDbBuiltAtText}`
        : builtVirtualDbStatusText
      : runtimeStatusError
        ? "Error"
        : isDesignVirtualDbLoaded
          ? designVirtualDbLoadedAtText
            ? `Online since ${designVirtualDbLoadedAtText}`
            : "Online"
          : "Offline";
  const activeVirtualDbDetailText = isWorkspacePublishedMode
    ? publishedVirtualDbStatusText
    : isBuiltVirtualDbActive
      ? builtVirtualDbStatusText
      : runtimeServerStatusText;
  const activeVirtualDbLabelText = isWorkspacePublishedMode
    ? publishedVirtualDbLabelText
    : isBuiltVirtualDbActive
      ? builtVirtualDbLabelText
      : designVirtualDbVersionText;
  const activeVirtualDbDetailTone = isWorkspacePublishedMode
    ? publishedVirtualDbStatusTone
    : isBuiltVirtualDbActive
      ? builtVirtualDbStatusTone
      : runtimeStatusDetailTone;
  const activeVirtualDbLabelTone = isWorkspacePublishedMode
    ? publishedVirtualDbLabelTone
    : isBuiltVirtualDbActive
      ? builtVirtualDbLabelTone
      : "";
  const workspaceVisibilityTitle = currentWorkspace?.IsPublic ? "Public" : "Private";

  useEffect(() => {
    setDesignVirtualDbTarget(DESIGN_VIRTUAL_DB_TARGET_DESIGN);
  }, [currentWorkspaceId]);

  useEffect(() => {
    if (!hasPublishedVirtualDbBuild && designVirtualDbTarget !== DESIGN_VIRTUAL_DB_TARGET_DESIGN) {
      autoPreviewOnModeChangeRef.current = true;
      setDesignVirtualDbTarget(DESIGN_VIRTUAL_DB_TARGET_DESIGN);
    }
  }, [designVirtualDbTarget, hasPublishedVirtualDbBuild]);

  const designViewingInfoFooter = useMemo(() => {
    if (!isWorkspaceDesignMode) {
      return null;
    }

    return (
      <div className="workspace-viewing-footer__info-block">
        <div className="workspace-viewing-footer__info">
          {activeVirtualDbLabelText}{" "}
          <span className="workspace-viewing-footer__info-separator">|</span>{" "}
          {activeViewingInfoDetailText}
        </div>
        {designVirtualDbTarget === DESIGN_VIRTUAL_DB_TARGET_BUILT ? (
          <div className="workspace-viewing-footer__note">
            Preview is using the last built snapshot. Rebuild Virtual DB to include recent design changes.
          </div>
        ) : null}
      </div>
    );
  }, [
    activeViewingInfoDetailText,
    activeVirtualDbLabelText,
    designVirtualDbTarget,
    isWorkspaceDesignMode,
  ]);

  const designViewingStatusFooter = useMemo(() => {
    if (!isWorkspaceDesignMode) {
      return null;
    }

    return (
      <div className="workspace-viewing-footer">
        <div className="workspace-viewing-toggle">
          <span className="workspace-viewing-toggle__label">Viewing</span>
          <div className="workspace-viewing-toggle__group">
            <button
              type="button"
              className={`workspace-viewing-toggle__button${designVirtualDbTarget === DESIGN_VIRTUAL_DB_TARGET_DESIGN ? " is-active" : ""}`}
              onClick={() => handleSetDesignVirtualDbTarget(DESIGN_VIRTUAL_DB_TARGET_DESIGN)}
              disabled={!currentWorkspaceId || designVirtualDbTarget === DESIGN_VIRTUAL_DB_TARGET_DESIGN}
              title="View the live design Virtual DB"
            >
              Design Virtual DB
            </button>
            <button
              type="button"
              className={`workspace-viewing-toggle__button${designVirtualDbTarget === DESIGN_VIRTUAL_DB_TARGET_BUILT ? " is-active" : ""}`}
              onClick={() => handleSetDesignVirtualDbTarget(DESIGN_VIRTUAL_DB_TARGET_BUILT)}
              disabled={!currentWorkspaceId || !hasPublishedVirtualDbBuild || designVirtualDbTarget === DESIGN_VIRTUAL_DB_TARGET_BUILT}
              title={hasPublishedVirtualDbBuild ? "View the latest built Virtual DB" : "Build a Virtual DB first"}
            >
              Built Virtual DB
            </button>
          </div>
        </div>
      </div>
    );
  }, [
    currentWorkspaceId,
    designVirtualDbTarget,
    handleSetDesignVirtualDbTarget,
    hasPublishedVirtualDbBuild,
    isWorkspaceDesignMode,
  ]);

  const dashboardWorkspaceHeaderActions = useMemo(() => (
    dashboardMode ? (
      <>
        <button
          type="button"
          className="dashboard-header-action-button dashboard-header-action-button--plain"
          title={isWorkspaceDesignMode ? "Edit Model Workspace" : WORKSPACE_READ_ONLY_MESSAGE}
          aria-label="Edit Model Workspace"
          onClick={openWorkspaceEditDialog}
          disabled={!currentWorkspaceId || isWorkspaceEditSaving || !isWorkspaceDesignMode}
        >
          <IconControl iconKey="edit" title={isWorkspaceDesignMode ? "Edit Model Workspace" : WORKSPACE_READ_ONLY_MESSAGE} />
        </button>
        {isWorkspaceDesignMode ? (
          <button
            type="button"
            className="dashboard-header-action-button dashboard-header-action-button--plain"
            title="Publish Virtual DB"
            aria-label="Publish Virtual DB"
            onClick={openPublishArtifactConfirmDialog}
            disabled={!currentWorkspaceId || isArtifactActionLoading}
          >
            <IconControl iconKey="upload" title="Publish Virtual DB" />
          </button>
        ) : null}
        {currentWorkspaceId ? (
          <IconControl
            className={`public-icon ${currentWorkspace?.IsPublic ? "" : "off-mode"}`}
            title={workspaceVisibilityTitle}
            iconKey={workspaceVisibilityIconKey}
          />
        ) : null}
      </>
    ) : null
  ), [
    currentWorkspaceId,
    currentWorkspace?.IsPublic,
    dashboardMode,
    isArtifactActionLoading,
    isWorkspaceDesignMode,
    isWorkspaceEditSaving,
    openPublishArtifactConfirmDialog,
    openWorkspaceEditDialog,
    workspaceVisibilityIconKey,
    workspaceVisibilityTitle,
  ]);
  const standaloneWorkspaceHeaderActions = useMemo(() => (
    <>
      <button
        type="button"
        className="data-header__back-button data-header__back-button--plain"
        title={isWorkspaceDesignMode ? "Edit Model Workspace" : WORKSPACE_READ_ONLY_MESSAGE}
        aria-label="Edit Model Workspace"
        onClick={openWorkspaceEditDialog}
        disabled={!currentWorkspaceId || isWorkspaceEditSaving || !isWorkspaceDesignMode}
      >
        <IconControl iconKey="edit" title={isWorkspaceDesignMode ? "Edit Model Workspace" : WORKSPACE_READ_ONLY_MESSAGE} />
      </button>
      {isWorkspaceDesignMode ? (
        <button
          type="button"
          className="data-header__back-button data-header__back-button--plain"
          title="Publish Virtual DB"
          aria-label="Publish Virtual DB"
          onClick={openPublishArtifactConfirmDialog}
          disabled={!currentWorkspaceId || isArtifactActionLoading}
        >
          <IconControl iconKey="upload" title="Publish Virtual DB" />
        </button>
      ) : null}
      {currentWorkspaceId ? (
        <IconControl
          className={`data-header__public-icon ${currentWorkspace?.IsPublic ? "" : "off-mode"}`}
          title={workspaceVisibilityTitle}
          iconKey={workspaceVisibilityIconKey}
        />
      ) : null}
    </>
  ), [
    currentWorkspaceId,
    currentWorkspace?.IsPublic,
    isArtifactActionLoading,
    isWorkspaceDesignMode,
    isWorkspaceEditSaving,
    openPublishArtifactConfirmDialog,
    openWorkspaceEditDialog,
    workspaceVisibilityIconKey,
    workspaceVisibilityTitle,
  ]);
  const dataHeaderStatusMenus = useMemo(() => {
    const isRuntimeActionDisabled = isRuntimeLoadLoading || !currentWorkspaceId;
    const isArtifactActionDisabled = isArtifactActionLoading || !currentWorkspaceId;
    const workspaceModeItems = [
      {
        text: "Design Mode",
        onClick: () => handleSetWorkspaceSurfaceMode(WORKSPACE_SURFACE_MODE_DESIGN),
        disabled: !currentWorkspaceId || isWorkspaceDesignMode,
      },
      {
        text: "Published Mode",
        onClick: () => handleSetWorkspaceSurfaceMode(WORKSPACE_SURFACE_MODE_PUBLISHED),
        disabled: !currentWorkspaceId || isWorkspacePublishedMode,
      }
    ];
    const virtualDbItems = isWorkspacePublishedMode
      ? [
        {
          text: VIRTUAL_DB_STATUS_LABEL,
          icon: "info",
          onClick: handleStatusArtifact,
          disabled: isArtifactActionDisabled,
        },
      ]
      : isBuiltVirtualDbActive
        ? [
          {
            text: VIRTUAL_DB_STATUS_LABEL,
            icon: "info",
            onClick: handleStatusArtifact,
            disabled: isArtifactActionDisabled,
          },
          {
            text: "Rebuild Virtual DB",
            icon: "settings",
            onClick: openRebuildArtifactConfirmDialog,
            disabled: isArtifactActionDisabled,
          },
        ]
        : [
          {
            text: VIRTUAL_DB_STATUS_LABEL,
            icon: "info",
            onClick: handleStatusRuntime,
            disabled: isRuntimeActionDisabled,
          },
          {
            text: isDesignVirtualDbLoaded ? "Reload Virtual DB" : "Load Virtual DB",
            icon: isDesignVirtualDbLoaded ? "refresh" : "download2",
            onClick: isDesignVirtualDbLoaded ? handleReloadRuntime : handleOpenRuntimeLoadDialog,
            disabled: isRuntimeActionDisabled,
          },
        {
          text: "Drop Virtual DB",
          icon: "delete",
          onClick: openVirtualDbDropDialog,
          disabled: isRuntimeActionDisabled,
          danger: true,
        },
        {
          text: hasPublishedVirtualDbBuild ? "Rebuild Virtual DB" : "Build Virtual DB",
          icon: hasPublishedVirtualDbBuild ? "settings" : "download2",
          onClick: hasPublishedVirtualDbBuild
            ? openRebuildArtifactConfirmDialog
            : () => handleBuildArtifact({ forceRebuild: false }),
          disabled: isArtifactActionDisabled,
        },
      ];
    return [
      {
        key: "workspace-mode",
        detail: "",
        label: workspaceModeLabelText,
        detailTone: isWorkspaceDesignMode ? "offline" : "",
        labelClassName: "status-chip--emphasis",
        title: "Workspace mode actions",
        items: workspaceModeItems,
      },
      {
        key: "virtual-db",
        detail: activeVirtualDbDetailText,
        label: activeVirtualDbLabelText,
        detailTone: activeVirtualDbDetailTone,
        labelTone: activeVirtualDbLabelTone,
        title: isWorkspacePublishedMode ? "Published Virtual DB actions" : `${VIRTUAL_DB_STATUS_LABEL} actions`,
        items: virtualDbItems,
      },
      ...(isWorkspacePublishedMode
        ? [
          {
            key: "local-db",
            detail: "Offline",
            label: "Local DB Unavailable",
            title: "Local DB actions",
            items: [
              {
                text: "Status",
                icon: "info",
                onClick: () => handleLocalDbStubAction("Status"),
              },
              {
                text: "Load",
                icon: "download2",
                onClick: () => handleLocalDbStubAction("Load"),
              },
              {
                text: "Reload",
                icon: "refresh",
                onClick: () => handleLocalDbStubAction("Reload"),
              },
              {
                text: "Drop",
                icon: "delete",
                onClick: openLocalDbDropDialog,
                danger: true,
              },
            ],
          },
        ]
        : []),
      ];
  }, [
    activeVirtualDbDetailText,
    activeVirtualDbDetailTone,
    activeVirtualDbLabelText,
    activeVirtualDbLabelTone,
    currentWorkspaceId,
    handleBuildArtifact,
    handleLocalDbStubAction,
    handleOpenRuntimeLoadDialog,
    handleReloadRuntime,
    handleSetWorkspaceSurfaceMode,
    handleStatusArtifact,
    handleStatusRuntime,
    hasPublishedVirtualDbBuild,
    isBuiltVirtualDbActive,
    isDesignVirtualDbLoaded,
    isArtifactActionLoading,
    isRuntimeLoadLoading,
    isWorkspaceDesignMode,
    isWorkspacePublishedMode,
    openLocalDbDropDialog,
    openRebuildArtifactConfirmDialog,
    openVirtualDbDropDialog,
    workspaceModeLabelText,
  ]);
  const artifactDialogSummary = artifactActionResult ?? artifactStatusSummary;
  const artifactDialogPublishedArtifact = artifactDialogSummary?.currentPublishedArtifact ?? null;
  const artifactDialogLatestArtifact = artifactDialogSummary?.latestSuccessfulArtifact ?? null;
  const artifactDialogLatestBuildRun = artifactDialogSummary?.latestBuildRun ?? null;
  const artifactDialogLatestBuildRunStatus = Number(artifactDialogLatestBuildRun?.Status ?? 0);
  const artifactDialogStateText = artifactActionError || artifactStatusError
    ? "Error"
    : artifactDialogLatestBuildRunStatus === 2
      ? "Building"
      : artifactDialogPublishedArtifact
        ? "Published"
        : artifactDialogLatestBuildRunStatus === 4
          ? "Failed"
          : artifactDialogLatestArtifact
            ? "Built"
            : "Unbuilt";
  const publishTargetVirtualDbVersionText = useMemo(() => {
    const parsedVersion = parseWorkspaceVersion(currentWorkspace?.Version);
    return `${parsedVersion.userVersion}.${parsedVersion.publishedVersion + 1}.0`;
  }, [currentWorkspace?.Version]);
  const artifactActionProgressTitle = String(artifactActionLabel ?? "").trim().toLowerCase() === "publish"
    ? `Publishing Virtual DB v${publishTargetVirtualDbVersionText}`
    : `${getRuntimeActionProgressLabel(artifactActionLabel)} Virtual DB`;
  const artifactDialogTabs = [
    { key: ARTIFACT_STATUS_TAB_OVERVIEW, label: "Overview" },
    { key: ARTIFACT_STATUS_TAB_BUILT, label: "Built Virtual DB" },
    { key: ARTIFACT_STATUS_TAB_PUBLISHED, label: "Published Virtual DB" },
    { key: ARTIFACT_STATUS_TAB_DIAGNOSTICS, label: "Diagnostics" },
  ];
  const renderArtifactStatusCard = (artifact, emptyTitle, emptyMeta) => (
    <div className="runtime-load-dialog__relation-list">
      <div className="runtime-load-dialog__relation-row">
        <div className="runtime-load-dialog__relation-text">
          <div className="runtime-load-dialog__relation-name">
            {artifact?.VersionId
              ? middleEllipsis(String(artifact.VersionId), 48)
              : emptyTitle}
          </div>
          <div className="runtime-load-dialog__relation-meta">
            {artifact
              ? formatArtifactStatusLabel(artifact?.Status)
              : emptyMeta}
          </div>
        </div>
        <div className="runtime-load-dialog__relation-count">
          {artifact?.BuiltAtUtc
            ? formatDashboardRuntimeLoadedAt(artifact.BuiltAtUtc)
            : "Not built"}
        </div>
      </div>
    </div>
  );
  const renderArtifactDiagnosticRows = (artifact, emptyText) => (
    <div className="runtime-load-dialog__diagnostics-grid">
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">Version</div>
        <div className="runtime-load-dialog__diagnostic-value" title={artifact?.VersionId || ""}>
          {artifact?.VersionId ? middleEllipsis(String(artifact.VersionId), 56) : emptyText}
        </div>
      </div>
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">Status</div>
        <div className="runtime-load-dialog__diagnostic-value">
          {artifact ? formatArtifactStatusLabel(artifact?.Status) : "Unknown"}
        </div>
      </div>
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">Built At</div>
        <div className="runtime-load-dialog__diagnostic-value">
          {formatDashboardRuntimeLoadedAt(artifact?.BuiltAtUtc) || "Unknown"}
        </div>
      </div>
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">Container</div>
        <div className="runtime-load-dialog__diagnostic-value" title={artifact?.ArtifactContainerName || ""}>
          {formatArtifactDiagnosticValue(artifact?.ArtifactContainerName)}
        </div>
      </div>
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">Artifact Path</div>
        <div
          className="runtime-load-dialog__diagnostic-value runtime-load-dialog__diagnostic-value--code"
          title={artifact?.ArtifactRelativePath || artifact?.ArtifactPath || ""}
        >
          {formatArtifactDiagnosticValue(artifact?.ArtifactRelativePath || artifact?.ArtifactPath)}
        </div>
      </div>
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">Manifest Path</div>
        <div
          className="runtime-load-dialog__diagnostic-value runtime-load-dialog__diagnostic-value--code"
          title={artifact?.ManifestRelativePath || artifact?.ManifestPath || ""}
        >
          {formatArtifactDiagnosticValue(artifact?.ManifestRelativePath || artifact?.ManifestPath)}
        </div>
      </div>
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">Build Log Path</div>
        <div
          className="runtime-load-dialog__diagnostic-value runtime-load-dialog__diagnostic-value--code"
          title={artifact?.BuildLogRelativePath || artifact?.BuildLogPath || ""}
        >
          {formatArtifactDiagnosticValue(artifact?.BuildLogRelativePath || artifact?.BuildLogPath)}
        </div>
      </div>
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">ETag</div>
        <div className="runtime-load-dialog__diagnostic-value" title={artifact?.ArtifactETag || ""}>
          {formatArtifactDiagnosticValue(artifact?.ArtifactETag)}
        </div>
      </div>
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">Content Hash</div>
        <div className="runtime-load-dialog__diagnostic-value" title={artifact?.ArtifactContentHash || ""}>
          {formatArtifactDiagnosticValue(artifact?.ArtifactContentHash)}
        </div>
      </div>
      <div className="runtime-load-dialog__diagnostic-row">
        <div className="runtime-load-dialog__diagnostic-label">Artifact Size</div>
        <div className="runtime-load-dialog__diagnostic-value">
          {formatArtifactSizeLabel(artifact?.ArtifactSizeBytes)}
        </div>
      </div>
    </div>
  );
  const runtimeDialogTabs = [
    { key: RUNTIME_STATUS_TAB_OVERVIEW, label: "Overview" },
    { key: RUNTIME_STATUS_TAB_SOURCE, label: "Source Relations" },
    { key: RUNTIME_STATUS_TAB_MODEL, label: "Model Relations" },
    { key: RUNTIME_STATUS_TAB_DIAGNOSTICS, label: "Diagnostics" },
  ];
  const runtimeDialogSourceRelations = Array.isArray(runtimeLoadResult?.SourceRelations) ? runtimeLoadResult.SourceRelations : [];
  const runtimeDialogModelRelations = Array.isArray(runtimeLoadResult?.ModelRelations) ? runtimeLoadResult.ModelRelations : [];
  const renderRuntimeRelationList = (relations, emptyTitle, emptyMeta) => (
    <div className="runtime-load-dialog__relation-list">
      {relations.length > 0 ? relations.map((relation, index) => (
        <div
          key={`runtime-${relation?.RelationName ?? relation?.ModelKey ?? relation?.DataModelId ?? index}`}
          className="runtime-load-dialog__relation-row"
        >
          <div className="runtime-load-dialog__relation-text">
            <div className="runtime-load-dialog__relation-name">
              {relation?.RelationName || relation?.ModelKey || "Unnamed relation"}
            </div>
            <div className="runtime-load-dialog__relation-meta">
              {relation?.RelationType || emptyMeta}
            </div>
          </div>
          <div className="runtime-load-dialog__relation-count">
            {formatRuntimeRowCount(relation?.RowCount)} rows
          </div>
        </div>
      )) : (
        <div className="runtime-load-dialog__relation-row">
          <div className="runtime-load-dialog__relation-text">
            <div className="runtime-load-dialog__relation-name">{emptyTitle}</div>
            <div className="runtime-load-dialog__relation-meta">{emptyMeta}</div>
          </div>
          <div className="runtime-load-dialog__relation-count">0 rows</div>
        </div>
      )}
    </div>
  );

  return (
    <div className={`${dashboardMode ? "dashboard-container " : ""}data-model-container model-designer-screen`}>
      {toastMessage && <ToastNotifications position="top" message={toastMessage} />}

        <>
          {dashboardMode ? (
            <DashboardHeader
              statusMenus={dataHeaderStatusMenus}
              topRightActions={dashboardWorkspaceHeaderActions}
              leftFooter={designViewingInfoFooter}
              statusFooter={designViewingStatusFooter}
              showDefaultPublicIcon={false}
              hideTemplateLabel={true}
            />
          ) : (
            <DataHeader
              statusMenus={dataHeaderStatusMenus}
              title={String(currentWorkspace?.Name ?? "").trim() || "Model Workspace"}
              description={String(currentWorkspace?.Description ?? "").trim() || "Reusable data model authoring and semantic design."}
              subtitle={workspaceVersionText ? `Model Workspace v${workspaceVersionText}` : "Standalone Data Model Designer"}
              iconKey="schema"
              headerActions={standaloneWorkspaceHeaderActions}
              leftFooter={designViewingInfoFooter}
              statusFooter={designViewingStatusFooter}
              onBackClick={handleNavigateToWorkspaceHome}
              backButtonTitle="Back to Model Workspaces"
            />
        )}
        <div className="model-designer-workspace">
          {isModelsLoading ? (
            <div className="model-designer-loading-state">
              <Loading type="spinner-2" />
            </div>
          ) : (
            <>
            <div className="model-list-pane">
              <div className="model-pane-header">
                <div>
                  <div className="model-pane-eyebrow">{String(currentWorkspace?.Name ?? "").trim() || "Model Workspace"}</div>
                  <div className="model-pane-title">Models</div>
                </div>
                <ButtonControl
                  className="model-pane-header-action"
                  type="standard"
                  onClick={openCreateModelPanel}
                  disabled={!currentWorkspaceId || !isWorkspaceDesignMode}
                  title={isWorkspaceDesignMode ? "Add Model" : WORKSPACE_READ_ONLY_MESSAGE}
                >
                  <IconControl iconKey="create" />
                </ButtonControl>
              </div>

              {models.length === 0 ? (
                <div className="model-pane-empty">
                  <div className="empty-title">No models yet</div>
                  <div className="empty-copy">
                    Add a table or view to start binding visuals to reusable data.
                  </div>
                </div>
              ) : (
                <div className="model-list">
                  {models.map((model) => (
                    <div
                      key={model.id}
                      role="button"
                      tabIndex={0}
                      className={`model-list-item${selectedModel?.id === model.id ? " selected" : ""}`}
                      onClick={() => handleSelectModel(model.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleSelectModel(model.id);
                        }
                      }}
                    >
                      <div className="model-list-header">
                        <div className="model-list-text">
                          <div className="model-list-title" title={model.title}>
                            {middleEllipsis(model.title, 30)}
                          </div>
                          <div
                            className="model-list-subtitle"
                            title={`Key: ${model.subtitle || "No key"}`}
                          >
                            {`Key: ${middleEllipsis(model.subtitle || "No key", 24)}`}
                          </div>
                        </div>
                        <span className="model-type-badge">
                          {model.type === TABLE_TYPE ? "Table" : "View"}
                        </span>
                      </div>
                      <div className="model-list-footer">
                        <ButtonControl
                          type="icon-small"
                          className="model-designer-delete-button"
                          title={isWorkspaceDesignMode ? "Delete Model" : WORKSPACE_READ_ONLY_MESSAGE}
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteError("");
                            if (selectedModel?.id !== model.id) {
                              setSelectedModelId(model.id);
                            }
                            setIsDeleteDialogOpen(true);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              event.stopPropagation();
                              setDeleteError("");
                              if (selectedModel?.id !== model.id) {
                                setSelectedModelId(model.id);
                              }
                              setIsDeleteDialogOpen(true);
                            }
                          }}
                          disabled={!isWorkspaceDesignMode}
                        >
                          <IconControl iconKey="delete" />
                        </ButtonControl>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="model-data-pane">
              <div className="model-pane-header">
                <div className="model-pane-header-copy">
                  <div className="model-pane-eyebrow">Model Data</div>
                  <div className="model-pane-title">
                    {selectedModel
                      ? (String(selectedModel.item?.Description ?? "").trim() || "No description provided for this model.")
                      : "Select a model to inspect its data"}
                  </div>
                </div>
                <div className="model-pane-header-right">
                  <div className="model-pane-identity">
                    <div
                      className="model-pane-identity-name"
                      title={
                        selectedModel
                          ? `${selectedModel.item?.Label || selectedModel.title || "Not set"} (Key: ${selectedModel.item?.Key || "Not set"})`
                          : "No model selected"
                      }
                    >
                      {selectedModel
                        ? `${middleEllipsis(selectedModel.item?.Label || selectedModel.title || "Not set", 24)} (Key: ${middleEllipsis(selectedModel.item?.Key || "Not set", 18)})`
                        : "No model selected"}
                    </div>
                  </div>
                  <div className="model-pane-actions">
                    <div className="model-pane-preview-mode">
                      <SelectListControl
                        metadata={{
                          FieldName: "previewExecutionMode",
                          DataJson: previewExecutionModeOptions,
                          PlaceHolder: "Preview Mode",
                          IsRequired: true,
                        }}
                        value={previewExecutionMode}
                        onChange={handlePreviewExecutionModeChange}
                        disabled={!selectedModel}
                      />
                    </div>
                    <span title="Refresh Preview">
                      <ButtonControl
                        className="model-pane-icon-action"
                        type="icon"
                        onClick={requestPreview}
                        disabled={!selectedModel || isClientPreviewMode || (!(isRealtimePreviewAvailable || isServerPreviewAvailable)) || previewLoading}
                        title="Refresh Preview"
                      >
                        <IconControl iconKey="refresh" title="Refresh Preview" />
                      </ButtonControl>
                    </span>
                    {isSystemAdmin ? (
                      <span
                        title={
                          isRealtimePreviewMode
                            ? "SQL compilation is available for Client and Virtual DB preview modes."
                            : "Compile model SQL"
                        }
                      >
                        <ButtonControl
                          className="model-pane-icon-action"
                          type="icon"
                          onClick={handleOpenModelSqlDialog}
                          disabled={!selectedModel || isRealtimePreviewMode || isModelSqlLoading}
                          title={
                            isRealtimePreviewMode
                              ? "SQL compilation is available for Client and Virtual DB preview modes."
                              : "Compile model SQL"
                          }
                        >
                          <IconControl
                            iconKey="bug"
                            title={
                              isRealtimePreviewMode
                                ? "SQL compilation is available for Client and Virtual DB preview modes."
                                : "Compile model SQL"
                            }
                          />
                        </ButtonControl>
                      </span>
                    ) : null}
                    <span
                      title={
                        selectedModel?.type === TABLE_TYPE
                          ? (isWorkspaceDesignMode ? "Edit Semantic Layer" : "View Semantic Layer")
                          : "Semantic editing is available for table models"
                      }
                    >
                      <ButtonControl
                        className="model-pane-icon-action"
                        type="icon"
                        onClick={openSemanticPanel}
                        disabled={!selectedModel || selectedModel.type !== TABLE_TYPE}
                        title={
                          selectedModel?.type === TABLE_TYPE
                            ? (isWorkspaceDesignMode ? "Edit Semantic Layer" : "View Semantic Layer")
                            : "Semantic editing is available for table models"
                        }
                      >
                        <IconControl
                          iconKey="field"
                          title={
                            selectedModel?.type === TABLE_TYPE
                              ? (isWorkspaceDesignMode ? "Edit Semantic Layer" : "View Semantic Layer")
                              : "Semantic editing is available for table models"
                          }
                        />
                      </ButtonControl>
                    </span>
                    <span title={isWorkspaceDesignMode ? "Edit Relation" : "View Relation"}>
                      <ButtonControl
                        className="model-pane-icon-action"
                        type="icon"
                        onClick={openEditModelPanel}
                        disabled={!selectedModel}
                        title={isWorkspaceDesignMode ? "Edit Relation" : "View Relation"}
                      >
                        <IconControl
                          iconKey={isWorkspaceDesignMode ? "edit" : "view"}
                          title={isWorkspaceDesignMode ? "Edit Relation" : "View Relation"}
                        />
                      </ButtonControl>
                    </span>
                  </div>
                </div>
              </div>

              {!selectedModel ? (
                <div className="model-pane-empty wide">
                  <div className="empty-title">No model open</div>
                  <div className="empty-copy">
                    The selected model&apos;s data preview will load in this area.
                  </div>
                </div>
              ) : (
                <div className="model-details-pane">
                  <div className="model-selection-summary">
                    <div className="summary-card summary-card--info">
                      <div className="summary-card-header">
                        <div className="summary-label">Information</div>
                        <div
                          className="summary-count-chip"
                          title={`${selectedModelSemanticCounts.columnCount} columns | ${selectedModelSemanticCounts.measureCount} measures`}
                        >
                          {selectedModelSemanticCounts.columnCount} Columns | {selectedModelSemanticCounts.measureCount} Measures
                        </div>
                      </div>
                      {selectedModel.type === VIEW_TYPE ? (
                        <>
                          <div className="summary-subvalue summary-subvalue--first">
                            <span className="summary-subvalue-label">Authoring:</span>{" "}
                            {Number(getModelViewAuthoringMode(selectedModel.item)) === VIEW_AUTHORING_MODE_SQL ? "SQL" : "Builder"}
                          </div>
                          <div
                            className="summary-subvalue"
                            title={selectedModelPrimaryModelListItem?.title || "Not set"}
                          >
                            <span className="summary-subvalue-label">Primary Model:</span>{" "}
                            {middleEllipsis(selectedModelPrimaryModelListItem?.title || "Not set", 36)}
                          </div>
                        </>
                      ) : Number(getModelSourceType(selectedModel.item)) === 2 ? (
                        <div
                          className="summary-subvalue summary-subvalue--first"
                          title={`${selectedModelContext.containerLabel || "Not set"} / ${selectedModelSourceName || "Not set"}`}
                        >
                          <span className="summary-subvalue-label">Entity:</span>{" "}
                          {middleEllipsis(selectedModelContext.containerLabel || "Not set", 16)} /{" "}
                          {middleEllipsis(selectedModelSourceName || "Not set", 16)}
                        </div>
                      ) : (
                        <>
                          <div className="summary-subvalue summary-subvalue--first">
                            <span className="summary-subvalue-label">Source:</span>{" "}
                            {middleEllipsis(selectedModelSourceName || "Not set", 36)}
                          </div>
                          <div className="summary-subvalue" title={selectedModelContext.containerLabel || "Not set"}>
                            <span className="summary-subvalue-label">
                              {Number(getModelSourceType(selectedModel.item)) === 1 ? "System:" : "Parent:"}
                            </span>{" "}
                            {middleEllipsis(selectedModelContext.containerLabel || "Not set", 36)}
                          </div>
                        </>
                      )}
                      {selectedModel.type === TABLE_TYPE ? (
                        <div
                          className="summary-subvalue"
                          title={
                            selectedModelWarehouseContext.warehouseLabel
                              ? [
                                  `Warehouse: ${selectedModelWarehouseContext.warehouseLabel}`,
                                  selectedModelWarehouseContext.deploymentLabel || null,
                                ]
                                  .filter(Boolean)
                                  .join(" / ")
                              : "Source Data"
                          }
                        >
                          {selectedModelWarehouseContext.warehouseLabel ? (
                            <>
                              <span className="summary-subvalue-label">Warehouse:</span>{" "}
                              {middleEllipsis(selectedModelWarehouseContext.warehouseLabel, 18)}
                              {selectedModelWarehouseContext.deploymentLabel ? (
                                <> / {middleEllipsis(selectedModelWarehouseContext.deploymentLabel, 18)}</>
                              ) : null}
                            </>
                          ) : (
                            "Source Data"
                          )}
                        </div>
                      ) : null}
                    </div>
                    <div className="summary-card summary-card--filter">
                      <div className="summary-label">Filter</div>
                      {selectedModelFilterState ? (
                        <div className="summary-state-chip">{selectedModelFilterChipText}</div>
                      ) : null}
                      {selectedModelFilterSummary !== "All Data" ? (
                        <div className="summary-subvalue summary-subvalue--first" title={selectedModelFilterSummary}>
                          {middleEllipsis(selectedModelFilterSummary, 36)}
                        </div>
                      ) : (
                        <div className="summary-subvalue summary-subvalue--first">
                          No Filter Set
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="model-preview-pane">
                    {isClientPreviewMode ? (
                      <div className="model-pane-empty wide inline">
                        <div className="empty-title">Preview unavailable</div>
                        <div className="empty-copy">
                          Client {isMeasuresOnlyPreviewMode ? "measures" : "columns"} preview is not wired yet. Use the SQL dialog to inspect the compiled model SQL.
                        </div>
                      </div>
                    ) : isRealtimePreviewMode && selectedModel.type !== TABLE_TYPE ? (
                      <div className="model-pane-empty wide inline">
                        <div className="empty-title">Preview unavailable</div>
                        <div className="empty-copy">
                          RealTime preview is only available for table models.
                        </div>
                      </div>
                    ) : !previewRequested ? (
                      <div className="model-pane-empty wide inline">
                        <div className="model-preview-empty-actions">
                          <ButtonControl
                            type="standard"
                            onClick={requestPreview}
                            disabled={!selectedModel || previewLoading}
                          >
                            Load Preview
                          </ButtonControl>
                        </div>
                        <div className="empty-copy">
                          Click above to fetch live data for the selected preview mode.
                        </div>
                      </div>
                    ) : previewLoading ? (
                      <div className="model-pane-empty wide inline">
                        <div className="empty-title">Loading preview</div>
                        <Loading type="spinner-2" size="1.5rem" className="model-preview-loading-spinner" />
                      </div>
                    ) : previewError ? (
                      <div className="model-pane-empty wide inline">
                        <div className="empty-title">Preview unavailable</div>
                        <div className="empty-copy">{previewError}</div>
                        <div className="model-preview-empty-actions">
                          <ButtonControl
                            type="standard"
                            onClick={requestPreview}
                            disabled={!selectedModel || previewLoading}
                          >
                            Retry Preview
                          </ButtonControl>
                        </div>
                      </div>
                    ) : !previewTableMetadata || !previewTableMgr ? (
                      <div className="model-pane-empty wide inline">
                        <div className="empty-title">Preview unavailable</div>
                        <div className="model-preview-empty-actions">
                          <ButtonControl
                            type="standard"
                            onClick={requestPreview}
                            disabled={!selectedModel || previewLoading}
                          >
                            Retry Preview
                          </ButtonControl>
                        </div>
                      </div>
                    ) : (
                      <div className="model-preview-table">
                        <TableControl
                          key={`${selectedModel.id}-${selectedModel.item?.UpdatedDate ?? "preview"}-${Array.isArray(previewTableMetadata) ? previewTableMetadata.length : 0}`}
                          data={previewRows}
                          className="model-preview-table-control"
                          defaultNumRows={50}
                          metadata={previewTableMetadata}
                          loadingState={previewLoading}
                          mgr={previewTableMgr}
                          onServerSideQuery={isServerPreviewMode ? handleServerPreviewTableQuery : undefined}
                          fieldOverrides={isRealtimePreviewMode ? previewFieldOverrides : null}
                          disableTableTitle={true}
                          hideHeader={true}
                        />
                      </div>
                    )}

                  </div>
                </div>
              )}
            </div>
            </>
          )}
        </div>

        <DialogControl
          className="data-model-dialog"
          openDialog={isPublishArtifactConfirmDialogOpen}
          title="Publish Virtual DB"
          okText={isArtifactActionLoading || isPublishArtifactConfirmSaving ? "Publishing..." : "Publish"}
          disableOk={isArtifactActionLoading || isPublishArtifactConfirmSaving || !currentWorkspaceId || !isWorkspaceDesignMode}
          onOk={handleConfirmPublishArtifact}
          onCancel={closePublishArtifactConfirmDialog}
        >
          <div className="dialog-text">
            Please confirm that you want to publish this Virtual DB.
            <div className="dialog-checkbox">
              <CheckboxControl
                value={publishArtifactPublicState}
                onChange={() => setPublishArtifactPublicState(!publishArtifactPublicState)}
              />
              <span>Make this model workspace public</span>
            </div>
            {publishArtifactConfirmError ? (
              <div className="error" style={{ whiteSpace: "pre-wrap" }}>
                {publishArtifactConfirmError}
              </div>
            ) : null}
          </div>
        </DialogControl>

        <DialogControl
          className="data-model-dialog"
          openDialog={isRebuildArtifactConfirmDialogOpen}
          title="Rebuild Virtual DB"
          okText={isArtifactActionLoading ? "Rebuilding..." : "Rebuild"}
          disableOk={isArtifactActionLoading || !currentWorkspaceId || !isWorkspaceDesignMode}
          onOk={handleConfirmRebuildArtifact}
          onCancel={closeRebuildArtifactConfirmDialog}
        >
          <div className="dialog-text">
            Rebuilding the Virtual DB may take a long time.
            <div className="dialog-text dialog-text--compact">
              This will rebuild the current built Virtual DB from the latest workspace data and source data before it can be used for preview or publish.
            </div>
          </div>
        </DialogControl>

        <DialogControl
          className="data-model-dialog"
          openDialog={isWorkspaceEditDialogOpen}
          title="Edit Model Workspace"
          okText={isWorkspaceEditSaving ? "Saving..." : "Save"}
          disableOk={
            isWorkspaceEditSaving
            || !isWorkspaceDesignMode
            || !String(workspaceEditDraft?.Name ?? "").trim()
            || !String(workspaceEditDraft?.Version ?? "").trim()
          }
          onOk={handleSaveWorkspaceEdit}
          onCancel={closeWorkspaceEditDialog}
        >
          <div className="dialog-text">
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.TextBox,
                FieldName: "WorkspaceName",
                DisplayName: "Workspace Name",
                IsRequired: true,
              }}
              groupId="editModelWorkspace"
              value={workspaceEditDraft?.Name ?? ""}
              onChange={(value) => setWorkspaceEditDraft((prev) => ({ ...prev, Name: value }))}
            />
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.TextArea,
                FieldName: "WorkspaceDescription",
                DisplayName: "Description",
                IsRequired: false,
              }}
              groupId="editModelWorkspace"
              value={workspaceEditDraft?.Description ?? ""}
              onChange={(value) => setWorkspaceEditDraft((prev) => ({ ...prev, Description: value }))}
            />
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.TextBox,
                FieldName: "WorkspaceVersion",
                DisplayName: "Workspace Version",
                IsRequired: true,
              }}
              groupId="editModelWorkspace"
              value={workspaceEditDraft?.Version ?? ""}
              onChange={(value) => setWorkspaceEditDraft((prev) => ({ ...prev, Version: value }))}
            />
            {isSystemAdmin ? (
              <>
                <div className="dialog-checkbox">
                  <CheckboxControl
                    value={Number(workspaceEditDraft?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL) === DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH}
                    onChange={() => setWorkspaceEditDraft((prev) => ({
                      ...prev,
                      ServerExecutionHost:
                        Number(prev?.ServerExecutionHost ?? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL) === DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH
                          ? DATA_MODEL_SERVER_EXECUTION_HOST_LOCAL
                          : DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH,
                    }))}
                  />
                  <span>Use Remote Batch Host For Server Mode</span>
                </div>
                <div className="dialog-text dialog-text--compact">
                  When enabled, server-mode Virtual DB load, query, build, and publish operations are routed through the assigned remote batch host instead of the local app server.
                </div>
              </>
            ) : null}
            {workspaceEditError ? <div className="dialog-text" style={{ color: "var(--error)" }}>{workspaceEditError}</div> : null}
          </div>
        </DialogControl>

        <AIPanel
            open={isPanelOpen}
            title={
              panelWorkflow === PANEL_WORKFLOW_SEMANTIC
                ? "Edit Semantic Layer"
                : panelMode === "edit"
                  ? "Edit Model"
                  : "Create Model"
            }
            onCancel={closeModelPanel}
            mapToClass="core-layout"
            chatPane="right"
            chatPaneCollapseMode={isWorkspacePanelReadOnly ? "closed" : "open"}
            chatPaneToggleEnabled={!isWorkspacePanelReadOnly}
            enableContentDragHiding={false}
            minLeft={panelWorkflow === PANEL_WORKFLOW_SEMANTIC ? 340 : 700}
            minMiddle={panelWorkflow === PANEL_WORKFLOW_SEMANTIC ? 520 : 360}
            minRight={360}
            middle={panelWorkflow === PANEL_WORKFLOW_SEMANTIC ? semanticEditorContent : null}
            operationName={null}
            referenceName="DataModel"
            referenceId={workingModel?._id}
            introMessage="Lets configure your model!"
            splashMessage="Coming soon..."
          >
            {panelWorkflow === PANEL_WORKFLOW_SEMANTIC ? semanticSidebarContent : relationPanelContent}
          </AIPanel>

          <DialogControl
            className="data-model-dialog data-model-dialog--model-sql"
            openDialog={isModelSqlDialogOpen}
            title="Model SQL"
            cancelText="Close"
            onCancel={closeModelSqlDialog}
            canFullScreen={true}
          >
            <div className="model-sql-dialog">
              {isModelSqlLoading ? (
                <div className="model-pane-empty wide inline">
                  <div className="empty-title">Compiling SQL</div>
                  <Loading type="spinner-2" size="1.5rem" className="model-preview-loading-spinner" />
                </div>
              ) : modelSqlError ? (
                <div className="model-pane-empty wide inline">
                  <div className="empty-title">SQL unavailable</div>
                  <div className="empty-copy">{modelSqlError}</div>
                </div>
              ) : (
                <FieldControl
                  className="model-sql-dialog__viewer"
                  fieldMetadata={{
                    DisplayName: "Compiled Virtual DB SQL",
                    Description: "Compiled SQL for the selected data model and preview mode.",
                    FieldName: "compiledModelSql",
                    FieldType: SystemTypes.UIFieldType.SQL,
                    IsRequired: false,
                  }}
                  value={modelSqlText}
                  disabled={true}
                  onChange={() => {}}
                />
              )}
            </div>
          </DialogControl>

          <DialogControl
            className="data-model-dialog data-model-dialog--runtime-load"
            openDialog={isRuntimeLoadDialogOpen}
            title={runtimeLoadActionLabel === "Status" ? "Virtual DB" : `Virtual DB ${runtimeLoadActionLabel}`}
            cancelText="Close"
            onCancel={closeRuntimeLoadDialog}
            canFullScreen={true}
          >
            <div className="runtime-load-dialog">
              {isRuntimeLoadLoading ? (
                <div className="model-pane-empty wide inline">
                  <div className="empty-title">{getRuntimeActionProgressLabel(runtimeLoadActionLabel)} Virtual DB</div>
                  <Loading type="spinner-2" size="1.5rem" className="model-preview-loading-spinner" />
                </div>
              ) : runtimeLoadError ? (
                <div className="model-pane-empty wide inline">
                  <div className="empty-title">{runtimeLoadActionLabel} unavailable</div>
                  <div className="empty-copy">{runtimeLoadError}</div>
                </div>
              ) : runtimeLoadResult ? (
                <div className="runtime-load-dialog__content">
                  {runtimeLoadResult?.StatusMessage ? (
                    <div className="runtime-load-dialog__status-note">
                      {runtimeLoadResult.StatusMessage}
                    </div>
                  ) : null}
                  <div className="runtime-load-dialog__tabs">
                    {runtimeDialogTabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`runtime-load-dialog__tab${runtimeStatusDialogTab === tab.key ? " is-active" : ""}`}
                        onClick={() => setRuntimeStatusDialogTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  <div className="runtime-load-dialog__summary-grid">
                    <div className="summary-card">
                      <div className="summary-label">Status</div>
                      <div className="summary-value">{runtimeLoadResult?.Status || runtimeLoadActionLabel}</div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Runtime Id</div>
                      <div className="summary-subvalue summary-subvalue--first" title={runtimeLoadResult?.RuntimeId || ""}>
                        {middleEllipsis(String(runtimeLoadResult?.RuntimeId || ""), 42)}
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Loaded At</div>
                      <div className="summary-value">{formatDashboardRuntimeLoadedAt(runtimeLoadResult?.LoadedAtUtc) || "Unknown"}</div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Virtual DB Version</div>
                      <div className="summary-value">
                        {formatVirtualDbVersionLabel(runtimeLoadResult?.WorkspaceVersion || currentWorkspace?.Version)}
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">DuckDB Engine</div>
                      <div className="summary-value">
                        {String(runtimeLoadResult?.DatabaseVersion ?? "").trim() || "Unknown"}
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Relations</div>
                      <div className="summary-value">
                        {Number(runtimeLoadResult?.SourceRelationCount ?? 0)} Source
                      </div>
                      <div className="summary-subvalue">
                        {Number(runtimeLoadResult?.ModelRelationCount ?? 0)} Model
                      </div>
                    </div>
                  </div>

                  {runtimeStatusDialogTab === RUNTIME_STATUS_TAB_OVERVIEW ? (
                    <>
                      <div className="runtime-load-dialog__section">
                        <div className="runtime-load-dialog__section-title">Source Relations</div>
                        {renderRuntimeRelationList(
                          runtimeDialogSourceRelations,
                          "No source relations loaded",
                          "Source"
                        )}
                      </div>

                      <div className="runtime-load-dialog__section">
                        <div className="runtime-load-dialog__section-title">Model Relations</div>
                        {renderRuntimeRelationList(
                          runtimeDialogModelRelations,
                          "No model relations loaded",
                          "Model"
                        )}
                      </div>
                    </>
                  ) : null}

                  {runtimeStatusDialogTab === RUNTIME_STATUS_TAB_SOURCE ? (
                    <div className="runtime-load-dialog__section">
                      <div className="runtime-load-dialog__section-title">Source Relations</div>
                      {renderRuntimeRelationList(
                        runtimeDialogSourceRelations,
                        "No source relations loaded",
                        "Source"
                      )}
                    </div>
                  ) : null}

                  {runtimeStatusDialogTab === RUNTIME_STATUS_TAB_MODEL ? (
                    <div className="runtime-load-dialog__section">
                      <div className="runtime-load-dialog__section-title">Model Relations</div>
                      {renderRuntimeRelationList(
                        runtimeDialogModelRelations,
                        "No model relations loaded",
                        "Model"
                      )}
                    </div>
                  ) : null}

                  {runtimeStatusDialogTab === RUNTIME_STATUS_TAB_DIAGNOSTICS ? (
                    <div className="runtime-load-dialog__section">
                      <div className="runtime-load-dialog__section-title">Diagnostics</div>
                      <div className="runtime-load-dialog__diagnostics-grid">
                        <div className="runtime-load-dialog__diagnostic-row">
                          <div className="runtime-load-dialog__diagnostic-label">Runtime Id</div>
                          <div
                            className="runtime-load-dialog__diagnostic-value runtime-load-dialog__diagnostic-value--code"
                            title={runtimeLoadResult?.RuntimeId || ""}
                          >
                            {formatArtifactDiagnosticValue(runtimeLoadResult?.RuntimeId)}
                          </div>
                        </div>
                        <div className="runtime-load-dialog__diagnostic-row">
                          <div className="runtime-load-dialog__diagnostic-label">Status</div>
                          <div className="runtime-load-dialog__diagnostic-value">
                            {runtimeLoadResult?.Status || runtimeLoadActionLabel}
                          </div>
                        </div>
                        <div className="runtime-load-dialog__diagnostic-row">
                          <div className="runtime-load-dialog__diagnostic-label">Loaded At</div>
                          <div className="runtime-load-dialog__diagnostic-value">
                            {formatDashboardRuntimeLoadedAt(runtimeLoadResult?.LoadedAtUtc) || "Unknown"}
                          </div>
                        </div>
                        <div className="runtime-load-dialog__diagnostic-row">
                          <div className="runtime-load-dialog__diagnostic-label">Virtual DB Version</div>
                          <div className="runtime-load-dialog__diagnostic-value">
                            {formatVirtualDbVersionLabel(runtimeLoadResult?.WorkspaceVersion || currentWorkspace?.Version)}
                          </div>
                        </div>
                        <div className="runtime-load-dialog__diagnostic-row">
                          <div className="runtime-load-dialog__diagnostic-label">DuckDB Engine</div>
                          <div className="runtime-load-dialog__diagnostic-value">
                            {formatArtifactDiagnosticValue(runtimeLoadResult?.DatabaseVersion, "Unknown")}
                          </div>
                        </div>
                        <div className="runtime-load-dialog__diagnostic-row">
                          <div className="runtime-load-dialog__diagnostic-label">Source Relation Count</div>
                          <div className="runtime-load-dialog__diagnostic-value">
                            {Number(runtimeLoadResult?.SourceRelationCount ?? 0)}
                          </div>
                        </div>
                        <div className="runtime-load-dialog__diagnostic-row">
                          <div className="runtime-load-dialog__diagnostic-label">Model Relation Count</div>
                          <div className="runtime-load-dialog__diagnostic-value">
                            {Number(runtimeLoadResult?.ModelRelationCount ?? 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="model-pane-empty wide inline">
                  <div className="empty-title">No result</div>
                  <div className="empty-copy">Run one of the Virtual DB actions to see the current state.</div>
                </div>
              )}
            </div>
          </DialogControl>

          <DialogControl
            className="data-model-dialog data-model-dialog--runtime-load"
            openDialog={isArtifactActionDialogOpen}
            title={artifactActionLabel === "Status" ? "Virtual DB Status" : `Virtual DB ${artifactActionLabel}`}
            cancelText="Close"
            onCancel={closeArtifactActionDialog}
            canFullScreen={true}
          >
            <div className="runtime-load-dialog">
              {isArtifactActionLoading ? (
                <div className="model-pane-empty wide inline">
                  <div className="empty-title">{artifactActionProgressTitle}</div>
                  <Loading type="spinner-2" size="1.5rem" className="model-preview-loading-spinner" />
                </div>
              ) : artifactActionError ? (
                <div className="model-pane-empty wide inline">
                  <div className="empty-title">{artifactActionLabel} unavailable</div>
                  <div className="empty-copy">{artifactActionError}</div>
                </div>
              ) : artifactDialogSummary ? (
                <div className="runtime-load-dialog__content">
                  {artifactActionResult?.statusMessage ? (
                    <div className="runtime-load-dialog__status-note">
                      {artifactActionResult.statusMessage}
                    </div>
                  ) : null}
                  <div className="runtime-load-dialog__tabs">
                    {artifactDialogTabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`runtime-load-dialog__tab${artifactStatusDialogTab === tab.key ? " is-active" : ""}`}
                        onClick={() => setArtifactStatusDialogTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  <div className="runtime-load-dialog__summary-grid">
                    <div className="summary-card">
                      <div className="summary-label">State</div>
                      <div className="summary-value">{artifactDialogStateText}</div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Published Version</div>
                      <div className="summary-subvalue summary-subvalue--first" title={artifactDialogPublishedArtifact?.VersionId || ""}>
                        {artifactDialogPublishedArtifact?.VersionId
                          ? middleEllipsis(String(artifactDialogPublishedArtifact.VersionId), 32)
                          : "None"}
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Latest Version</div>
                      <div className="summary-subvalue summary-subvalue--first" title={artifactDialogLatestArtifact?.VersionId || ""}>
                        {artifactDialogLatestArtifact?.VersionId
                          ? middleEllipsis(String(artifactDialogLatestArtifact.VersionId), 32)
                          : "None"}
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Latest Build</div>
                      <div className="summary-value">
                        {formatBuildRunStatusLabel(artifactDialogLatestBuildRun?.Status ?? "")}
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Built At</div>
                      <div className="summary-value">
                        {formatDashboardRuntimeLoadedAt(
                          artifactDialogPublishedArtifact?.BuiltAtUtc
                          || artifactDialogLatestArtifact?.BuiltAtUtc
                          || artifactDialogLatestBuildRun?.CompletedAtUtc
                          || artifactDialogLatestBuildRun?.StartedAtUtc
                        ) || "Unknown"}
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">Model Definition</div>
                      <div className="summary-subvalue summary-subvalue--first" title={artifactDialogSummary?.workspace?.CurrentModelDefinitionVersionId || ""}>
                        {artifactDialogSummary?.workspace?.CurrentModelDefinitionVersionId
                          ? middleEllipsis(String(artifactDialogSummary.workspace.CurrentModelDefinitionVersionId), 32)
                          : "Unknown"}
                      </div>
                    </div>
                  </div>
                  {artifactStatusDialogTab === ARTIFACT_STATUS_TAB_OVERVIEW ? (
                    <>
                      <div className="runtime-load-dialog__section">
                        <div className="runtime-load-dialog__section-title">Published Virtual DB</div>
                        {renderArtifactStatusCard(
                          artifactDialogPublishedArtifact,
                          "No published Virtual DB",
                          "Publish the workspace Virtual DB to make Server visuals available in dashboard view mode."
                        )}
                      </div>

                      <div className="runtime-load-dialog__section">
                        <div className="runtime-load-dialog__section-title">Built Virtual DB</div>
                        {renderArtifactStatusCard(
                          artifactDialogLatestArtifact,
                          "No built Virtual DB",
                          "Build the workspace Virtual DB to create a reviewable artifact."
                        )}
                      </div>

                      <div className="runtime-load-dialog__section">
                        <div className="runtime-load-dialog__section-title">Latest Build Run</div>
                        <div className="runtime-load-dialog__relation-list">
                          <div className="runtime-load-dialog__relation-row">
                            <div className="runtime-load-dialog__relation-text">
                              <div className="runtime-load-dialog__relation-name">
                                {formatBuildRunStatusLabel(artifactDialogLatestBuildRun?.Status ?? "")}
                              </div>
                              <div className="runtime-load-dialog__relation-meta">
                                {String(artifactDialogLatestBuildRun?.DiagnosticSummary ?? "").trim() || "No build run has completed yet."}
                              </div>
                            </div>
                            <div className="runtime-load-dialog__relation-count">
                              {formatDashboardRuntimeLoadedAt(
                                artifactDialogLatestBuildRun?.CompletedAtUtc
                                || artifactDialogLatestBuildRun?.StartedAtUtc
                                || artifactDialogLatestBuildRun?.RequestedAtUtc
                              ) || "Unknown"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}

                  {artifactStatusDialogTab === ARTIFACT_STATUS_TAB_BUILT ? (
                    <div className="runtime-load-dialog__section">
                      <div className="runtime-load-dialog__section-title">Built Virtual DB</div>
                      {renderArtifactStatusCard(
                        artifactDialogLatestArtifact,
                        "No built Virtual DB",
                        "Build the workspace Virtual DB to create a reviewable artifact."
                      )}
                      {renderArtifactDiagnosticRows(
                        artifactDialogLatestArtifact,
                        "No built Virtual DB"
                      )}
                    </div>
                  ) : null}

                  {artifactStatusDialogTab === ARTIFACT_STATUS_TAB_PUBLISHED ? (
                    <div className="runtime-load-dialog__section">
                      <div className="runtime-load-dialog__section-title">Published Virtual DB</div>
                      {renderArtifactStatusCard(
                        artifactDialogPublishedArtifact,
                        "No published Virtual DB",
                        "Publish the workspace Virtual DB to make Server visuals available in dashboard view mode."
                      )}
                      {renderArtifactDiagnosticRows(
                        artifactDialogPublishedArtifact,
                        "No published Virtual DB"
                      )}
                    </div>
                  ) : null}

                  {artifactStatusDialogTab === ARTIFACT_STATUS_TAB_DIAGNOSTICS ? (
                    <>
                      <div className="runtime-load-dialog__section">
                        <div className="runtime-load-dialog__section-title">Workspace Pointers</div>
                        <div className="runtime-load-dialog__diagnostics-grid">
                          <div className="runtime-load-dialog__diagnostic-row">
                            <div className="runtime-load-dialog__diagnostic-label">Workspace Version</div>
                            <div className="runtime-load-dialog__diagnostic-value">
                              {formatVirtualDbVersionLabel(artifactDialogSummary?.workspace?.Version)}
                            </div>
                          </div>
                          <div className="runtime-load-dialog__diagnostic-row">
                            <div className="runtime-load-dialog__diagnostic-label">Published Pointer</div>
                            <div
                              className="runtime-load-dialog__diagnostic-value runtime-load-dialog__diagnostic-value--code"
                              title={artifactDialogSummary?.workspace?.CurrentPublishedArtifactVersionId || ""}
                            >
                              {formatArtifactDiagnosticValue(artifactDialogSummary?.workspace?.CurrentPublishedArtifactVersionId)}
                            </div>
                          </div>
                          <div className="runtime-load-dialog__diagnostic-row">
                            <div className="runtime-load-dialog__diagnostic-label">Latest Successful Pointer</div>
                            <div
                              className="runtime-load-dialog__diagnostic-value runtime-load-dialog__diagnostic-value--code"
                              title={artifactDialogSummary?.workspace?.LatestSuccessfulArtifactVersionId || ""}
                            >
                              {formatArtifactDiagnosticValue(artifactDialogSummary?.workspace?.LatestSuccessfulArtifactVersionId)}
                            </div>
                          </div>
                          <div className="runtime-load-dialog__diagnostic-row">
                            <div className="runtime-load-dialog__diagnostic-label">Model Definition</div>
                            <div
                              className="runtime-load-dialog__diagnostic-value runtime-load-dialog__diagnostic-value--code"
                              title={artifactDialogSummary?.workspace?.CurrentModelDefinitionVersionId || ""}
                            >
                              {formatArtifactDiagnosticValue(artifactDialogSummary?.workspace?.CurrentModelDefinitionVersionId)}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="runtime-load-dialog__section">
                        <div className="runtime-load-dialog__section-title">Built Virtual DB Diagnostics</div>
                        {renderArtifactDiagnosticRows(
                          artifactDialogLatestArtifact,
                          "No built Virtual DB"
                        )}
                      </div>

                      <div className="runtime-load-dialog__section">
                        <div className="runtime-load-dialog__section-title">Published Virtual DB Diagnostics</div>
                        {renderArtifactDiagnosticRows(
                          artifactDialogPublishedArtifact,
                          "No published Virtual DB"
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="model-pane-empty wide inline">
                  <div className="empty-title">No Virtual DB status</div>
                  <div className="empty-copy">Run one of the Virtual DB actions to see the current status.</div>
                </div>
              )}
            </div>
          </DialogControl>

          <DialogControl
            className="data-model-dialog"
            openDialog={isRuntimeDropDialogOpen}
            title={runtimeDropTarget === RUNTIME_DROP_TARGET_LOCAL ? "Drop Local DB" : "Drop Virtual DB"}
            okText={isRuntimeLoadLoading ? "Dropping..." : "Drop"}
            disableOk={isRuntimeLoadLoading || String(runtimeDropConfirmText ?? "").trim().toUpperCase() !== "DROP"}
            onOk={handleConfirmRuntimeDrop}
            onCancel={closeRuntimeDropDialog}
          >
            <div className="dialog-text">
              <div>
                {runtimeDropTarget === RUNTIME_DROP_TARGET_LOCAL
                  ? "Type DROP to confirm the local DB drop action."
                  : "Type DROP to confirm dropping the Virtual DB for this model workspace."}
              </div>
              <FieldControl
                fieldMetadata={{
                  FieldType: SystemTypes.UIFieldType.TextBox,
                  FieldName: "RuntimeDropConfirmation",
                  DisplayName: "Confirmation",
                  Description: "Type DROP to enable the drop action.",
                  IsRequired: true,
                }}
                groupId="runtimeDrop"
                value={runtimeDropConfirmText}
                onChange={setRuntimeDropConfirmText}
              />
            </div>
          </DialogControl>

          <DialogControl
            className="data-model-dialog"
            openDialog={isSemanticDeleteDialogOpen}
            title={pendingSemanticDeleteKind === SEMANTIC_DELETE_KIND_MEASURE ? "Delete Measure" : "Delete Semantic Column"}
            okText="Delete"
            disableOk={!isWorkspaceDesignMode}
            onOk={handleConfirmRemoveSemanticColumn}
            onCancel={closeSemanticDeleteDialog}
          >
            <div className="dialog-text">
              {pendingSemanticDeleteColumnLabel
                ? `Delete "${pendingSemanticDeleteColumnLabel}"?`
                : pendingSemanticDeleteKind === SEMANTIC_DELETE_KIND_MEASURE
                  ? "Delete this measure?"
                  : "Delete this semantic column?"}
            </div>
          </DialogControl>

          <DialogControl
            className="data-model-dialog"
            openDialog={isDeleteDialogOpen}
            title="Delete Model"
            okText={isDeletingModel ? "Deleting..." : "Delete"}
            disableOk={isDeletingModel || !isWorkspaceDesignMode}
            onOk={handleDeleteModel}
            onCancel={closeDeleteDialog}
          >
            <div className="dialog-text">
              {selectedModel?.title
                ? `Delete "${selectedModel.title}"?`
                : "Delete this model?"}
            </div>
            {deleteError ? <div className="model-panel-error">{deleteError}</div> : null}
          </DialogControl>
          {dashboardMode ? <NavigationRibbon /> : null}
      </>
    </div>
  );
};

export default DataModelDesigner;
